/**
 * 自動生成。直接直さない。
 *
 * 作り直しはリポジトリルートから次を実行する。
 *   set -a && . packages/shared-repositories/.env && set +a && \
 *     bun run apps/client/scripts/build-course-mistakes.ts
 *
 * レッスン本文の「よくある間違い」節から抜いたつまずき。
 * 並びはコース内のレッスン順で、レッスンごとにまとまっている。
 */
import {
  type CourseMistakeCatalogCourse,
  assertUniquePublicCourseMistakes,
  materializeCourseMistakes as materializeCourseMistakeRows,
  staticCourseMistakeKey,
} from "./course-mistakes-projection";
import { MISTAKE_TOTAL } from "./course-mistakes-metadata.generated";

export interface CourseMistake {
  /** コースの slug */
  course: string;
  /** つまずきが書かれているレッスンの slug */
  lesson: string;
  /** レッスンの題名 */
  lessonTitle: string;
  /** つまずきの見出し */
  heading: string;
  /** どうなるか・どう書くか */
  detail: string;
}

export const COURSE_MISTAKES: CourseMistake[] = [
  {
    course: "java-basics",
    lesson: "java-hello-world",
    lessonTitle: "はじめての Java プログラム",
    heading: "文末の `;` を忘れる",
    detail: "Java は `return \"Hello, Java!\";` のように、文の終わりに必ずセミコロンが要ります。これを忘れるとコンパイラに怒られます",
  },
  {
    course: "java-basics",
    lesson: "java-hello-world",
    lessonTitle: "はじめての Java プログラム",
    heading: "ダブルクォートとシングルクォートの混同",
    detail: "文字列は `\"Hello\"` のように 必ずダブルクォート で囲みます。`'Hello'` と書くと「文字 1 個」の型 (`char`) になってしまい、コンパイルエラーです",
  },
  {
    course: "java-basics",
    lesson: "java-hello-world",
    lessonTitle: "はじめての Java プログラム",
    heading: "`return` を忘れる",
    detail: "戻り値の型を `String` と書いたのに `return` を書かないと「missing return statement」と怒られます。戻り値があるメソッドは必ず `return` で締めましょう",
  },
  {
    course: "java-basics",
    lesson: "java-var-declare",
    lessonTitle: "変数を宣言する",
    heading: "型を書き忘れる",
    detail: "`age = 20;` のように、いきなり代入から書くとエラーになります。Java では最初に必ず `int age = 20;` のように型を書く必要があります",
  },
  {
    course: "java-basics",
    lesson: "java-var-declare",
    lessonTitle: "変数を宣言する",
    heading: "変数名を数字から始める",
    detail: "`int 1stScore = 100;` はコンパイルエラー。`int firstScore = 100;` のように英字始まりにします",
  },
  {
    course: "java-basics",
    lesson: "java-var-declare",
    lessonTitle: "変数を宣言する",
    heading: "初期化せずに使う",
    detail: "`int x; System.out.println(x);` のように、値を入れる前にローカル変数を使うと `variable x might not have been initialized` と怒られます",
  },
  {
    course: "java-basics",
    lesson: "java-var-declare",
    lessonTitle: "変数を宣言する",
    heading: "型と合わない値を代入する",
    detail: "`int age = \"20\";` のように、`int` 型の箱に文字列を入れようとするとエラーになります。`int age = 20;` のように、型と値の種類を合わせましょう",
  },
  {
    course: "java-basics",
    lesson: "java-arithmetic",
    lessonTitle: "四則演算と剰余",
    heading: "整数除算で小数を期待する",
    detail: "`int avg = (a + b) / 2;` と書いて、平均が想定より 1 小さい、というのは定番の罠です。`double avg = (a + b) / 2.0;` のように、片方を小数にして回避しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-arithmetic",
    lessonTitle: "四則演算と剰余",
    heading: "ゼロ除算で実行時エラー",
    detail: "`int x = 10 / 0;` を実行すると `ArithmeticException: / by zero` が出てプログラムが落ちます。`%` も同様で `10 % 0` も例外になります。割る前に `if (b != 0)` でチェックするか、`b == 0` の場合の戻り値を決めておくのが安全です",
  },
  {
    course: "java-basics",
    lesson: "java-arithmetic",
    lessonTitle: "四則演算と剰余",
    heading: "優先順位を勘違いする",
    detail: "`1 + 2 * 3` は `9` ではなく `7` です。「左から順番」と思い込んで計算するとズレます。迷ったらカッコを付けて自分の意図を明示しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-increment",
    lessonTitle: "インクリメントとデクリメント",
    heading: "前置と後置の混同",
    detail: "`b = a++;` と `b = ++a;` を「同じだろう」と思って書くと、`b` に入る値が違ってバグになります。代入や式の中で使うときは、必ずどちらか意識すること",
  },
  {
    course: "java-basics",
    lesson: "java-increment",
    lessonTitle: "インクリメントとデクリメント",
    heading: "同じ式の中で同じ変数を何度も増減させる",
    detail: "`int y = x++ + x++;` のように 1 行で `x` を 2 回動かすコードは、読みづらいうえに動作の理解も難しくなります。Java では仕様上動作は決まっていますが、避けるべきコード の代表例です",
  },
  {
    course: "java-basics",
    lesson: "java-increment",
    lessonTitle: "インクリメントとデクリメント",
    heading: "`n` をそのまま `n++` してしまう",
    detail: "今回の課題のように引数の値を保ったまま `+1` `-1` した値が欲しい場合、`n++` をすると `n` の値そのものが変わってしまいます。必ず別の変数にコピーしてから動かすこと",
  },
  {
    course: "java-basics",
    lesson: "java-compare-operator",
    lessonTitle: "比較演算子",
    heading: "`=` と `==` の混同",
    detail: "上で説明した通り。`if (a = 5)` と書いてしまうと `Java` ではコンパイルエラー、他の言語では「代入してから true 判定」という別の動きになる罠です",
  },
  {
    course: "java-basics",
    lesson: "java-compare-operator",
    lessonTitle: "比較演算子",
    heading: "文字列を `==` で比較する",
    detail: "`String s1 = \"hello\"; String s2 = new String(\"hello\"); s1 == s2` は意外にも `false` になります。文字列の中身を比べたいときは必ず `s1.equals(s2)` を使ってください。`==` はオブジェクトの「参照」が同じかを見るので、中身が同じでも別オブジェクトなら `false` です",
  },
  {
    course: "java-basics",
    lesson: "java-compare-operator",
    lessonTitle: "比較演算子",
    heading: "浮動小数の `==` 比較",
    detail: "`double x = 0.1 + 0.2; x == 0.3` は `false` です。`double` は内部で 2 進数表現するため、計算誤差が生じます。浮動小数を比べたいときは `Math.abs(x - 0.3) < 1e-9` のように「許容誤差以内か」で判定するのが定石です",
  },
  {
    course: "java-basics",
    lesson: "java-logical-operator",
    lessonTitle: "論理演算子",
    heading: "`&` `|` (ビット演算) と `&&` `||` の混同",
    detail: "`&` 1 個と `|` 1 個は、`int` 用のビット演算子です。`boolean` にも一応使えますが、短絡評価が効かないので推奨されません。`if` の条件には必ず `&&` `||` を 2 個重ねて書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-logical-operator",
    lessonTitle: "論理演算子",
    heading: "`!` を忘れる / 付け忘れる",
    detail: "「ログインしていない場合」は `!isLoggedIn` です。`isLoggedIn` のままだと逆の意味になります。日本語の「~でない」を `!` に翻訳する習慣をつけてください",
  },
  {
    course: "java-basics",
    lesson: "java-logical-operator",
    lessonTitle: "論理演算子",
    heading: "優先順位の勘違い",
    detail: "`&&` は `||` よりも優先順位が高いです。`a || b && c` は `a || (b && c)` の意味になります。意図したとおりに書きたいときは、迷わずカッコ `()` で囲みましょう",
  },
  {
    course: "java-basics",
    lesson: "java-operator-priority",
    lessonTitle: "演算子の優先順位",
    heading: "`a + b * c` を `(a + b) * c` と勘違いする",
    detail: "数学の `2 + 3 × 4` と同じく、`*` が先です。`Java` の式を読むときは、まず `*` `/` `%` を探して心の中で先にカッコを補うクセを付けましょう",
  },
  {
    course: "java-basics",
    lesson: "java-operator-priority",
    lessonTitle: "演算子の優先順位",
    heading: "`x = y = z` の結合方向を逆にする",
    detail: "代入 `=` は 右から左 に結合するので `x = (y = z)` と読みます。`(x = y) = z` ではないので注意。さらに代入式は `=` の右辺の値を返すため、`if ((line = reader.readLine()) != null)` のような書き方も存在します",
  },
  {
    course: "java-basics",
    lesson: "java-operator-priority",
    lessonTitle: "演算子の優先順位",
    heading: "ビット演算 `&` `|` と論理演算 `&&` `||` の優先順位を混同する",
    detail: "ビット演算 `&` `^` `|` は等価演算子 `==` `!=` や関係演算子 `<` `>` よりも優先順位が低く、論理演算子 `&&` `||` よりは高い位置にあります。ただし `&` と `&&` の本質的な違いは優先順位だけでなく 短絡評価 (short-circuit evaluation) の有無です。`&&` は左辺が `false` の時点で右辺を評価しませんが、`&` は必ず両辺を評価します。たとえば `if (obj != null & obj.getValue() > 0)` のように `&` を使うと、`obj` が `null` でも右辺が実行されて `NullPointerException` が発生します。論理判定では必ず `&&` `||` を使ってください",
  },
  {
    course: "java-basics",
    lesson: "java-string-concat",
    lessonTitle: "文字列の連結",
    heading: "数値同士を連結したつもりが足し算になる",
    detail: "`\"合計: \" + a + b` と書くと、`a + b` が最初に連結されてしまい、`a = 1, b = 2` の場合 `\"合計: 12\"` になります。`\"合計: \" + (a + b)` のようにカッコで括れば `\"合計: 3\"` になります",
  },
  {
    course: "java-basics",
    lesson: "java-string-concat",
    lessonTitle: "文字列の連結",
    heading: "評価順序を勘違いする",
    detail: "`1 + 2 + \"円\"` は `\"3円\"` ですが、`\"金額: \" + 1 + 2` は `\"金額: 12\"` です。`String` が登場した瞬間から、以降の `+` は文字列連結になります",
  },
  {
    course: "java-basics",
    lesson: "java-string-concat",
    lessonTitle: "文字列の連結",
    heading: "null を連結すると `\"null\"` になる",
    detail: "`String name = null; String msg = \"こんにちは、\" + name;` と書くと、`msg` は `\"こんにちは、null\"` という文字列になります。意図せず `null` を含んだ表示が出てしまい、ユーザーに気付かれて慌てる、というのは Java の現場でよくある失敗です。事前に `if (name != null)` でチェックするか、`Objects.toString(name, \"匿名\")` のようにデフォルト値を用意しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-type-cast",
    lessonTitle: "型変換 (キャスト)",
    heading: "キャストの位置を間違える",
    detail: "`(double)(a / b)` のようにカッコ全体に掛けると、先に整数除算してから `double` 化することになり、小数部が消えます。`(double) a / b` のように 割り算する前のオペランド にキャストするのが正解です",
  },
  {
    course: "java-basics",
    lesson: "java-type-cast",
    lessonTitle: "型変換 (キャスト)",
    heading: "桁あふれ (overflow)",
    detail: "`long → int` や `int → byte` のような縮小変換で、変換先の範囲を超えると値が壊れます。たとえば `int n = (int) 3000000000L;` は `int` の上限 (約 21 億) を超えるため、まったく違う負の数になります。`(int)` のキャストはコンパイラに「失われてもいいから変換する」と伝える宣言なので、警告は出ません。範囲を超えていないか自分でチェックする責任があります",
  },
  {
    course: "java-basics",
    lesson: "java-type-cast",
    lessonTitle: "型変換 (キャスト)",
    heading: "`(double)` を片側だけに付けるのを忘れる",
    detail: "`int a = 7, b = 2;` のとき、`double r = a / b;` と書いても結果は `3.0` で、`3.5` にはなりません。整数除算してから `double` に拡大しているだけだからです。少なくともどちらか一方に `(double)` を明示しないと、小数演算にはなりません",
  },
  {
    course: "java-basics",
    lesson: "java-if-basic",
    lessonTitle: "if 文の基本",
    heading: "`{ }` を省略してハマる",
    detail: "─ `if (x > 0) return \"positive\";` のように `{ }` なしで 1 行書いた後、「処理を追加しよう」と 2 行目を足しても、その 2 行目は `if` の中ではなく外として扱われます。インデントが揃っているのでぱっと見では気づきにくく、デバッグに時間を取られる典型例です",
  },
  {
    course: "java-basics",
    lesson: "java-if-basic",
    lessonTitle: "if 文の基本",
    heading: "`if (x = 0)` と書いてしまう",
    detail: "─ 代入の `=` と比較の `==` の混同です。`x = 0` は「`x` に `0` を代入する」という意味で、条件式としては成り立ちません。Java では `boolean` を期待する場所に `int` の代入結果が来るのでコンパイルエラーになり、最近の言語の中では比較的安全です",
  },
  {
    course: "java-basics",
    lesson: "java-if-basic",
    lessonTitle: "if 文の基本",
    heading: "`if (x);` と書いてしまう",
    detail: "─ `if` のカッコの直後にセミコロン `;` を置くミスです。これは「条件が true のとき何もしない」という空文として扱われ、その後ろの `{ }` は `if` と無関係なただのブロックになります。文法エラーにならないのでとくに厄介で、コードレビューで指摘されるまで気づかないことすらあります",
  },
  {
    course: "java-basics",
    lesson: "java-if-else",
    lessonTitle: "if-else 文",
    heading: "`else` の前にセミコロン `;` を入れる",
    detail: "`if (n % 2 == 0) { ... };` のように `}` の後ろに `;` を書いてしまうと、その時点で文が終わってしまい、続く `else` が孤立してエラーになります。`} else {` の前に余計な `;` を入れない、これが鉄則です",
  },
  {
    course: "java-basics",
    lesson: "java-if-else",
    lessonTitle: "if-else 文",
    heading: "`else` に条件を書いてしまう (else if との混同)",
    detail: "`else (n > 0) { ... }` のように、`else` の後ろに丸カッコで条件を書こうとする間違いです。条件付きの else を書きたいときは `else if (n > 0) { ... }` のように `else if` と書きます。`else` 単体は条件を取らない、と覚えてください",
  },
  {
    course: "java-basics",
    lesson: "java-if-else",
    lessonTitle: "if-else 文",
    heading: "`%` と `==` の優先順位の勘違い",
    detail: "`n % 2 == 0` という式は `%` のほうが `==` より先に計算される ので、内部的には `(n % 2) == 0` と評価されます。心配ならカッコを付けて `(n % 2) == 0` と書いてもまったく問題ありません。むしろ可読性が上がるので、慣れないうちはカッコを付けるのもおすすめです",
  },
  {
    course: "java-basics",
    lesson: "java-if-elseif",
    lessonTitle: "else if で複数分岐",
    heading: "範囲の広い条件を上に書いてしまう",
    detail: "先ほどの `grade` の例。`score >= 60` を一番上に書くと、`95` 点でも `C` 判定になります。厳しい条件を上、ゆるい条件を下 が鉄則",
  },
  {
    course: "java-basics",
    lesson: "java-if-elseif",
    lessonTitle: "else if で複数分岐",
    heading: "`else if` を `elseif` と書く",
    detail: "`Java` では `else` と `if` の 間に半角スペースが必要 です。`elseif` と続けて書くと `';' expected` や `'else' without 'if'` のようなコンパイルエラーになります。`Python` の `elif` とも違うので注意",
  },
  {
    course: "java-basics",
    lesson: "java-if-elseif",
    lessonTitle: "else if で複数分岐",
    heading: "最後の `else` を忘れて `return` が足りない",
    detail: "`if` と `else if` だけ書いて `else` を書かないと、戻り値のあるメソッドでは `missing return statement` のコンパイルエラーになります。最後は `else` でガッチリ閉じる、または `if` の外側で `return` を 1 本用意する",
  },
  {
    course: "java-basics",
    lesson: "java-condition-grade",
    lessonTitle: "成績判定プログラム",
    heading: "`>=` を `>` と書いてしまう",
    detail: "「`90` 以上」のつもりで `if (score > 90)` と書くと、ちょうど `90` 点が `\"A\"` ではなく `\"B\"` に落ちてしまいます。「以上」は `>=`、「より大きい」は `>`、と日本語と記号を必ずペアで覚えてください",
  },
  {
    course: "java-basics",
    lesson: "java-condition-grade",
    lessonTitle: "成績判定プログラム",
    heading: "条件の順序を逆にしてしまう",
    detail: "`if (score >= 60) ... else if (score >= 70) ...` のように下から並べると、`70` 点以上の人もみんな最初の `score >= 60` でヒットしてしまい、`\"D\"` のままになります。`else if` チェーンは必ず狭い (厳しい) 条件から書く",
  },
  {
    course: "java-basics",
    lesson: "java-condition-grade",
    lessonTitle: "成績判定プログラム",
    heading: "範囲を重複させる",
    detail: "`if (score >= 90)` と `else if (score >= 80 && score < 95)` のように、範囲が重なる書き方をすると、どちらにヒットするかが順番に依存してしまい、読む人を混乱させます。境界はきっちり閉じる",
  },
  {
    course: "java-basics",
    lesson: "java-condition-grade",
    lessonTitle: "成績判定プログラム",
    heading: "`else` を忘れて漏れを作る",
    detail: "`else if (score >= 60)` で止めて `else` を書かないと、`50` 点のときに `\"F\"` を返す経路がなくなり、コンパイラに「missing return statement」と怒られます。多段階の `if` の最後は必ず `else` で受ける",
  },
  {
    course: "java-basics",
    lesson: "java-condition-leap-year",
    lessonTitle: "うるう年を判定する",
    heading: "`%` と `==` の優先順位を勘違いする",
    detail: "`year % 4 == 0` を「`year % (4 == 0)`」と解釈してしまう人がいますが、実際は `%` のほうが `==` より優先順位が高いので `(year % 4) == 0` と評価されます。心配なときは自分で `()` を付けて意図を明確にしてもよいです",
  },
  {
    course: "java-basics",
    lesson: "java-condition-leap-year",
    lessonTitle: "うるう年を判定する",
    heading: "`&&` と `||` の優先順位を取り違える",
    detail: "`&&` は `||` より優先順位が高いです。`a || b && c` は `a || (b && c)` の意味になります。うるう年式で `(year % 4 == 0 && year % 100 != 0)` のように カッコで囲んでいる のはこの理由で、可読性とミス防止のためにカッコを残すのがおすすめです",
  },
  {
    course: "java-basics",
    lesson: "java-condition-leap-year",
    lessonTitle: "うるう年を判定する",
    heading: "`==` と `=` の打ち間違い",
    detail: "`year % 4 == 0` を `year % 4 = 0` と書くと「`year % 4` に `0` を代入」しようとしてコンパイルエラーになります。比較は イコール 2 個、代入は イコール 1 個、と暗唱して指に覚えさせてください",
  },
  {
    course: "java-basics",
    lesson: "java-switch",
    lessonTitle: "switch 文",
    heading: "`break` を忘れる",
    detail: "一番多いミス。`case 1:` の処理後に `break;` を書かないと、`case 2:` のブロックに勝手に流れ込みます。`return` で抜ける形なら起きませんが、`System.out.println(...)` などで終わる場合は 必ず `break;` を最後に書くこと",
  },
  {
    course: "java-basics",
    lesson: "java-switch",
    lessonTitle: "switch 文",
    heading: "`case` の値の型ミスマッチ",
    detail: "`switch (day)` で `day` が `int` なのに `case \"1\":` と書くと、`incompatible types: String cannot be converted to int` というエラーが出ます。`case` の値は `switch` の式と 同じ型 にする必要があります",
  },
  {
    course: "java-basics",
    lesson: "java-switch",
    lessonTitle: "switch 文",
    heading: "`default` を書き忘れる",
    detail: "戻り値があるメソッドだと `missing return statement` のコンパイルエラーで気づけますが、`void` メソッドだと黙って通り抜けてバグの温床になります。`default` は常に書く習慣を付けましょう",
  },
  {
    course: "java-basics",
    lesson: "java-switch",
    lessonTitle: "switch 文",
    heading: "`switch` 式 (Java 14+) との混同",
    detail: "`Java 14` から追加された `switch` 式 は `String s = switch (day) { case 1 -> \"Monday\"; ... };` のように `->` (アロー) を使う新構文です。従来の `switch` 文 (`case ... :` + `break;`) とは別物で、構文を混ぜると `';' expected` などのエラーが出ます。このレッスンでは古典的な `switch` 文の方を使います",
  },
  {
    course: "java-basics",
    lesson: "java-ternary",
    lessonTitle: "三項演算子",
    heading: "`?` と `:` の順番を逆にする",
    detail: "正しいのは `条件 ? 真の値 : 偽の値` の順番です。`条件 : 真の値 ? 偽の値` のように書くとコンパイルエラーになります。「クエスチョン (`?`) が先、コロン (`:`) が後」と覚えましょう",
  },
  {
    course: "java-basics",
    lesson: "java-ternary",
    lessonTitle: "三項演算子",
    heading: "ネストして読めなくする",
    detail: "`a ? b ? c : d : e ? f : g` のような書き方は、構造を理解していてもパッと意味が取れません。3 段以上のネストは避けて `if-else` や `switch` を使う",
  },
  {
    course: "java-basics",
    lesson: "java-ternary",
    lessonTitle: "三項演算子",
    heading: "両辺の型がずれてコンパイルエラー",
    detail: "`?` の後と `:` の後で型が違う (例: `n >= 0 ? \"正の数\" : -n`) と、Java は「結果の型がひとつに決まらない」と怒ります。文字列と数値を混ぜないように、両辺の型を必ず揃えましょう",
  },
  {
    course: "java-basics",
    lesson: "java-condition-janken",
    lessonTitle: "じゃんけんの勝敗判定",
    heading: "条件の網羅性が足りない",
    detail: "勝ちパターンを 2 つしか書かずに残り 1 つを忘れる、引き分けの判定を書き忘れる、といったケースです。テストケースを増やしてあげると、こういった抜けが必ず炙り出されます。先ほどの `Mermaid` 図のように、すべての分岐を絵にしてから書くと抜けが減ります",
  },
  {
    course: "java-basics",
    lesson: "java-condition-janken",
    lessonTitle: "じゃんけんの勝敗判定",
    heading: "`||` と `&&` の使い間違い",
    detail: "`me == 0 || opponent == 1` のように、本来 `&&` で繋ぐべき条件を `||` で書いてしまうミスです。`||` は どちらか片方でも `true` ならパスしてしまうので、「自分がグー」というだけで勝ち扱いになるなど、判定が壊滅的に狂います。「両方とも成り立つ必要があるなら `&&`、どれか 1 つでも成り立てばよいなら `||`」と毎回声に出して確かめてください",
  },
  {
    course: "java-basics",
    lesson: "java-condition-janken",
    lessonTitle: "じゃんけんの勝敗判定",
    heading: "引き分けの検出忘れ",
    detail: "勝ちパターンだけ書いて、勝ちでなかったらすべて `\"lose\"` を返してしまうと、`janken(1, 1)` のような同じ手のときも `\"lose\"` になります。`me == opponent` をどこかで必ず判定する必要があります",
  },
  {
    course: "java-basics",
    lesson: "java-condition-fizzbuzz-partial",
    lessonTitle: "FizzBuzz の判定部分",
    heading: "判定順序を逆にする",
    detail: "─ `if (n % 3 == 0) return \"Fizz\";` から書き始めてしまうと、`15` のときも `\"Fizz\"` が返ってしまい、`\"FizzBuzz\"` の出番がなくなります。`FizzBuzz` (両方の倍数) を 必ず最初に 判定するのが鉄則です",
  },
  {
    course: "java-basics",
    lesson: "java-condition-fizzbuzz-partial",
    lessonTitle: "FizzBuzz の判定部分",
    heading: "`String.valueOf` を忘れて型エラー",
    detail: "─ 戻り値の型は `String` なのに、`return n;` と書いてしまうと「`int` を `String` として返せません」というコンパイルエラーになります。整数を文字列にしたいときは `String.valueOf(n)` か `Integer.toString(n)` を使うか、もしくは `return \"\" + n;` のように空文字と連結する手もあります",
  },
  {
    course: "java-basics",
    lesson: "java-condition-fizzbuzz-partial",
    lessonTitle: "FizzBuzz の判定部分",
    heading: "`% 3 && % 5` のような略記を使う",
    detail: "─ `n % 3 && n % 5` のように書きたくなる人がいますが、これは文法エラーです。`&&` の左右には `boolean` が必要なので、必ず `n % 3 == 0 && n % 5 == 0` のように `== 0` まで 書いてください",
  },
  {
    course: "java-basics",
    lesson: "java-for-basic",
    lessonTitle: "for 文の基本",
    heading: "区切り文字をカンマにしてしまう",
    detail: "`for (int i = 0, i < 10, i++)` のようにカンマで区切るのは間違いで、正しくはセミコロン `;` です",
  },
  {
    course: "java-basics",
    lesson: "java-for-basic",
    lessonTitle: "for 文の基本",
    heading: "条件と更新を逆にして無限ループ",
    detail: "`for (int i = 0; i < 10; i--)` のように更新が逆向きだと `i` が永遠に増えないので、ループが終わりません。chotdekiru の Java Playground では `5` 秒の実行制限で強制終了されますが、本番環境では `CPU` を食い続けてシステムが固まります",
  },
  {
    course: "java-basics",
    lesson: "java-for-basic",
    lessonTitle: "for 文の基本",
    heading: "ループ変数をループの外で使ってしまう",
    detail: "`for (int i = 0; i < 10; i++)` の `i` は丸カッコ内で宣言されているので、`for` ブロックの外では使えません",
  },
  {
    course: "java-basics",
    lesson: "java-for-sum",
    lessonTitle: "1 から N までの合計",
    heading: "`sum` を初期化せずに使う",
    detail: "`int sum;` とだけ書いて `sum = sum + i;` をやると、`Java` のコンパイラに「`variable sum might not have been initialized`」と怒られます。初期値 `0` を必ず書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-for-sum",
    lessonTitle: "1 から N までの合計",
    heading: "継続条件を `i < n` にしてしまう",
    detail: "`i < n` だと最後の `n` が足されません。`sumOneToN(10)` の答えが `55` ではなく `45` になります。`<=` と `<` の違いで結果がガラッと変わるので、ループ条件を書いたら頭の中で「`i` は最後にいくつまで到達するか?」を必ず確認するクセをつけましょう",
  },
  {
    course: "java-basics",
    lesson: "java-for-sum",
    lessonTitle: "1 から N までの合計",
    heading: "`int` の桁あふれ (overflow)",
    detail: "`n` がとても大きい (`100000` 以上など) と、合計が `int` の最大値 `約 21 億` を超えて、マイナスの数になってしまうことがあります。今回の課題では `n` は小さいので気にしなくて OK ですが、本格的な数値計算では `long` 型を使うのが安全です",
  },
  {
    course: "java-basics",
    lesson: "java-for-multiplication",
    lessonTitle: "九九の段を作る",
    heading: "0 から始める / 1 から始めるの混同",
    detail: "九九は `n * 1` から `n * 9` までです。`for (int i = 0; i < 9; i++)` と書くと `i` は 0〜8 になり、`n * 0 = 0` が先頭に入ってしまいます。今回は `i = 1; i <= 9; i++` が正解です",
  },
  {
    course: "java-basics",
    lesson: "java-for-multiplication",
    lessonTitle: "九九の段を作る",
    heading: "末尾の余計な空白",
    detail: "`result = result + value + \" \";` のように書くと、最後の値の後ろにも空白が残ります。テストは `\"3 6 9 12 15 18 21 24 27\"` のように末尾に空白なしを期待しているので、`if (i == 1)` で分岐するか、`if (i > 1)` で先に空白を入れるパターンを使いましょう",
  },
  {
    course: "java-basics",
    lesson: "java-for-multiplication",
    lessonTitle: "九九の段を作る",
    heading: "`i` と `n` の取り違え",
    detail: "文字列に並べるのは `n * i` の結果であって、`i` そのものではありません。うっかり `result = result + i;` と書くと `\"1 2 3 4 5 6 7 8 9\"` がいつも返ってきてしまい、`n = 3` でも `n = 7` でも同じ出力になります",
  },
  {
    course: "java-basics",
    lesson: "java-while-basic",
    lessonTitle: "while 文",
    heading: "条件を更新し忘れて無限ループ",
    detail: "`while (n > 0) { count = count + 1; }` のように `n` を更新する行を書き忘れると、`n` は永遠に正のままで、`count` だけが増え続けます。Java の executor では `5` 秒で強制終了されますが、本物のアプリでは画面が固まって最悪のユーザー体験になります。ループの中で「条件に関係する変数を必ず変える」のは絶対のルールです",
  },
  {
    course: "java-basics",
    lesson: "java-while-basic",
    lessonTitle: "while 文",
    heading: "初期値を境界の外に置いてしまう",
    detail: "`int count = 1;` のように `count` を最初から `1` にしてしまうと、すべての結果が `+1` ずれます。「ループに入る前に何回実行したことになっているか」をいつも意識して、初期値を決めましょう",
  },
  {
    course: "java-basics",
    lesson: "java-while-basic",
    lessonTitle: "while 文",
    heading: "エッジケース (`0` や負の数) を考えていない",
    detail: "今回は `0` を `1` 桁として扱う仕様でしたが、これに気づかず `while (n > 0)` だけで提出すると、`digitCount(0)` が `0` を返してテスト失敗です。仕様の境界値は必ずテストケースに混ぜて、コードを書く前に紙に書き出すクセをつけましょう",
  },
  {
    course: "java-basics",
    lesson: "java-while-count",
    lessonTitle: "カウントダウン",
    heading: "終了条件の `> 0` と `>= 1` の混同",
    detail: "整数の `n` であればどちらも結果は同じですが、`n > 0` と書くつもりで `n >= 0` と書いてしまうと、`0` までカウントが進んで `\"3 2 1 0\"` のように余計な `0` が混ざります。`>= 1` か `> 0` のどちらかで意図を統一しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-while-count",
    lessonTitle: "カウントダウン",
    heading: "`n--` の位置を間違えて無限ループ",
    detail: "`result = result + n + \" \";` の 前 に `n--` を書いてしまうと、最初の値 `n` が結果に入らないまま 1 減って、想定とずれた文字列になります。さらに `n--` 自体を書き忘れると `n` が永遠に減らず無限ループです",
  },
  {
    course: "java-basics",
    lesson: "java-while-count",
    lessonTitle: "カウントダウン",
    heading: "末尾に余計な空白が残る",
    detail: "`result + n + \" \"` で連結すると、必ず最後にスペースが 1 つ余ります。`\"3 2 1 \"` のように末尾に空白が残った状態のままだと、テストの `\"3 2 1\"` と一致せず fail します。`trim()` で末尾の空白を必ず削ること",
  },
  {
    course: "java-basics",
    lesson: "java-do-while",
    lessonTitle: "do-while 文",
    heading: "`do { ... } while (条件)` の末尾セミコロン忘れ",
    detail: "`do { ... } while (n <= 0)` で終わると「`;` expected」というコンパイルエラーになります。`do-while` は文の最後に 必ず `;` を付けるのがルールです。`while` 文と違うところなので意識して書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-do-while",
    lessonTitle: "do-while 文",
    heading: "条件の方向を逆にする (上昇 vs 下降)",
    detail: "「正の数を見つける」課題なのに `while (n > 0);` と書いてしまうと、最初から正の数だったときに即終わり、負だったときは無限ループになります。「ループを 続ける ための条件」を書く、という基本に立ち戻ってください。`n` を上昇させて正を探すなら、ループ継続条件は `n <= 0` (まだ正でない間続ける) です",
  },
  {
    course: "java-basics",
    lesson: "java-do-while",
    lessonTitle: "do-while 文",
    heading: "更新を忘れて無限ループ",
    detail: "`do { ... } while (n <= 0);` の中で `n++` を書き忘れると、`n` がずっと初期値のままで条件が変わらず、永遠にループします。`Java` の Playground では実行が 5 秒で打ち切られますが、本番システムでこれをやると CPU 100% でサーバーが落ちます。ループ本体で必ず変数を更新する を呪文にしてください",
  },
  {
    course: "java-basics",
    lesson: "java-nested-loop",
    lessonTitle: "二重ループ",
    heading: "`i` と `j` を混同する",
    detail: "内側で書きたいのに `i++` と書いてしまうと、外側のカウンタを勝手にいじることになり、無限ループや想定外の動作になります。「外側 `i`、内側 `j`」と最初に決めてから書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-nested-loop",
    lessonTitle: "二重ループ",
    heading: "内側ループ後の改行を忘れる",
    detail: "1 行ぶんの `*` を並べたあとに `\\n` を追加しないと、全部が 1 行につながって `***` のような出力になります。改行は内側ループの「外」、外側ループの「中」**が定位置です",
  },
  {
    course: "java-basics",
    lesson: "java-nested-loop",
    lessonTitle: "二重ループ",
    heading: "末尾に余計な改行が付く",
    detail: "`pyramid(3)` の期待値は `\"*\\n\\n*\"` で、末尾の `\\n` はありません。毎回改行を付けてしまうと最後にも 1 つ余分に付き、テストが fail します。`if (i < n - 1)` で最後の行だけ改行をスキップするか、改行を行の「先頭」に置いて 1 行目だけスキップするテクニックを使いましょう",
  },
  {
    course: "java-basics",
    lesson: "java-break-continue",
    lessonTitle: "break と continue",
    heading: "`break` と `return` を混同する",
    detail: "`break` はループから抜けるだけで、メソッド自体は終わりません。一方 `return` はメソッドそのものを終了します。「ループの外でまだ処理を続けたいなら `break`、メソッドごと終わらせたいなら `return`」と使い分けてください。「`break` を書いたのに `return` まで処理が来てしまった」と慌てる人が多いですが、それは `break` の正しい動きです",
  },
  {
    course: "java-basics",
    lesson: "java-break-continue",
    lessonTitle: "break と continue",
    heading: "`continue` で更新を忘れて無限ループ",
    detail: "`while` ループの中で `continue` を使うとき要注意です。`int i = 0; while (i < 10) { if (i == 3) continue; i++; }` のように、`continue` の前にループ変数を更新していないと、`i = 3` のところで永遠に止まります。`for` ループなら更新式が自動的に走るので比較的安全ですが、`while` のときは `continue` の前に必ず更新を済ませる、または `continue` の中でも更新する書き方を意識しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-break-continue",
    lessonTitle: "break と continue",
    heading: "ラベル付き `break` の構文ミス",
    detail: "ラベルは `outer:` のように `:` をつけて `for` の 直前の行に単独で 書きます。`for outer:` や `outer for` のような書き方はコンパイルエラーです。`break outer;` の側はラベル名のあとに空白を入れて、最後に `;` で締めます。たまにしか使わない構文なので、必要なときは公式ドキュメントで形を確認してください",
  },
  {
    course: "java-basics",
    lesson: "java-loop-fizzbuzz",
    lessonTitle: "FizzBuzz 完成",
    heading: "判定順序を逆にする",
    detail: "─ 前章でも触れた話ですが、`if (i % 3 == 0)` を最初に書いてしまうと `15` のときも `\"Fizz\"` が返ってしまい、`\"FizzBuzz\"` が一度も出てきません。必ず `i % 15 == 0` を最初に判定する か、`i % 3 == 0 && i % 5 == 0` を最初に書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-loop-fizzbuzz",
    lessonTitle: "FizzBuzz 完成",
    heading: "末尾のスペースを取り忘れる",
    detail: "─ `\"1 2 Fizz 4 Buzz \"` のように末尾にスペースが残ると、テストの期待値 `\"1 2 Fizz 4 Buzz\"` と異なり fail します。`trim()` で取り除くか、最初から先頭判定方式で書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-loop-fizzbuzz",
    lessonTitle: "FizzBuzz 完成",
    heading: "`n` が `0` のときに何が返るかを考えていない",
    detail: "─ もし `fizzbuzzList(0)` が呼ばれたら、`for` は 1 回も回らず `result` は `\"\"` のまま `trim()` を経て空文字列が返ります。今回のテストでは `n >= 1` しか使いませんが、業務コードなら エッジケース として「0 のときに何を返すべきか」を意識しておくとバグが減ります",
  },
  {
    course: "java-basics",
    lesson: "java-method-basic",
    lessonTitle: "メソッドを定義する",
    heading: "戻り値型を `void` にしているのに値を `return` してしまう",
    detail: "`void` は「戻り値なし」の意味なので、`return \"Hello\";` のように値を返すとコンパイルエラーになります。値を返したいなら戻り値型を `String` や `int` に変更しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-method-basic",
    lessonTitle: "メソッドを定義する",
    heading: "`return;` だけ書いて値を返し忘れる",
    detail: "逆に戻り値型を `String` にしているのに `return;` とだけ書くと「missing return value」エラーが出ます。戻り値の型に合った値を必ず指定してください",
  },
  {
    course: "java-basics",
    lesson: "java-method-basic",
    lessonTitle: "メソッドを定義する",
    heading: "`static` の付け忘れ",
    detail: "chotdekiru の coding 問題は `Solution.getGreeting()` のように、クラス名から直接呼び出します。`static` を忘れるとインスタンス化が必要なメソッドになってしまい、テスト側から呼べずに fail します",
  },
  {
    course: "java-basics",
    lesson: "java-method-args",
    lessonTitle: "引数を受け取るメソッド",
    heading: "引数の型を省略する",
    detail: "`public static int square(n)` のように `int` を書き忘れると、`Java` は「`n` の型がわからない」と怒ります。引数は 必ず型から書く、と覚えましょう。同じ理由で `int a, b` ではなく `int a, int b` のように毎回型を書きます",
  },
  {
    course: "java-basics",
    lesson: "java-method-args",
    lessonTitle: "引数を受け取るメソッド",
    heading: "引数の数を間違えて呼び出す",
    detail: "メソッド側が `square(int n)` のように 1 個の引数を期待しているのに、呼び出し側で `Solution.square()` (引数なし) や `Solution.square(3, 4)` (引数 2 個) と書くと、`method square in class Solution cannot be applied to given types` のようなエラーが出ます。引数の 個数と型 が完全に一致していないとコンパイルは通りません",
  },
  {
    course: "java-basics",
    lesson: "java-method-args",
    lessonTitle: "引数を受け取るメソッド",
    heading: "変数名が衝突すると思い込む",
    detail: "呼び出し側に `int n = 5;` という変数があると、「メソッド側の `n` と被るからダメなんじゃない?」と心配する人がいます。実際には別物として扱われるので、まったく問題ありません。むしろ呼び出し側でもメソッド側でも、その場で自然な名前を使うほうが読みやすいコードになります",
  },
  {
    course: "java-basics",
    lesson: "java-method-return",
    lessonTitle: "戻り値を返す",
    heading: "`return` 文を書かない",
    detail: "戻り値の型を `String` や `int` と書いたのに、メソッド本体のどこにも `return` がないパターン。エディタのエラー欄に「`missing return statement`」と出たら、まず全ての分岐に `return` が届いているか確認しましょう。`if` で `return` して `else` で `return` しないと、それだけで未到達の分岐ができてしまいます",
  },
  {
    course: "java-basics",
    lesson: "java-method-return",
    lessonTitle: "戻り値を返す",
    heading: "`return` の後にコードを書く (dead code)",
    detail: "`return 0;` の次の行に `System.out.println(\"...\")` のような処理を書いてしまうやつ。デバッグ用の `println` を消し忘れる事故が多いです。`return` の後に書いてあるコードは絶対に動きません",
  },
  {
    course: "java-basics",
    lesson: "java-method-return",
    lessonTitle: "戻り値を返す",
    heading: "戻り値型と `return` 値の型不一致",
    detail: "`int` を返すと書いたのに `\"123\"` のような `String` を返してしまうケース。逆に `String` を返すメソッドで `return 0;` と書いてしまうのもよくあります。エラー「`incompatible types`」「`cannot return a value of type X`」が目印",
  },
  {
    course: "java-basics",
    lesson: "java-method-multi-args",
    lessonTitle: "複数の引数を取るメソッド",
    heading: "引数の順序を逆に渡す",
    detail: "`rectangleArea(width, height)` のつもりが `rectangleArea(height, width)` で呼んでしまうケース。長方形の面積では結果が同じなので気づきにくいですが、`drawRect(x, y, width, height)` のような関数では位置がズレて図形が変な場所に描かれます。呼び出すときは「メソッド宣言と同じ順番で書けているか」を必ず確認してください",
  },
  {
    course: "java-basics",
    lesson: "java-method-multi-args",
    lessonTitle: "複数の引数を取るメソッド",
    heading: "型を 1 個だけ書く",
    detail: "`int width, height` のように書いて、`int width, int height` と書き忘れるパターン。`Java` のコンパイラから `';' expected` `<identifier> expected` といった分かりにくいエラーが出ます。引数 1 個ごとに型を書く、を呪文として唱えてください",
  },
  {
    course: "java-basics",
    lesson: "java-method-multi-args",
    lessonTitle: "複数の引数を取るメソッド",
    heading: "引数 0 個のメソッドと混同する",
    detail: "1 つ前のレッスンで書いた `helloWorld()` のような引数なしメソッドの感覚で、`rectangleArea()` と引数を渡さずに呼び出してしまうケース。コンパイラから `method rectangleArea in class Solution cannot be applied to given types` と長いエラーが出ます。引数を必要とするメソッドは、呼び出すときも同じ数だけ値を渡す必要があります",
  },
  {
    course: "java-basics",
    lesson: "java-array-basic",
    lessonTitle: "配列の基本",
    heading: "1 始まりと勘違いする",
    detail: "数学やスプレッドシートに慣れていると、最初の要素を `arr[1]` と書きたくなります。`Java` のインデックスは `0` 始まりなので、最初は `arr[0]`、最後は `arr[arr.length - 1]` です。`arr[arr.length]` と書くと off-by-one エラー で `ArrayIndexOutOfBoundsException` になります",
  },
  {
    course: "java-basics",
    lesson: "java-array-basic",
    lessonTitle: "配列の基本",
    heading: "`length` をメソッド呼び出しで書く",
    detail: "`arr.length()` と書きたくなりますが、配列の `length` は プロパティ なのでカッコは付けません。`String` の `s.length()` (カッコあり) との違いが地味に厄介で、コンパイラに `cannot find symbol` と怒られたら大抵これです",
  },
  {
    course: "java-basics",
    lesson: "java-array-basic",
    lessonTitle: "配列の基本",
    heading: "null チェックを忘れる",
    detail: "`int[] arr = null;` の状態で `arr.length` や `arr[0]` を触ると、即 `NullPointerException` で落ちます。引数で受け取る配列は、信頼できないなら `if (arr == null)` のチェックを先に入れる癖をつけましょう",
  },
  {
    course: "java-basics",
    lesson: "java-array-access",
    lessonTitle: "配列の要素にアクセス",
    heading: "`arr.length` と `arr.length()` の混同",
    detail: "配列は `length` でフィールド、`String` は `length()` でメソッド。逆にすると「cannot find symbol」コンパイルエラーになる",
  },
  {
    course: "java-basics",
    lesson: "java-array-access",
    lessonTitle: "配列の要素にアクセス",
    heading: "最後の要素を `arr[arr.length]` で取ろうとする",
    detail: "0 始まりなので最後は `arr[arr.length - 1]`。`arr[arr.length]` は範囲外で `ArrayIndexOutOfBoundsException`",
  },
  {
    course: "java-basics",
    lesson: "java-array-access",
    lessonTitle: "配列の要素にアクセス",
    heading: "負のインデックスを渡す",
    detail: "一部の言語 (例えば `Python`) では `arr[-1]` で末尾要素が取れますが、Java では 負のインデックスはすべて例外。便利機能はないので、自分で `arr.length - 1` を書く必要がある",
  },
  {
    course: "java-basics",
    lesson: "java-array-access",
    lessonTitle: "配列の要素にアクセス",
    heading: "`length == 0` の配列で先頭にアクセスする",
    detail: "空配列に対して `arr[0]` を呼ぶと例外。要素にアクセスする前に `arr.length > 0` をチェックする習慣を付ける",
  },
  {
    course: "java-basics",
    lesson: "java-array-loop",
    lessonTitle: "配列を for で走査",
    heading: "`i < arr.length` を `i <= arr.length` にしてしまう",
    detail: "配列の添字は `0` から `arr.length - 1` までです。`i <= arr.length` だと最後の周で `arr[arr.length]` という存在しない要素にアクセスし、`ArrayIndexOutOfBoundsException` で落ちます。継続条件は必ず `<`、と決めてしまうのが安全です",
  },
  {
    course: "java-basics",
    lesson: "java-array-loop",
    lessonTitle: "配列を for で走査",
    heading: "末尾に余計な `, ` が残る",
    detail: "`result = result + arr[i] + \", \";` のように書くと、最後の要素の後ろにもカンマと空白が付いてしまい、`\"1, 2, 3, \"` のような出力になります。テストはこれを fail と判定します。先頭だけ特別扱い (`if (i > 0)` で前に `\", \"`) で組み立てるのが定番のテクニックです",
  },
  {
    course: "java-basics",
    lesson: "java-array-loop",
    lessonTitle: "配列を for で走査",
    heading: "空配列の処理を忘れる",
    detail: "`arr.length` が `0` の場合に何を返すかを考えていないと、`NullPointerException` や思わぬ文字列を返してしまうことがあります。今回の課題では空配列は `\"\"` を返す決まりです。`for (int i = 0; i < arr.length; i++)` パターンなら自然にループをスキップしてくれるので、安心して使えます",
  },
  {
    course: "java-basics",
    lesson: "java-array-sum",
    lessonTitle: "Javaで配列の合計・平均を求める方法（forループ）",
    heading: "`sum` を初期化しない",
    detail: "`int sum;` と書いただけだと、`sum += x;` のところで `Java` のコンパイラに `variable sum might not have been initialized` と怒られます。必ず `int sum = 0;` と初期値を書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-array-sum",
    lessonTitle: "Javaで配列の合計・平均を求める方法（forループ）",
    heading: "累積の代わりに上書き",
    detail: "`sum = x;` と書いてしまうと、毎回 `sum` の中身が `x` で上書きされて、最後の要素しか残りません。`arraySum({1, 2, 3})` が `3` を返してきたら、まずここを疑ってください。`sum = sum + x;` (または `sum += x;`) と書く、が累積パターンの命です",
  },
  {
    course: "java-basics",
    lesson: "java-array-sum",
    lessonTitle: "Javaで配列の合計・平均を求める方法（forループ）",
    heading: "`null` チェックを書きすぎる / 書かなさすぎる",
    detail: "今回の課題では `arr` が `null` で渡されることは想定していませんが、実務だと `null` が来る可能性があります。安全側に倒すなら `if (arr == null) return 0;` を冒頭に書きます。逆に「`null` ではなく空配列で渡す」と決めたなら、書く必要はありません。呼び出し側との契約をはっきりさせる ことが、`null` を巡るバグを防ぐ一番のコツです",
  },
  {
    course: "java-basics",
    lesson: "java-array-avg",
    lessonTitle: "配列の平均",
    heading: "整数除算のままにしてしまう",
    detail: "`return sum / arr.length;` と書くと戻り値の型が `double` でも、右辺の `int / int` の結果 `int` が `double` に変換されるだけです。`arrayAvg(new int[]{4, 7})` で `5.5` を期待しているのに `5.0` が返ってきて「あれ?」となります。割り算の前に、片方を `(double)` でキャスト しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-array-avg",
    lessonTitle: "配列の平均",
    heading: "キャストの位置を間違える",
    detail: "`(double)(sum / arr.length)` は意味がありません。括弧の中で先に `int` の割り算が走ってしまうからです。`(double) sum / arr.length` のように、キャストは割り算の左側の値だけに掛けるのが正解です",
  },
  {
    course: "java-basics",
    lesson: "java-array-avg",
    lessonTitle: "配列の平均",
    heading: "空配列を素通りさせる",
    detail: "`if (arr.length == 0) return 0.0;` を忘れると、`arr.length` が `0` のとき `sum / 0` を計算しようとして `ArithmeticException` や `NaN` が出てきます。割る前に必ず分母が `0` でないかをチェックするのが鉄則です",
  },
  {
    course: "java-basics",
    lesson: "java-array-max",
    lessonTitle: "配列の最大値",
    heading: "`max = 0` で初期化して負の数で失敗",
    detail: "`int max = 0;` で始めると、配列が `[-1, -5, -3]` のように 全要素がマイナス のとき、答えが `0` になってしまいます。実際の最大値は `-1` なのに、です。`max` の初期値は `arr[0]` または `Integer.MIN_VALUE` にするのが鉄則です",
  },
  {
    course: "java-basics",
    lesson: "java-array-max",
    lessonTitle: "配列の最大値",
    heading: "空配列で `IndexOutOfBoundsException`",
    detail: "`int max = arr[0];` を書いた直後に、空配列 `[]` が渡されると、`arr[0]` にアクセスした瞬間に例外が飛びます。必ず `if (arr.length == 0) return 0;` のような 空配列ガード を先頭に置いてください",
  },
  {
    course: "java-basics",
    lesson: "java-array-max",
    lessonTitle: "配列の最大値",
    heading: "`>=` と `>` の違い",
    detail: "更新条件を `arr[i] >= max` と書いても答え自体は同じになりますが、`>=` だと「同じ値」に出会うたびに上書きが走るので、わずかに余計な代入が発生します。`>` で「より大きいときだけ更新する」のが教科書的な書き方です",
  },
  {
    course: "java-basics",
    lesson: "java-array-search",
    lessonTitle: "配列の線形探索",
    heading: "見つかった後もループを続けてしまう",
    detail: "`for` の中で `return i;` ではなく、`int found = i;` のように変数に保存するだけにしてしまうと、後ろにある同じ値で `found` が上書きされてしまうことがあります。「先頭から見て 最初 に見つかった位置」という仕様なので、見つけた瞬間に `return` で抜けるのが正解です。どうしてもループの後で返したい場合は、見つかった時点で `break;` してから `return found;` する形にしましょう",
  },
  {
    course: "java-basics",
    lesson: "java-array-search",
    lessonTitle: "配列の線形探索",
    heading: "`index` の初期化忘れ / 初期化ミス",
    detail: "拡張 for で書くときに `int index = 0;` の宣言を忘れると `cannot find symbol` のコンパイルエラーになります。逆に `int index = -1;` から始めてしまうと、`arr[0]` が `target` と一致したときに `-1` が返ってしまい、テストが fail します。初期値は `0` が正解です",
  },
  {
    course: "java-basics",
    lesson: "java-array-search",
    lessonTitle: "配列の線形探索",
    heading: "複数一致の挙動を取り違える",
    detail: "`arr = [10, 20, 10, 20]` で `target = 10` の答えは、`0` (最初の `10`) であって `2` (後ろの `10`) ではありません。普通の `for` ループで先頭から `return` する書き方になっていれば自然に `0` が返りますが、「最後に一致した index を返したい」と仕様を勘違いすると `2` を返すコードを書いてしまいます。仕様を読むときに「最初」「最後」「全部」のどれを期待されているか必ず確認するクセを付けましょう",
  },
  {
    course: "java-basics",
    lesson: "java-array-foreach",
    lessonTitle: "拡張 for ループ",
    heading: "`for-each` で要素を書き換えたつもりになる",
    detail: "`for (int x : arr) { x = ... }` は元の配列に反映されない。書き換えたいときは通常の `for` 文を使い、`arr[i]` に直接代入する",
  },
  {
    course: "java-basics",
    lesson: "java-array-foreach",
    lessonTitle: "拡張 for ループ",
    heading: "コロン `:` の代わりにカンマやセミコロンを使う",
    detail: "通常の `for` 文の影響で `for (int x ; arr)` や `for (int x , arr)` と書いてしまう。正しいのは コロン `:` 一文字 で、それ以外は全部コンパイルエラー",
  },
  {
    course: "java-basics",
    lesson: "java-array-foreach",
    lessonTitle: "拡張 for ループ",
    heading: "要素の型を間違える",
    detail: "`int[]` の配列に対して `for (String x : arr)` とやると `incompatible types` のエラーになる。要素の型と一致させる必要があり、自信がなければ `var` (Java 10 以降) でコンパイラに推論させるのも手",
  },
  {
    course: "java-basics",
    lesson: "java-class-define",
    lessonTitle: "クラスを定義する",
    heading: "クラス名を小文字で始める",
    detail: "`public class book { ... }` のように小文字で書くとコンパイル自体は通ってしまうことがあるものの、`Java` の世界ではクラス名を小文字で始めるのは強い `アンチパターン` です。チームのコードレビューでも一発で指摘されます。必ず `Book` のように大文字始まりで書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-class-define",
    lessonTitle: "クラスを定義する",
    heading: "ファイル名とクラス名が食い違う",
    detail: "`Book.java` の中に `public class Library { ... }` と書くと、`class Library is public, should be declared in a file named Library.java` というエラーで止まります",
  },
  {
    course: "java-basics",
    lesson: "java-class-define",
    lessonTitle: "クラスを定義する",
    heading: "1 つのファイルに `public class` を複数書く",
    detail: "`Book` と `Library` の両方を `public class` で書こうとすると同じくコンパイルエラーになります",
  },
  {
    course: "java-basics",
    lesson: "java-class-fields",
    lessonTitle: "クラスにフィールドを持たせる",
    heading: "アクセス修飾子を書き忘れる",
    detail: "── 修飾子なしで `String title;` と書くと「パッケージプライベート」になります。最初のうちは `public` を明示的に書きましょう",
  },
  {
    course: "java-basics",
    lesson: "java-class-fields",
    lessonTitle: "クラスにフィールドを持たせる",
    heading: "`static` の付け間違い",
    detail: "── インスタンスフィールドにすべき題名に `static` を付けてしまうと、全 `Book` オブジェクトでタイトルが共有されてしまいます",
  },
  {
    course: "java-basics",
    lesson: "java-class-fields",
    lessonTitle: "クラスにフィールドを持たせる",
    heading: "初期化を忘れて `NullPointerException`",
    detail: "── `String title;` のまま `book.title.length()` を呼ぶと `null` に対してメソッドを呼ぶことになり、実行時にクラッシュします",
  },
  {
    course: "java-basics",
    lesson: "java-class-methods",
    lessonTitle: "クラスにメソッドを定義する",
    heading: "`static` を付けたり外したりを間違える",
    detail: "クラス内のフィールドを使うインスタンスメソッドに `static` を付けると、`non-static field cannot be referenced from a static context` というエラーが出ます。逆にユーティリティ的な計算なのに `static` を外すと、毎回 `new` してから呼ばないといけなくなり不便です。「フィールドに触るかどうか」で判断しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-class-methods",
    lessonTitle: "クラスにメソッドを定義する",
    heading: "`this` を使わずに引数とフィールドを混同する",
    detail: "メソッドの引数とフィールドが同じ名前のとき、`title = title;` と書いても何も起きません (引数が引数に代入されているだけ)。`this.title = title;` のように、フィールド側だけ `this.` を付けて区別する必要があります",
  },
  {
    course: "java-basics",
    lesson: "java-class-methods",
    lessonTitle: "クラスにメソッドを定義する",
    heading: "戻り値の型と `return` の値がずれる",
    detail: "メソッドの宣言が `public String getDescription()` なのに `return 123;` と書くとコンパイルエラーです。戻り値の型と `return` で返す値の型は必ず一致させてください。何も返さないなら戻り値型を `void` に変える、というルールも忘れずに",
  },
  {
    course: "java-basics",
    lesson: "java-class-instance",
    lessonTitle: "インスタンスを作る",
    heading: "`new` を書き忘れる",
    detail: "`Book b = Book();` のように `new` を抜いてしまうと、「`Book` という名前のメソッドを呼ぼうとした」と解釈されてコンパイルエラーになります。インスタンスを作るときは必ず `new` を先頭に付けてください",
  },
  {
    course: "java-basics",
    lesson: "java-class-instance",
    lessonTitle: "インスタンスを作る",
    heading: "変数宣言と `new` を混同する",
    detail: "`Book b;` だけで終わってしまうと、変数 `b` は宣言されていますが中身は `null` のままです。この状態で `b.read()` を呼ぶと実行時に `NullPointerException` が飛んできます。必ず `= new Book();` まで書ききりましょう",
  },
  {
    course: "java-basics",
    lesson: "java-class-instance",
    lessonTitle: "インスタンスを作る",
    heading: "`static` メソッドをインスタンスから呼ぼうとする",
    detail: "`Solution.helloWorld()` のような `static` メソッドは、本来クラス名から呼ぶものです。インスタンスを作って `s.helloWorld()` と書いてもエラーにはなりませんが、警告が出るうえに意味も薄れます。`static` はクラス、それ以外はインスタンス、と使い分けましょう",
  },
  {
    course: "java-basics",
    lesson: "java-class-constructor",
    lessonTitle: "コンストラクタ",
    heading: "コンストラクタに戻り値型を書いてしまう",
    detail: "─ `public void Book(...)` `public String Book(...)` のように `void` や `String` を入れてしまうと、`Java` のコンパイラはそれを 同じ名前のただのメソッド として扱います。引数付きで `new Book(\"x\", \"y\")` のように呼ぼうとするとコンパイルエラーになります（コンストラクタとして認識されるものが引数なしのデフォルトコンストラクタしかないため）。一方、引数なしで `new Book()` と呼んだ場合はデフォルトコンストラクタが動くものの、フィールドは初期化されないままになります。`public ClassName(...) {` の形を厳守してください",
  },
  {
    course: "java-basics",
    lesson: "java-class-constructor",
    lessonTitle: "コンストラクタ",
    heading: "複数コンストラクタの解決ミス",
    detail: "─ `public Book(String title)` と `public Book(String title, String author)` の両方を用意した状態で `new Book(\"Java入門\")` と呼ぶと、引数 1 個の方が選ばれます。`new Book(\"Java入門\", null)` を呼ぶと 2 引数の方が選ばれ、`author` が `null` のまま `fullTitle()` を実行すると `null` が出力に混ざります。引数の数と意味は、コンストラクタを増やすたびに丁寧に確認しましょう",
  },
  {
    course: "java-basics",
    lesson: "java-class-constructor",
    lessonTitle: "コンストラクタ",
    heading: "デフォルトコンストラクタが消えていることに気づかない",
    detail: "─ 自分で `public Book(String title, String author)` を 1 つ追加した瞬間、`new Book()` は使えなくなります。テストコードや別ファイルでまだ `new Book()` を呼んでいると、`constructor Book in class Book cannot be applied to given types` というエラーが出ます。引数なしも使いたいなら、`public Book() {}` を明示的に書き足してください",
  },
  {
    course: "java-basics",
    lesson: "java-class-this",
    lessonTitle: "this キーワード",
    heading: "`static` メソッドの中で `this` を使ってしまう",
    detail: "`public static String describe()` の中で `this.title` と書くと「`non-static variable this cannot be referenced from a static context`」と怒られます。`static` メソッドはクラス全体に属するので、「自分自身のインスタンス」が存在しません。インスタンスメソッド (`static` が付かないメソッド) でだけ `this` が使えます",
  },
  {
    course: "java-basics",
    lesson: "java-class-this",
    lessonTitle: "this キーワード",
    heading: "`this` を省略したつもりが、ローカル変数を上書きしてしまう",
    detail: "`Book(String title) { title = title; }` のように書くと、引数 `title` を引数自身に代入するだけでフィールドは空のまま。フィールドへ書き込みたいときは必ず `this.title = title;` と書くか、引数名を変える (`_title` `newTitle` など) ようにします",
  },
  {
    course: "java-basics",
    lesson: "java-class-this",
    lessonTitle: "this キーワード",
    heading: "コンストラクタで `this(...)` を 2 行目以降に書く",
    detail: "`this(...)` はコンストラクタの 先頭行 でなければなりません。何か別の処理を挟むと「`call to this must be first statement in constructor`」というエラーになります。共通処理は `this(...)` で呼ばれる側のコンストラクタにまとめるのが鉄則です",
  },
  {
    course: "java-basics",
    lesson: "java-class-encapsulation",
    lessonTitle: "カプセル化 (private と getter)",
    heading: "フィールドをついつい `public` にしてしまう",
    detail: "書き始めのうちは、`public int balance;` のほうがコード量が少なくて楽に感じます。けれどそれは「これから先、誰かが変な値を入れても止められない」という時限爆弾を埋めている状態です。フィールドは原則 `private`、必要に応じて getter / setter を足す という順番を体に染み込ませてください。`Java` の世界では「`private` が当たり前、`public` フィールドは要相談」が共通認識です",
  },
  {
    course: "java-basics",
    lesson: "java-class-encapsulation",
    lessonTitle: "カプセル化 (private と getter)",
    heading: "getter / setter を機械的に全フィールドに作る",
    detail: "`IDE` の自動生成でフィールドの数だけ getter / setter が並ぶと、見た目はリッチですが、実質的には `public` フィールドと同じ「中身を素通しできるクラス」になりがちです。setter は「外から自由に書き換えてほしい場合だけ」用意する、それ以外は読み取り専用にする (getter だけ作る)、という判断をするとカプセル化が一段強くなります。`balance` のように外から直接いじらせたくない値には、`deposit(int)` `withdraw(int)` のような 意図のあるメソッド を用意するのが理想です",
  },
  {
    course: "java-basics",
    lesson: "java-class-encapsulation",
    lessonTitle: "カプセル化 (private と getter)",
    heading: "ボイラープレートだらけのクラスになって本質が埋もれる",
    detail: "`private` フィールドと getter / setter を律儀に書いていくと、`Java` のコードはどうしても縦に長くなります。意味のあるロジックが 1 行なのに、その周りに 50 行のボイラープレートがついている、ということもよくあります。これを `Lombok` の `@Getter` `@Setter` や `Java 16` 以降の `record` で短く書く、というテクニックもありますが、まずは「カプセル化は手間に見合った価値がある」と感じてもらえる程度の手書きに慣れることが大事です",
  },
  {
    course: "java-basics",
    lesson: "java-class-person",
    lessonTitle: "Person クラスを作る",
    heading: "フィールドの初期化漏れ",
    detail: "フィールドだけ宣言して、コンストラクタの中で `this.name = name;` を書き忘れるパターン。コンパイルは通ってしまうのに、`introduce()` を呼ぶと `name` が `null` のままで `\"null (0)\"` のような結果が返ってきます。`Java` では `String` のフィールドは初期値 `null`、`int` のフィールドは初期値 `0` になる仕様なので、入れ忘れに気づきにくいのが厄介です",
  },
  {
    course: "java-basics",
    lesson: "java-class-person",
    lessonTitle: "Person クラスを作る",
    heading: "コンストラクタなしで放置する",
    detail: "コンストラクタを 1 つも書かないと、`Java` が自動で 引数なしのデフォルトコンストラクタ を作ってしまいます。すると `new Person(\"太郎\", 25)` という書き方ができず、コンパイラから `constructor Person in class Person cannot be applied to given types` と怒られます。引数付きで初期化したいなら、その引数を取るコンストラクタを必ず自分で書く のが鉄則です",
  },
  {
    course: "java-basics",
    lesson: "java-class-person",
    lessonTitle: "Person クラスを作る",
    heading: "`equals` を実装していないのに `==` で比較する",
    detail: "2 人の `Person` を比べたくて `taro == hanako` のように `==` で書くと、`Java` は メモリ上の同じ場所を指しているか だけを見ます。中身が同じでも `new` で別々に作っていれば結果は `false` です。中身を比べたいときは `equals` メソッドを自分で実装する必要があります",
  },
  {
    course: "java-basics",
    lesson: "java-class-toString",
    lessonTitle: "toString のオーバーライド",
    heading: "`@Override` アノテーションを忘れる",
    detail: "付け忘れてもコンパイル自体は通ってしまうので、気づかずにメソッド名を `tostring` と書き間違えても、コンパイラは何も言ってくれません。すると `toString()` は親の実装が呼ばれ続け、いつまで経っても `Point@1540e19d` のままで「あれ、書いたはずなのに反映されない」とハマります。`@Override` を付けておけば、コンパイル時に即エラーで教えてくれます",
  },
  {
    course: "java-basics",
    lesson: "java-class-toString",
    lessonTitle: "toString のオーバーライド",
    heading: "引数を勝手に増やしてしまう",
    detail: "`Object.toString()` は `引数なし` のメソッドです。これを `public String toString(int n)` のように引数付きにしてしまうと、それは別のメソッドの `オーバーロード` (overload) になり、`オーバーライド` にはなりません。`@Override` を付けていればコンパイラが「親クラスに引数 `int` の `toString` はない」と教えてくれます",
  },
  {
    course: "java-basics",
    lesson: "java-class-toString",
    lessonTitle: "toString のオーバーライド",
    heading: "戻り値の型を間違える",
    detail: "`Object.toString()` の戻り値は `String` です。これを `void` にしたり、`Object` にしたりすると、`String` 以外の戻り値を持つメソッドはオーバーライドとして認められません。やはり `@Override` を付けておけばコンパイラが守ってくれます",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-basics",
    lessonTitle: "ArrayList を使う",
    heading: "`import` を忘れる",
    detail: "`ArrayList` を使うには `import java.util.ArrayList;` が必要です。これを忘れると `cannot find symbol` というエラーが出ます。IDE なら自動補完でやってくれますが、手書きの試験などでは注意しましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-basics",
    lessonTitle: "ArrayList を使う",
    heading: "要素アクセスで `list[0]` と書いてしまう",
    detail: "`ArrayList` はクラスなので、配列の `[]` 記法は使えません。必ず `list.get(0)` のようにメソッド呼び出しで取り出します。逆に、取り出した値の型 (`Integer`) を `int` 変数に入れるときは `int x = list.get(0);` のように自動で変換されます (`オートアンボクシング`)",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-basics",
    lessonTitle: "ArrayList を使う",
    heading: "raw 型で書いてキャストが必要になる",
    detail: "`ArrayList list = new ArrayList();` のようにジェネリクスを省くと、`get(0)` の戻り値は `Object` になります。`Integer x = (Integer) list.get(0);` のように明示的なキャストが必要になり、安全性も下がります。型は必ず `<>` で指定しましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-iterate",
    lessonTitle: "List を走査する",
    heading: "`list.size` と書いてしまう",
    detail: "要素数は `list.size()` です。配列の `arr.length` はカッコなしのフィールドですが、`ArrayList` の `size()` は メソッド なので必ず `()` が必要です。`list.size` と書くとコンパイルエラーになります",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-iterate",
    lessonTitle: "List を走査する",
    heading: "`int` と `Integer` を取り違える",
    detail: "`ArrayList<Integer>` には基本型の `int` をそのまま入れているように見えますが、実はラッパクラス `Integer` に自動変換 (auto-boxing) されています。`list.remove(5)` と書くと「インデックス 5 番目を削除」、`list.remove(Integer.valueOf(5))` と書くと「値が 5 の要素を削除」になり、結果がまったく違うので注意してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-iterate",
    lessonTitle: "List を走査する",
    heading: "走査中に `remove` する",
    detail: "上で書いた `ConcurrentModificationException` の話です。拡張 for ループ中に `list.remove(...)` を呼ぶと例外が飛びます。安全に消すなら `Iterator.remove()` を使うか、別 `List` に集めてからまとめて削除します",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-remove",
    lessonTitle: "要素の削除と検索",
    heading: "`remove(int)` と `remove(Object)` の混同",
    detail: "特に `List<Integer>` で頻発します。位置で消すのか、値で消すのかを意識し、値で消したい場合は `Integer.valueOf(...)` で包みましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-remove",
    lessonTitle: "要素の削除と検索",
    heading: "`null` を渡したときの挙動",
    detail: "`contains(null)` や `remove(null)` は実行時例外にはならず、`null` を要素として比較します。`null` を入れないリストを作りたいなら、追加前に `if (value != null)` でガードします",
  },
  {
    course: "java-intermediate",
    lesson: "java-list-remove",
    lessonTitle: "要素の削除と検索",
    heading: "拡張 `for` 文の中で `remove` を呼ぶ",
    detail: "`for (String s : list) { list.remove(s); }` のように書くと `ConcurrentModificationException` で落ちます。走査中に消したいときは `Iterator.remove` を使うか、`list.removeIf(s -> 条件)` のような書き方に切り替えてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-map-basics",
    lessonTitle: "HashMap の基本",
    heading: "`null` をキーに使ってしまう",
    detail: "`HashMap` は技術的には `null` キーを 1 つだけ許容しますが、可読性が落ちるうえ `ConcurrentHashMap` では NPE を投げます。実務では `null` キーは使わないと決めておきましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-map-basics",
    lessonTitle: "HashMap の基本",
    heading: "auto-boxing を忘れて型エラー",
    detail: "`Map<String, Integer>` に `int` を `put` するときは自動で `Integer` に箱詰め (auto-boxing) されますが、逆に `int x = map.get(\"apple\")` のように受け取るとき、もし `null` が返ると NPE になります。`Integer x = ...` で受けるか `getOrDefault` でデフォルト値を指定するのが安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-map-basics",
    lessonTitle: "HashMap の基本",
    heading: "順序が保たれていると勘違いする",
    detail: "`HashMap` は `put` した順番を保持しません。順番に処理したいときは `LinkedHashMap` を使うか、キーをリスト化してソートしてから回します",
  },
  {
    course: "java-intermediate",
    lesson: "java-map-iterate",
    lessonTitle: "Map を走査する",
    heading: "`HashMap` の順序を期待してしまう",
    detail: "`put` した順や `key` のソート順が出てくると思い込むのは典型的なバグです。順序が必要なら `LinkedHashMap` か `TreeMap` を選ぶ",
  },
  {
    course: "java-intermediate",
    lesson: "java-map-iterate",
    lessonTitle: "Map を走査する",
    heading: "`Map.Entry` を `null` チェックしてしまう",
    detail: "`for-each` が回している `Map.Entry` 自体は `null` にはなりません。`null` チェックすべきは中身の `value` (`e.getValue() == null`) であって、`Entry` そのものではありません",
  },
  {
    course: "java-intermediate",
    lesson: "java-map-iterate",
    lessonTitle: "Map を走査する",
    heading: "`map.entrySet()` の戻り型を勘違いする",
    detail: "`entrySet()` は `Set<Map.Entry<K,V>>` を返します。`List` でも `Map` でもなく `Set` なので、インデックスでアクセスはできません。あくまで「ペアの集合」をループするためのものだと覚えましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-set-basics",
    lessonTitle: "Set で重複を排除",
    heading: "`HashSet` の順序を期待してしまう",
    detail: "`HashSet` は要素の並びを保証しません。`for` で回したときの順番がプログラムを再起動したら変わる、ということも普通に起きます。順序が大事なら `LinkedHashSet` か `TreeSet` を使いましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-set-basics",
    lessonTitle: "Set で重複を排除",
    heading: "`equals` / `hashCode` の実装を忘れる",
    detail: "自作クラスを `HashSet` に入れるときは、`equals` と `hashCode` を両方ともオーバーライドする必要があります。片方だけだと「同じはずの 2 つが別物扱いされる」事故が起きます",
  },
  {
    course: "java-intermediate",
    lesson: "java-set-basics",
    lessonTitle: "Set で重複を排除",
    heading: "`null` の扱いを誤解する",
    detail: "`HashSet` と `LinkedHashSet` は `null` を 1 個だけ入れられます。`TreeSet` は `null` を入れようとすると `NullPointerException` で落ちます。`Set` ならどれでも同じ、と思い込まないようにしましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-collections-utility",
    lessonTitle: "Collections クラスの便利メソッド",
    heading: "`Arrays.asList` で返るリストは固定長",
    detail: "`Arrays.asList(1, 2, 3)` の戻り値は `add` や `remove` を呼ぶと `UnsupportedOperationException` が飛びます。サイズを変えたいなら `new ArrayList<>(Arrays.asList(...))` のように `ArrayList` で ラップ してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-collections-utility",
    lessonTitle: "Collections クラスの便利メソッド",
    heading: "`Arrays.asList(int[])` は要素 1 個のリストになる",
    detail: "`int` 配列を `Arrays.asList(arr)` に渡すと、`List<int[]>` (要素数 `1` の、配列を 1 個だけ持つリスト) になってしまいます。`Collections.sort` も期待どおりに動きません。プリミティブ配列を List 化したいときは `for` ループか `Arrays.stream(arr).boxed()` を使う必要があります",
  },
  {
    course: "java-intermediate",
    lesson: "java-collections-utility",
    lessonTitle: "Collections クラスの便利メソッド",
    heading: "`Collections.sort` を `Comparator` なしで呼ぶ条件",
    detail: "`Collections.sort(list)` が引数 1 つで動くのは、要素が `Comparable` を実装している型 (`Integer` `String` `Double` など) のときだけです。自作クラスのリストを `sort` したい場合は、クラスに `implements Comparable<MyClass>` を書くか、`Collections.sort(list, comparator)` で `Comparator` を渡す必要があります",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-class",
    lessonTitle: "ジェネリッククラスを作る",
    heading: "raw 型で書いてしまう",
    detail: "─ `Box box = new Box(\"hello\");` のように `<>` を省略すると `raw 型` 扱いになり、`getValue()` の戻り値が `Object` になってしまいます。コンパイラは警告を出すだけで止めてはくれませんが、現代の Java では絶対に書かないのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-class",
    lessonTitle: "ジェネリッククラスを作る",
    heading: "プリミティブ型を `T` に入れようとする",
    detail: "─ `Box<int>` `Box<double>` はコンパイルエラーです。型パラメータには必ず参照型 (クラス) を渡す必要があるので、`int` は `Integer`、`double` は `Double`、`boolean` は `Boolean` というラッパー型を指定します",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-class",
    lessonTitle: "ジェネリッククラスを作る",
    heading: "`T` を `static` フィールドに使う",
    detail: "─ `static T defaultValue;` のような書き方はコンパイルエラーになります。`static` はクラス全体で共有される領域で、インスタンスごとに `T` が変わるという前提と矛盾するためです。`static` メソッドにジェネリクスを使いたいときは、メソッド側に `<T>` を書く「ジェネリックメソッド」という別の仕組みを使います",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-method",
    lessonTitle: "ジェネリックメソッド",
    heading: "`<T>` を戻り値の後ろに書いてしまう",
    detail: "`public static T <T> firstOf(...)` のように書くとコンパイルエラーです。必ず 戻り値型の直前 に `<T>` を置きましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-method",
    lessonTitle: "ジェネリックメソッド",
    heading: "raw 型のリストを渡してしまう",
    detail: "`List raw = new ArrayList();` のようなジェネリクスなしのリストを渡すと、`T` が `Object` 扱いになり警告が出ます。raw 型は禁忌 と覚えて、必ず `List<String>` のように型を指定してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-method",
    lessonTitle: "ジェネリックメソッド",
    heading: "型推論が効かない場面で `<>` を省く",
    detail: "`var x = Util.firstOf(List.of());` のように空のリストを直接渡すと推論できず `Object` になります。そういう時は `Util.<String>firstOf(List.of())` のように明示的に型を指定する必要があります",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-bounded",
    lessonTitle: "境界型パラメータ",
    heading: "`extends` を `implements` と書いてしまう",
    detail: "普段クラスで `implements Comparable` と書いている習慣で、つい `<T implements Comparable<T>>` と書きたくなりますが、これは文法エラーです。ジェネリクスでは、相手がクラスでもインターフェイスでも、境界はすべて `extends` で揃えます",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-bounded",
    lessonTitle: "境界型パラメータ",
    heading: "境界に `int` などのプリミティブを書こうとする",
    detail: "`<T extends int>` のような書き方はできません。ジェネリクスはそもそも `参照型` (クラス・インターフェイス) しか扱えないので、整数なら `Integer`、小数なら `Double` のように `ラッパー型` を境界に指定します",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-bounded",
    lessonTitle: "境界型パラメータ",
    heading: "境界を付けないまま `.doubleValue()` 等を呼ぶ",
    detail: "`<T>` だけだと、コンパイラは `T` を `Object` 相当としか見られないので、`Number` のメソッドは呼べません。「数値専用の処理を書きたい」と思ったら、まず `<T extends Number>` を疑うクセを付けると安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-wildcard",
    lessonTitle: "ワイルドカード ?",
    heading: "`?` と `T` を同じものだと思ってしまう",
    detail: "メソッドの型パラメータ `<T>` は「呼び出し時に決まる具体的な型」を表すので、引数と戻り値で同じ `T` を使うとリンクされます。一方 `?` は「何か未知の型」というだけで、複数の `?` は別々の未知です。「引数を読み取って同じ型のまま返したい」なら `<T> T pick(List<T> list)` のように `T` を使い、「中身は何でもいい、件数だけ知りたい」なら `?` を使う、と使い分けましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-wildcard",
    lessonTitle: "ワイルドカード ?",
    heading: "`List<?>` に `add` しようとする",
    detail: "`List<?>` は「中身の本当の型が分からないリスト」なので、安全に追加できる値は `null` だけです。`list.add(10)` と書くと `capture of ?` というエラーメッセージで止まります。`add` したいときは `<? super T>` を使うか、具体的な型 `<Integer>` を使ってください",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-wildcard",
    lessonTitle: "ワイルドカード ?",
    heading: "`PECS` の方向を逆に覚えてしまう",
    detail: "「`extends` で書き込めそう」「`super` で読み取れそう」と直感で考えると逆向きに覚えてしまいがちです。`Producer` (生産者 = 出す側) は `extends`、`Consumer` (消費者 = 受け取る側) は `super` という言葉ごと暗記してしまうのが早道です",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-pair",
    lessonTitle: "Pair<K,V> を作る",
    heading: "型パラメータの順序を逆にする",
    detail: "`Pair<K, V>` を `new Pair<Integer, String>(30, \"Alice\")` のように逆に渡してしまうと、`getKey()` が `Integer`、`getValue()` が `String` になります。`Map<K, V>` も同じ順序で、`Key → Value` が世界共通の並びだと覚えておきましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-pair",
    lessonTitle: "Pair<K,V> を作る",
    heading: "`equals` を書かずに `HashSet` `HashMap` で使う",
    detail: "自作 `Pair` を `Set` に入れて重複排除しようとしても、`equals` と `hashCode` を実装していないと別物として扱われます。値クラスは `Record` で書くか、`equals` と `hashCode` を必ずセットで実装しましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-pair",
    lessonTitle: "Pair<K,V> を作る",
    heading: "フィールドを可変にしてしまう",
    detail: "`key` `value` を `final` にせず、`setKey` のような setter を生やしてしまうと、`Pair` がいつでも変わる箱になってしまいます。`HashMap` のキーとして使った後に値を書き換えると、ハッシュ値が変わってキーを見失う、という最悪のバグを引き起こします。値クラスは必ず `不変` (`イミュータブル`) にしましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-chapter2-quiz",
    lessonTitle: "ジェネリクス まとめクイズ",
    heading: "`List<Integer>` は `List<Number>` のサブ型ではない",
    detail: "`Integer` は `Number` のサブ型でも、コレクションは 不変 (`invariant`) なのでサブ型関係が崩れる。柔軟にしたいときに `List<? extends Number>` を使う",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-chapter2-quiz",
    lessonTitle: "ジェネリクス まとめクイズ",
    heading: "`<T super Number>` は書けない",
    detail: "下限境界はクラス / メソッド定義では使えず、ワイルドカードでのみ使える",
  },
  {
    course: "java-intermediate",
    lesson: "java-generics-chapter2-quiz",
    lessonTitle: "ジェネリクス まとめクイズ",
    heading: "`new T()` は不可",
    detail: "型消去のため。`Class<T>` をコンストラクタで受け取って `clazz.getDeclaredConstructor().newInstance()` を呼ぶのが定番の回避策",
  },
  {
    course: "java-intermediate",
    lesson: "java-try-catch",
    lessonTitle: "try-catch で例外をつかむ",
    heading: "`catch (Exception e)` で握りつぶす",
    detail: "─ 何でもキャッチして `catch` ブロックが空っぽ (`{}` だけ) になっているコード。例外が完全に消えてしまい、本番でバグが起きても原因を追えません。最低でも `e.printStackTrace();` か業務ログ出力を入れる、というのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-try-catch",
    lessonTitle: "try-catch で例外をつかむ",
    heading: "`stacktrace` を捨てる",
    detail: "─ `catch (Exception e) { throw new RuntimeException(\"失敗\"); }` のように、元の `e` を渡さずに新しい例外を作るパターン。これだと「失敗」とだけ書かれた例外が飛んで、本当の原因 (どのファイルの何行目で何が起きたか) が消えます。必ず `throw new RuntimeException(\"失敗\", e);` のように原因を引き継ぎましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-try-catch",
    lessonTitle: "try-catch で例外をつかむ",
    heading: "`catch` の順序を間違える",
    detail: "─ 複数の `catch` を並べるときは、`catch (Exception e)` のような広いものを後ろに書きます。先頭に書くと、その後ろの `catch (NumberFormatException e)` は「絶対に到達できないコード」とみなされてコンパイルエラーになります。狭い型から広い型へ、の順番が原則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-multiple-catch",
    lessonTitle: "複数の catch ブロック",
    heading: "`Exception` を一番上に書いて到達不可コードを作る",
    detail: "`catch (Exception e)` を最初に置き、その下に `catch (NumberFormatException e)` を書くと、後者には永遠に到達しません。Java はビルドエラー (`exception has already been caught`) で止めてくれますが、原因がピンと来ないと数十分溶かしがちです。順序は 具体 → 抽象 が鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-multiple-catch",
    lessonTitle: "複数の catch ブロック",
    heading: "`catch` でもみ消して原因が分からなくなる",
    detail: "`catch (Exception e) { /* 何もしない */ }` のようにブロックを空にすると、例外が握り潰されて呼び出し側が異常に気付けません。最低でも `System.err.println(e)` でログを出すか、別の例外で再 `throw` するのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-multiple-catch",
    lessonTitle: "複数の catch ブロック",
    heading: "`finally` の中で `return` してしまう",
    detail: "`finally` の中で `return` を書くと、`try` や `catch` 内で書いた `return` が 上書きされて消える という非直感的な挙動になります。`finally` には後始末の処理だけを書き、`return` は `try`/`catch` 側にまとめておくのが安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-throws",
    lessonTitle: "throws と例外の伝播",
    heading: "`throws` を書き忘れて検査例外を投げる",
    detail: "`FileReader` などを使うコードを書いて `throws IOException` を忘れると、`unreported exception IOException; must be caught or declared to be thrown` というエラーが出ます。コンパイラのメッセージのとおり、`try-catch` か `throws` のどちらかを必ず書きましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-throws",
    lessonTitle: "throws と例外の伝播",
    heading: "`catch (Exception e) {}` で握り潰す",
    detail: "例外を `catch` しただけで中身を空にすると、エラーが起きてもログにも出ず、原因の追跡が極めて困難になります。最低でも `e.printStackTrace()` を呼ぶか、`throw new RuntimeException(e)` のように包み直して再送出するのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-throws",
    lessonTitle: "throws と例外の伝播",
    heading: "非検査例外に `throws` を書く",
    detail: "`throws NullPointerException` のように非検査例外を `throws` 句に書いても、コンパイラは強制してくれませんし、可読性も上がりません。`Javadoc` の `@throws` タグでドキュメントに書く方が読みやすいです",
  },
  {
    course: "java-intermediate",
    lesson: "java-custom-exception",
    lessonTitle: "独自の例外を作る",
    heading: "`Exception` を継承して検査例外まみれになる",
    detail: "何の気なしに `extends Exception` と書くと、その例外を投げるメソッドには `throws InsufficientFundsException` の宣言が必要になります。呼び出し元、その呼び出し元、と上に向かってずっと宣言が伝染し、ラムダや `Stream` の中で投げられなくなる、という悲劇が起きがちです。意図的に検査例外にしたいのでなければ `RuntimeException` 継承にしましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-custom-exception",
    lessonTitle: "独自の例外を作る",
    heading: "`super(message)` を呼び忘れて `getMessage()` が `null` になる",
    detail: "コンストラクタの中で `super(message)` を呼ばないと、親クラスにメッセージが伝わりません。後で `e.getMessage()` を見たら `null` で原因が全く分からない、というハマり方をします。引数で受け取った `message` は必ず `super(message)` (もしくは `super(message, cause)`) で渡してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-custom-exception",
    lessonTitle: "独自の例外を作る",
    heading: "`catch (Exception e) {}` で握り潰す",
    detail: "カスタム例外を作っておきながら、上の層で `catch (Exception e) {}` のように何もしないと、例外を作った意味が完全に消えます。最低でも `e.printStackTrace()` か、構造化されたロガー (`SLF4J` の `logger.error(...)`) で出力するクセを付けましょう。投げる例外と捕まえる例外は、必ずペアで設計するのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-try-with-resources",
    lessonTitle: "try-with-resources",
    heading: "`implements AutoCloseable` を忘れる",
    detail: "`try (Resource r = ...)` の `Resource` クラスが `AutoCloseable` を実装していないと、`required type that implements AutoCloseable` というコンパイルエラーになります。`class Resource implements AutoCloseable` と必ず書きましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-try-with-resources",
    lessonTitle: "try-with-resources",
    heading: "`close()` の中で重い処理や例外を派手に投げる",
    detail: "`close()` は「後始末」のメソッドです。ここでネットワーク通信をしたり、独自の例外を派手に投げたりすると、デバッグが非常に難しくなります。`close()` は冪等 (`idempotent`) に、つまり何回呼んでも安全になるよう書くのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-try-with-resources",
    lessonTitle: "try-with-resources",
    heading: "`try` の `()` の中で複数リソースを書く順番を間違える",
    detail: "`close()` は宣言と逆順で呼ばれるので、依存関係 (たとえば `Connection` → `Statement` → `ResultSet`) がある場合は、依存される側を先に書き、依存する側を後に書きます。`Connection` `Statement` `ResultSet` の順に宣言すれば、逆順で `ResultSet` → `Statement` → `Connection` の順に閉じられる、ということです",
  },
  {
    course: "java-intermediate",
    lesson: "java-finally",
    lessonTitle: "finally ブロック",
    heading: "`finally` の中で `return` を書いて例外を握りつぶす",
    detail: "`try` から飛んできた例外を `catch` していないのに、`finally` で `return` してしまうと、本来呼び出し元に伝わるはずだった例外が消えてしまいます。デバッグ時に「エラーがどこかで握りつぶされている」と気づくのが非常に難しくなるので、`finally` の中で `return` するのは原則禁止と覚えておきます",
  },
  {
    course: "java-intermediate",
    lesson: "java-finally",
    lessonTitle: "finally ブロック",
    heading: "`finally` で参照する変数を初期化していない",
    detail: "`try` ブロックの中だけで宣言した変数は、`finally` のスコープ外です。`reader` のようなリソース変数は `try` の 外側 で宣言し、`null` で初期化しておき、`finally` で `if (reader != null) reader.close();` のようにチェックしてから使うのが定石です",
  },
  {
    course: "java-intermediate",
    lesson: "java-finally",
    lessonTitle: "finally ブロック",
    heading: "リソース解放を `try` の最後に書いて、例外時に呼ばれない",
    detail: "「`reader.close()` を `try` ブロックの最後の行に書けば良い」と思いがちですが、`try` 中で例外が出るとそこから先は実行されません。`close()` が呼ばれず、ファイルハンドルやコネクションがリークします。リソース解放は `finally`、もしくは `try-with-resources` に必ず移す のが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-create",
    lessonTitle: "Stream を作る",
    heading: "Stream を使い回そうとする",
    detail: "前述の通り、ターミナル操作を呼んだ Stream は閉じます。`Stream<Integer> s = ...; s.count(); s.forEach(...);` のように 2 回呼ぶと例外で落ちます。中間状態を変数で取っておく書き方はやめて、メソッドチェーンで一気に書くのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-create",
    lessonTitle: "Stream を作る",
    heading: "`Collection` と `Stream` を混同する",
    detail: "`list.stream()` は新しい `Stream` を返すだけで、元の `list` は変わりません。`list.stream().filter(...)` の結果をどこにも代入せずに、「`list` から偶数だけ消えたはず」と思い込むのはよくあるミスです。Stream は元データを変更しません",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-create",
    lessonTitle: "Stream を作る",
    heading: "ターミナル操作を忘れる",
    detail: "`Arrays.stream(arr).filter(n -> n > 0);` と書いて「実行された」と思い込んでも、ターミナル操作がないので 何も起きません。これは `遅延評価` のせいで、`filter` を呼んだだけでは実際の処理が走らないからです。`count()` `collect()` `forEach()` などを最後に必ず付けましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-filter",
    lessonTitle: "filter で絞り込む",
    heading: "`filter` の中で副作用を書く",
    detail: "`.filter(n -> { System.out.println(n); return n > 0; })` のようにログ出力やフィールド更新を `filter` の中に書くのは避けるべきです。`filter` は純粋に判定だけをするのが約束で、副作用を入れるとデバッグが難しくなり、並列ストリームでは予期しない動作になります。ログを入れたいときは専用の `peek` を使うか、デバッグ用に一時的に挟むだけにしましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-filter",
    lessonTitle: "filter で絞り込む",
    heading: "複数 `filter` を分けて書きすぎる",
    detail: "`.filter(n -> n > 0).filter(n -> n < 100).filter(n -> n % 2 == 0)` のように `filter` をひたすら連結する書き方はパフォーマンスは大きく変わりませんが、可読性は条件によって落ちることがあります。論理 AND でつながる条件は `n -> n > 0 && n < 100 && n % 2 == 0` のように 1 つにまとめるか、逆に意味の単位で分割するかを意識すると読みやすくなります",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-filter",
    lessonTitle: "filter で絞り込む",
    heading: "`null` 要素が混ざる可能性を忘れる",
    detail: "`Stream<String>` のように参照型を扱う場合、要素に `null` が混ざることがあります。`.filter(s -> s.length() > 0)` と書くと、`null` 要素で `NullPointerException` が出てしまいます。先に `.filter(s -> s != null)` で `null` を弾いてから本来の条件を書く、または `.filter(Objects::nonNull)` を使うのが定石です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-map",
    lessonTitle: "map で変換する",
    heading: "`map` の中で副作用を書く",
    detail: "`map(x -> { System.out.println(x); return x; })` のように、変換のついでに `println` やフィールド書き換えをするのは Stream の作法に反します。表示やログ出力は `peek` か終端操作の `forEach` で行うのがルールです。副作用入りの `map` は並列ストリームで予期しない順序で動いて、バグの温床になります",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-map",
    lessonTitle: "map で変換する",
    heading: "型が合わずコンパイルエラー",
    detail: "`IntStream` の `map` は `int -> int` しか受け付けません。`IntStream.of(1, 2, 3).map(x -> \"num=\" + x)` のように `String` を返そうとすると怒られます。型を変えたいときは `mapToObj` を使うのが正解です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-map",
    lessonTitle: "map で変換する",
    heading: "null を返す `map`",
    detail: "`map(x -> something(x))` の中で `null` を返すと、後続の処理で `NullPointerException` が出やすくなります。null を返す可能性があるなら、`Optional` でラップするか `filter(Objects::nonNull)` で前処理するのが安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-reduce",
    lessonTitle: "reduce で集約する",
    heading: "`identity` を省略して `Optional` を扱い忘れる",
    detail: "`reduce(BinaryOperator)` のように引数 1 つ版もありますが、こちらは空 Stream の可能性があるため戻り値が `Optional<T>` になります。`int product = stream.reduce((a, b) -> a * b);` と書いてもコンパイルすら通らず、`Optional<Integer>` を `int` に代入できないと怒られます。今回のように空配列でも `1` を返したい場合は、`identity` を渡す 2 引数版を使うのが圧倒的に楽です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-reduce",
    lessonTitle: "reduce で集約する",
    heading: "`accumulator` に副作用を入れる",
    detail: "`reduce` の関数の中で `System.out.println` を呼んだり、外側のフィールドを書き換えたりするのは禁じ手です。並列化したときに表示順がぐちゃぐちゃになったり、`ConcurrentModificationException` が出たりして、デバッグ困難なバグの温床になります。`accumulator` は「`a` と `b` を受け取って新しい値を返すだけの純粋関数」に保つ、というのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-reduce",
    lessonTitle: "reduce で集約する",
    heading: "結合則を破る演算を使う",
    detail: "`reduce(0, (a, b) -> a - b)` のように引き算で書くと、順次実行では `0 - 1 - 2 - 3 = -6` になりますが、並列で `(0 - 1) と (2 - 3)` を計算して合成すると結果が変わってしまいます。減算や除算は結合的ではないので、`reduce` の `accumulator` には使わないのが原則です。どうしても引き算したいときは、`map(x -> -x).reduce(0, Integer::sum)` のように加算に変換するか、`for` ループで書くかを検討します",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-collect",
    lessonTitle: "collect で集める",
    heading: "`toMap` のキー重複",
    detail: "`toMap(keyMapper, valueMapper)` でキーが重複すると `IllegalStateException: Duplicate key` が飛びます。マージ方法を決めるには 3 引数版の `toMap(k, v, (a, b) -> a)` で「重複したら先勝ち」のように指定するか、そもそも `groupingBy` で `List` にまとめるのが定石です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-collect",
    lessonTitle: "collect で集める",
    heading: "`joining` の `delimiter` 指定忘れ",
    detail: "`Collectors.joining()` を引数なしで呼ぶと区切り文字なしで全部くっつきます。CSV のつもりで `joining()` と書いてしまうと `\"246\"` のようになるので、`joining(\",\")` を渡すのを忘れないようにしましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-collect",
    lessonTitle: "collect で集める",
    heading: "`toList()` の戻り値を変更しようとする",
    detail: "Java 16 から増えた `Stream.toList()` (引数なし) は `immutable` な `List` を返します。後で `add` したいなら `Collectors.toList()` を使うか、`new ArrayList<>(immutableList)` で詰め替えてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-sorted",
    lessonTitle: "sorted で並び替え",
    heading: "`sorted(null)` のように `Comparator` に `null` を渡す",
    detail: "Java 8 の仕様では、`sorted(Comparator)` に `null` を渡すと `NullPointerException` が飛びます。`sorted()` (引数なし) は自然順を意味するので、「自然順がほしい」なら 引数を渡さない ことが正解です。`null` をデフォルト扱いにしてはいけません",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-sorted",
    lessonTitle: "sorted で並び替え",
    heading: "自然順 (`Comparable`) を実装していない型で `sorted()` を呼ぶ",
    detail: "自作クラス (たとえば `class Point {}`) のリストを `sorted()` (引数なし) で並べ替えようとすると、実行時に `ClassCastException: ... cannot be cast to class java.lang.Comparable` が出ます。自作クラスを自然順で並べたいなら `implements Comparable<Point>` を書いて `compareTo` を実装するか、`sorted(Comparator.comparing(...))` を渡してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-sorted",
    lessonTitle: "sorted で並び替え",
    heading: "`Comparator` に副作用を持ち込む",
    detail: "`sorted((a, b) -> { counter++; return a - b; })` のように `Comparator` の中で外部変数を書き換えると、ソート結果が並列実行や JIT の最適化で予測不能になります。`Comparator` は「同じ入力に同じ出力を返す純粋関数」として書く、というのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-distinct",
    lessonTitle: "distinct で重複排除",
    heading: "自作クラスで `equals` / `hashCode` を実装し忘れる",
    detail: "前述のとおり、自作クラスを `distinct()` に通しても参照比較になり、重複排除されません。`record` を使うか、IDE の自動生成で `equals` と `hashCode` を必ずペアで書きましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-distinct",
    lessonTitle: "distinct で重複排除",
    heading: "Stream を 2 回使い回そうとする",
    detail: "`Stream` は一度終端操作を呼ぶと閉じられ、`IllegalStateException` が出ます。`Stream<String> s = list.stream();` を 2 回使い回すのではなく、必要に応じて `list.stream()` を再度呼び直すのが正解です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-distinct",
    lessonTitle: "distinct で重複排除",
    heading: "順序保証を勘違いする",
    detail: "`distinct()` は元の Stream の出現順を維持します。`{3, 1, 2, 1, 3}` を流すと `{3, 1, 2}` の順で残ります。並べ替えたいときは別途 `sorted()` を繋いでください。逆に `parallelStream()` では順序保証が崩れる場合があるので、順序が重要なら `stream()` を使うか `forEachOrdered` を選びます",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-limit-skip",
    lessonTitle: "limit と skip",
    heading: "`skip → limit` と `limit → skip` の順序を取り違える",
    detail: "`stream.skip(2).limit(3)` は「先頭 2 件を捨ててから 3 件取る」(`3, 4, 5` 番目を取得)、`stream.limit(3).skip(2)` は「先頭 3 件に絞ってから 2 件捨てる」(結果は 1 件) と挙動が変わります。ページネーションは必ず `skip → limit` の順番で書きます",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-limit-skip",
    lessonTitle: "limit と skip",
    heading: "無限 Stream に `limit` を付け忘れる",
    detail: "`Stream.iterate` や `Stream.generate` から作った無限 Stream を `forEach` や `count` に直接渡すと、プログラムが止まらなくなります。途中で必ず `limit` か `takeWhile` などで打ち切りましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-limit-skip",
    lessonTitle: "limit と skip",
    heading: "`limit(-1)` `skip(-1)` のように負の引数を渡す",
    detail: "`limit` も `skip` も負の値はサポートしておらず、実行時に `IllegalArgumentException` を投げます。`page - 1` を計算するときに、`page` が `0` や負の値になっていないかを必ず検証してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-parallel",
    lessonTitle: "並列ストリーム",
    heading: "`parallel()` を付ければ順序が保たれていると思い込む",
    detail: "`forEach` の出力順、`reduce` の集合体の組み立て順は 実行のたびに変わる と思って書く必要があります。順序が必要なら `forEachOrdered` か `Collectors.toList()` を選びましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-parallel",
    lessonTitle: "並列ストリーム",
    heading: "`非結合的` な reducer を渡してしまう",
    detail: "引き算や、文字列を結合順序つきで作る処理は、並列だと結果が変わります。`reduce(\"\", String::concat)` を並列で回すと、繋がる順番が毎回違うので、最終の文字列がランダムに並び替わったように見えます",
  },
  {
    course: "java-intermediate",
    lesson: "java-stream-parallel",
    lessonTitle: "並列ストリーム",
    heading: "小さな配列に `parallel()` を付けて逆に遅くなる",
    detail: "配列の要素数が数百〜数千程度では、並列化のオーバーヘッドのほうが大きく、`for` のほうが速いことが普通です。並列はデータが多くて、要素 1 個の計算が重いとき だけが効きます",
  },
  {
    course: "java-intermediate",
    lesson: "java-lambda-basics",
    lessonTitle: "ラムダ式の基本",
    heading: "式形式とブロック形式を混同する",
    detail: "`n -> n * 2` は `return` を書かない、`n -> { return n * 2; }` は書く、というルールです。`n -> { n * 2; }` のように `return` を抜くとコンパイルエラーになります。`{}` を付けたら必ず `return` を書く、と覚えてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-lambda-basics",
    lessonTitle: "ラムダ式の基本",
    heading: "ローカル変数を後から書き換えてしまう",
    detail: "ラムダの中で参照する外側のローカル変数は `final` または `effectively final` (代入が 1 度きり) でなければなりません。`int x = 10; Function<Integer,Integer> f = n -> n + x;` まではよくても、その後 `x = 20;` と書き換えるとエラーになります。インスタンスフィールドなら自由に書き換えできますが、ローカル変数は固定です",
  },
  {
    course: "java-intermediate",
    lesson: "java-lambda-basics",
    lessonTitle: "ラムダ式の基本",
    heading: "`return` を書ける場所と書けない場所を混同する",
    detail: "式形式は値そのものを返すので `return` を書きません。ブロック形式は普通のメソッドと同じく `return` で値を返します。式形式の右側にうっかり `return n * 2` と書くとコンパイルエラーになるので注意してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-method-reference",
    lessonTitle: "メソッド参照 ::",
    heading: "引数の数が合わない",
    detail: "`BiFunction<Integer, Integer, Integer>` に対して `Integer::parseInt` を渡すとコンパイルエラーです。`parseInt` は 1 引数なので、2 引数のインターフェースには使えません。ラムダで書いたときに渡している引数の数と、メソッドの引数の数を必ず一致させることを意識しましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-method-reference",
    lessonTitle: "メソッド参照 ::",
    heading: "戻り値の型が一致しない",
    detail: "`Function<String, Integer>` のところに `Function<String, Long>` を返すメソッド参照を渡そうとすると `incompatible types` のエラーになります。`Long::parseLong` と `Integer::parseInt` を混同しがちなので、戻り値の型まで揃っているか確認してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-method-reference",
    lessonTitle: "メソッド参照 ::",
    heading: "コンストラクタ参照を `ClassName.new` と書く",
    detail: "正しくは `ClassName::new` です。`.new` は内部クラスのインスタンス生成用の別文法なので、ここで使うと意味が変わってしまいます。`::new` という記号の並びをそのまま覚えるのが安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-function-interface",
    lessonTitle: "Function インターフェース",
    heading: "`Function` と `Consumer` を混同する",
    detail: "`Function<T, R>` は「値を返す」関数、`Consumer<T>` は「値を受け取って戻り値なし (`void`) 」の関数です。`System.out.println` を渡したいだけなら `Consumer` 、結果を返してほしいなら `Function` 、と用途で選び分けます。間違って `Function<Integer, Void>` のような無理な型を作る前に、`Consumer` の存在を思い出してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-function-interface",
    lessonTitle: "Function インターフェース",
    heading: "ラムダ式の型推論に失敗する",
    detail: "`var f = x -> x * 2;` のように `var` でいきなり受けようとすると、コンパイラが「ラムダ式単体では型を決められない」と言ってエラーになります。必ず `Function<Integer, Integer> f = x -> x * 2;` のように 左辺で型を宣言 するか、メソッド引数として渡してその場で文脈から型を決めるかのどちらかにします",
  },
  {
    course: "java-intermediate",
    lesson: "java-function-interface",
    lessonTitle: "Function インターフェース",
    heading: "`null` 引数を素通しさせる",
    detail: "`Function<String, Integer> parser = Integer::parseInt;` に `null` を渡すと `NullPointerException` が出ます。`Function` 自体は `null` を弾く仕組みを持たないので、`null` の可能性があるときは事前に `null` チェックするか、`Optional<T>` を組み合わせるかで身を守る必要があります",
  },
  {
    course: "java-intermediate",
    lesson: "java-predicate-interface",
    lessonTitle: "Predicate インターフェース",
    heading: "`test` の戻り値型を勘違いする",
    detail: "`Predicate<Integer>` を作るときに、つい `n -> n * 2` のような「整数を返すラムダ」を書いてしまうケースです。`Predicate.test` は必ず `boolean` を返す約束なので、`n -> n * 2` は `Predicate<Integer>` としては成立せず、`incompatible types` というコンパイルエラーになります。整数を別の整数に変換したいときは `Predicate` ではなく `Function<Integer, Integer>` を使うのが正解です",
  },
  {
    course: "java-intermediate",
    lesson: "java-predicate-interface",
    lessonTitle: "Predicate インターフェース",
    heading: "`null` 判定を手書きしすぎる",
    detail: "参照型を扱う `Predicate<String>` などでは、要素に `null` が混ざることがあります。素直に `s -> s != null && s.length() > 0` と書いてもよいのですが、`null` チェック専用のメソッド参照 `Objects::nonNull` が `java.util.Objects` に用意されています。`Predicate<String> notNull = Objects::nonNull;` のように書くと、意図が一発で伝わるのでおすすめです",
  },
  {
    course: "java-intermediate",
    lesson: "java-predicate-interface",
    lessonTitle: "Predicate インターフェース",
    heading: "`negate` の順序を間違える",
    detail: "`isPositive.and(isEven).negate()` と `isPositive.and(isEven.negate())` は、見た目は似ていますが意味がまるで違います。前者は「正かつ偶数」の否定 (= `負または奇数`)、後者は「正かつ奇数」です。`negate` がどの `Predicate` にかかっているかを意識して、必要なら `(...)` で明示するか、いったん変数に入れて段階的に組み立てるのが安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-consumer-supplier",
    lessonTitle: "Consumer と Supplier",
    heading: "`Consumer` の中で `return` を書いてしまう",
    detail: "`Consumer<String> c = s -> { return s.length(); };` のように戻り値を書くとコンパイルエラーになります。`accept` の戻り値は `void` なので、ラムダの中も値を返してはいけません。値を返したいのは `Function<T, R>` の役割です",
  },
  {
    course: "java-intermediate",
    lesson: "java-consumer-supplier",
    lessonTitle: "Consumer と Supplier",
    heading: "`Supplier.get()` を毎回呼ぶのに副作用付きのラムダを書いてしまう",
    detail: "`Supplier<Integer> next = () -> counter++;` のように、内側でフィールドを書き換える Supplier は危険です。同じ Supplier を `get()` するたびに別の値が返ってくるので、ライブラリ側がいつ呼ぶかわからない (`orElseGet` `Stream.generate` 等) 文脈で予測不能なバグになります。Supplier は基本的に副作用なしで書きましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-consumer-supplier",
    lessonTitle: "Consumer と Supplier",
    heading: "`hello.get` のようにかっこを忘れて参照だけ書く",
    detail: "`hello` は `Supplier<String>` ですが、`String s = hello;` と書くと型が違うとエラーになります。値を取り出すには必ず `hello.get()` のようにかっこ付きで `get()` を呼びます。`Consumer` も同様で、呼び出すときは `printer.accept(\"x\")` の形が必要です",
  },
  {
    course: "java-intermediate",
    lesson: "java-bifunction",
    lessonTitle: "BiFunction と 2 引数の関数",
    heading: "型パラメータの数を間違える",
    detail: "`BiFunction<Integer, Integer>` のように 2 個しか書かないと「型パラメータが足りない」エラーになります。`BiFunction` は引数 2 個 + 戻り値 1 個の 合計 3 個 が必須です。逆に `Function<T, R>` は 2 個、`Predicate<T>` は 1 個、と数が違うので、ペアで覚えるのがコツです",
  },
  {
    course: "java-intermediate",
    lesson: "java-bifunction",
    lessonTitle: "BiFunction と 2 引数の関数",
    heading: "3 引数以上を `BiFunction` で書こうとする",
    detail: "標準ライブラリには `BiFunction` までしかありません。`TriFunction` を `import` しようとして `cannot find symbol` で立ち止まる人が多いですが、これは 自作するか、引数をまとめる のが正解です。`java.util.function.TriFunction` というクラスは存在しません",
  },
  {
    course: "java-intermediate",
    lesson: "java-bifunction",
    lessonTitle: "BiFunction と 2 引数の関数",
    heading: "1 引数の `Function` と混同する",
    detail: "`Function<Integer, Integer> f = (x, y) -> x + y;` は文法エラーです。`Function` は引数 1 個なので、ラムダも `(x) -> ...` か `x -> ...` の形である必要があります。2 引数を受け取りたいなら型を `BiFunction` に変えるか、ラムダの引数を 1 つにします",
  },
  {
    course: "java-intermediate",
    lesson: "java-string-format",
    lessonTitle: "String.format で書式化",
    heading: "`%d` に `String` を渡してしまう",
    detail: "`String.format(\"%d\", \"100\")` のように、文字列を `%d` の位置に入れると、`IllegalFormatConversionException` という実行時例外が飛びます。整数なら `%d`、文字列なら `%s`、と型を必ず合わせましょう。逆に `%s` には `int` でも `double` でも何でも入れられる (自動で `toString()` される) ので、迷ったときの保険として `%s` は便利です",
  },
  {
    course: "java-intermediate",
    lesson: "java-string-format",
    lessonTitle: "String.format で書式化",
    heading: "`\\n` と `%n` を取り違える",
    detail: "改行を入れたいとき、`\\n` は環境にかかわらず常に LF (`0x0A`) になりますが、`%n` は OS ごとの改行 (Windows なら CRLF、Linux/macOS なら LF) に展開されます。ログや帳票を `Windows` でも `Linux` でも揃えたいときは `%n` を、`HTML` などプラットフォーム非依存で必ず LF にしたいときは `\\n` を、というふうに使い分けます",
  },
  {
    course: "java-intermediate",
    lesson: "java-string-format",
    lessonTitle: "String.format で書式化",
    heading: "桁あふれと意図しない丸め",
    detail: "`%.2f` の丸めは `HALF_UP`（いわゆる四捨五入）です。ただし `double`/`float` は `0.1` や `0.2` などを二進浮動小数点で正確に表現できないため、表示する前の値そのものに微小な誤差が乗ります。お金の計算など厳密な精度が必要なときは `BigDecimal` と `setScale(2, RoundingMode.HALF_UP)` を使うのが安全です。`String.format` の `%.2f` はあくまで「画面表示用の見た目を整える」ためのもの、と割り切りましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-stringbuilder",
    lessonTitle: "StringBuilder で連結",
    heading: "`toString()` の呼び忘れ",
    detail: "`String result = sb;` と書くとコンパイルエラーです。`String result = sb.toString();` のように明示的に呼び出してください。`println` のように `Object#toString` を内部で使ってくれるメソッドなら不要ですが、`String` 型の変数に入れるときや、API の引数として渡すときは必ず変換が必要です",
  },
  {
    course: "java-intermediate",
    lesson: "java-stringbuilder",
    lessonTitle: "StringBuilder で連結",
    heading: "ループ外で `+` 連結を 1 回だけする場合に `StringBuilder` を持ち出す",
    detail: "`String full = first + \" \" + last;` のように、定数回 (2〜3 回) の連結なら、コンパイラが自動で `StringBuilder` 相当のコードに変換してくれます。乱用するとかえって読みにくくなるので、`for` `while` などのループ内で繰り返し連結するときに使うのが本来の使いどころです",
  },
  {
    course: "java-intermediate",
    lesson: "java-stringbuilder",
    lessonTitle: "StringBuilder で連結",
    heading: "初期容量を指定せず巨大な文字列を組み立てる",
    detail: "`new StringBuilder()` のデフォルト容量は 16 文字です。1 万文字以上を作ると分かっているなら、`new StringBuilder(10_000)` のように指定しておくと内部配列の再確保が発生せず高速です。性能を気にする場面では覚えておきましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-regex",
    lessonTitle: "正規表現の基本",
    heading: "`\\\\` のエスケープ忘れ",
    detail: "`Java` の文字列で `\\d` `\\w` `\\s` を書きたいときは必ず `\\\\d` `\\\\w` `\\\\s` のようにバックスラッシュ 2 つです。1 つしか書かないとコンパイルエラー (`illegal escape character`) になります",
  },
  {
    course: "java-intermediate",
    lesson: "java-regex",
    lessonTitle: "正規表現の基本",
    heading: "`matches` を部分一致だと思い込む",
    detail: "`String.matches` は 完全一致 です。`\"foo bar\".matches(\"foo\")` は `false` を返します。部分的に含まれているかを調べたいなら `s.contains(\"foo\")` を使うのが手軽です",
  },
  {
    course: "java-intermediate",
    lesson: "java-regex",
    lessonTitle: "正規表現の基本",
    heading: "`greedy` と `lazy` の混同",
    detail: "`*` `+` `?` はデフォルトで `greedy` (できるだけ長くマッチ) です。最小マッチにしたいときは `*?` `+?` のように `?` を後ろに付けます。今回の課題のような単純な完全一致では気にしなくて構いません",
  },
  {
    course: "java-intermediate",
    lesson: "java-string-split-join",
    lessonTitle: "split と join",
    heading: "`split` の引数に正規表現特殊文字をそのまま渡す",
    detail: "`\".\"` `\"|\"` `\"?\"` などはエスケープしないと意図どおりに動きません。`split(\"\\\\.\")` `split(\"\\\\|\")` のようにバックスラッシュ 2 つでエスケープするか、`Pattern.quote` を使いましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-string-split-join",
    lessonTitle: "split と join",
    heading: "空文字列を `split` した結果を空配列だと思い込む",
    detail: "`\"\".split(\",\")` は要素 1 個の配列 `[\"\"]` を返します。空入力に対して空文字列を返したいときは、`csv.isEmpty()` で先に判定するのが安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-string-split-join",
    lessonTitle: "split と join",
    heading: "末尾の空要素が消える仕様を知らない",
    detail: "`\"a,b,,\".split(\",\")` は 2 要素しか返しません。CSV の列数を厳密に揃えたいときは `split(\",\", -1)` を使って末尾の空要素を保持してください",
  },
  {
    course: "java-intermediate",
    lesson: "java-localdate",
    lessonTitle: "LocalDate で日付を扱う",
    heading: "`java.util.Date` を使ってしまう",
    detail: "IDE の自動 `import` で `Date` を選ぶと、`java.util.Date` が候補に出ます。今からの新規コードでは必ず `java.time.LocalDate` の方を選びましょう。`import` 文を確認する癖をつけると安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-localdate",
    lessonTitle: "LocalDate で日付を扱う",
    heading: "月を `0` 始まりだと思い込む",
    detail: "`Calendar` 時代の名残で「`5 月` は `4`」と書いてしまう人がいます。`LocalDate.of(2026, 5, 19)` の `5` はそのまま `5 月` です。`0` を渡すと `DateTimeException` が出ます",
  },
  {
    course: "java-intermediate",
    lesson: "java-localdate",
    lessonTitle: "LocalDate で日付を扱う",
    heading: "戻り値を捨てる",
    detail: "`date.plusDays(1);` と書いても、戻り値を受け取らないと意味がありません。`LocalDate` は不変なので、必ず `date = date.plusDays(1);` のように再代入するか、別の変数に受けてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-localdatetime",
    lessonTitle: "LocalDateTime で時刻も扱う",
    heading: "`yyyy` と `YYYY` を取り違える",
    detail: "小文字 `yyyy` が普通の暦年 (`2026`)、大文字 `YYYY` は `week-based-year` という年またぎ週の特殊な年を表します。たとえば `2025-12-29` (月) のように年末が月曜から始まる週だと、`YYYY` は `2026` を返すことがあります。請求書や領収書を作るときに混入すると、「なぜか 1 件だけ翌年扱い」というバグになるので、`年` を出したいときは必ず小文字 `yyyy` を使います",
  },
  {
    course: "java-intermediate",
    lesson: "java-localdatetime",
    lessonTitle: "LocalDateTime で時刻も扱う",
    heading: "`MM` (月) と `mm` (分) を取り違える",
    detail: "`yyyy-mm-dd hh:MM` のように書いてしまうと、月のはずの位置に分が、分のはずの位置に月が入り、`2026-30-19 02:05` のような壊れた文字列ができあがります。日本語で書くと「月は大文字 `MM`、分は小文字 `mm`」、と語呂で覚えるのが安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-localdatetime",
    lessonTitle: "LocalDateTime で時刻も扱う",
    heading: "`SimpleDateFormat` を使ってしまう",
    detail: "旧 API の `java.text.SimpleDateFormat` は今でも `import` できますが、スレッドセーフでないため、`Servlet` や `Spring` のように複数リクエストが同時に走る環境で共有変数として持つと、たまに壊れた文字列を返すという最悪のバグになります。新規コードでは必ず `DateTimeFormatter` を使い、`SimpleDateFormat` は触らないのが鉄則です",
  },
  {
    course: "java-intermediate",
    lesson: "java-duration",
    lessonTitle: "Duration で時間差を計算",
    heading: "`Duration` に年や月の長さを聞かない",
    detail: "`Duration` は最大単位が「日」ですが、`toDays()` の戻り値も「秒数を `86400` で割った値」にすぎず、`Duration` 自体は「年 / 月」の概念を持っていません。`Duration.ofDays(60).toMonths()` のようなメソッドはなく、書こうとしてもコンパイルエラーになります。年・月単位の差を取りたいときは `Period` または `ChronoUnit.MONTHS.between` を使いましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-duration",
    lessonTitle: "Duration で時間差を計算",
    heading: "`Period` で秒や時間を扱おうとする",
    detail: "逆に、`Period.between(localTime1, localTime2)` は 型エラー になります。`Period` は `LocalDate` 専用で、`LocalTime` や `LocalDateTime` を渡せません。「ストップウォッチ的な短い時間」を測りたいときは必ず `Duration` を使います",
  },
  {
    course: "java-intermediate",
    lesson: "java-duration",
    lessonTitle: "Duration で時間差を計算",
    heading: "`between` の引数順を逆にして符号がおかしくなる",
    detail: "`ChronoUnit.DAYS.between(start, end)` は `end - start` を返します。`start` のほうが未来だと 負の値 になります。「絶対値が欲しい」のか「向きを保ちたいのか」を意識して書きましょう。`Math.abs(...)` で絶対値を取るのは簡単ですが、「過去か未来か」の情報を捨てる前に、本当にそれでいいかを一度立ち止まって考えるのが大事です",
  },
  {
    course: "java-intermediate",
    lesson: "java-date-format",
    lessonTitle: "日付の書式化とパース",
    heading: "`yyyy` と `YYYY` を混同する",
    detail: "小文字の `yyyy` は普通の暦年ですが、大文字の `YYYY` は `週ベースの年` を意味します。年末年始をまたぐ ISO 週で結果がズレるため、`Windows` のメモ帳で書いた仕様書をコピペしたら 1 月 1 日が `2027` 年扱いになる、というバグが起きやすいです。`日付` を出すときはほぼ常に小文字の `yyyy` が正解です",
  },
  {
    course: "java-intermediate",
    lesson: "java-date-format",
    lessonTitle: "日付の書式化とパース",
    heading: "`MM` と `mm` の取り違え",
    detail: "大文字の `MM` は月、小文字の `mm` は分。これを逆に書くと、`yyyy/mm/dd` というパターンが「年/分/日」と解釈されます。`LocalDate` には時刻情報がないため分は常に `0` となり、`2026/00/19` のような意図しない出力になります。コンパイルエラーにならない分、見つけにくい不具合です",
  },
  {
    course: "java-intermediate",
    lesson: "java-date-format",
    lessonTitle: "日付の書式化とパース",
    heading: "`HH` と `hh` の取り違え",
    detail: "`HH`/`hh` は `LocalDateTime` や `LocalTime` など時刻を持つ型で使うパターンです。`LocalDate` には時刻フィールドがないため、このミスが実害として現れるのは時刻を扱う型が対象のときです。24 時間制で表示したいのに `hh` と書くと 12 時間制 (`1`〜`12`) になります。午後 3 時が `03` と表示されてしまい、`AM/PM` の区別 (`a` パターン) を入れ忘れると `03:00` が午前か午後か分からなくなります。業務アプリでは原則 `HH` を使うと覚えてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-date-arithmetic",
    lessonTitle: "日付の加減算",
    heading: "戻り値を捨ててしまう",
    detail: "`today.plusDays(10);` と書いただけで、戻り値を変数に代入し忘れるのが一番多いミスです。`LocalDate` は不変なので、戻り値を捨てると計算結果はどこにも残りません。必ず `LocalDate later = today.plusDays(10);` のように受け取りましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-date-arithmetic",
    lessonTitle: "日付の加減算",
    heading: "月またぎを手で計算しようとする",
    detail: "`5/30` に `5` 日足したいから「日 = `30` + `5` = `35`、`5` 月は `31` 日までだから `4` を引いて `6/4`」のような計算は不要です。`plusDays(5)` に任せれば一発です。月またぎ・年またぎ・うるう年は API がすべて面倒を見てくれます",
  },
  {
    course: "java-intermediate",
    lesson: "java-date-arithmetic",
    lessonTitle: "日付の加減算",
    heading: "不変オブジェクトを誤解する",
    detail: "`Calendar` 時代の感覚で `today.plusDays(10)` を呼んだら `today` 自身が変わると思い込むパターンです。`String` の `replace` と同じく、`LocalDate` の `plus*` `minus*` `with*` はすべて新しいインスタンスを返します。元のオブジェクトは絶対に変わりません",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-files-write",
    lessonTitle: "Files でファイルに書き込む",
    heading: "`close()` を呼び忘れる / 古い書き方をする",
    detail: "`new FileWriter(...)` 系の古い API を使うと、`finally` で `close()` を書かないとリソースが残ります。`try-with-resources` (`try (var w = new FileWriter(...)) { ... }`) を必ず使うか、いっそ `Files.writeString` に置き換えるのが正解です",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-files-write",
    lessonTitle: "Files でファイルに書き込む",
    heading: "エンコーディングを指定し忘れる",
    detail: "古い `new FileWriter(\"a.txt\")` は OS のデフォルトエンコーディングで書き込むので、`Windows` (Shift_JIS) と `Linux` (UTF-8) で結果が変わることがあります。`Files.writeString` は常に `UTF-8` なので安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-files-write",
    lessonTitle: "Files でファイルに書き込む",
    heading: "`IOException` を握りつぶす",
    detail: "ファイル操作は失敗する可能性が常にあります (権限・容量・パスの存在)。`catch (IOException e) {}` のように空ブロックで握りつぶすと、トラブルが起きても原因が分からなくなるので、必ず `e.printStackTrace()` か `throw new RuntimeException(e)` で見える形にしましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-read-lines",
    lessonTitle: "ファイルを行単位で読む",
    heading: "巨大ファイルに `readAllLines` を使う",
    detail: "`readAllLines` は全行を一気にメモリへロードします。`1GB` のログを読もうとすると `OutOfMemoryError` で死にます。サイズ感が分からない場合は最初から `Files.lines` + `Stream` で書く癖を付けるのが安全です",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-read-lines",
    lessonTitle: "ファイルを行単位で読む",
    heading: "エンコーディングを指定せず化ける",
    detail: "Java 8 以降は引数なしで `UTF-8` ですが、ファイル自体が `Shift_JIS` で保存されていれば文字化けします。読み込みが化けたら、まずファイルのエンコーディングを疑い `Charset.forName(\"Shift_JIS\")` を渡してみましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-read-lines",
    lessonTitle: "ファイルを行単位で読む",
    heading: "空ファイルで `NullPointerException` を期待する",
    detail: "`Files.readAllLines` は空ファイルでも `null` ではなく 空の `List` を返します。`if (lines == null)` のような防衛は不要で、代わりに `if (lines.isEmpty())` で判定するのが正解です",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-path",
    lessonTitle: "Path でファイルパスを扱う",
    heading: "`String` の `+` でパスを連結する",
    detail: "`dir + \"/\" + name` のように書くと `Windows` では `/` と `\\` が混ざり、`name` 側に余計な `/` が含まれていると `//` ができます。必ず `dir.resolve(name)` を使いましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-path",
    lessonTitle: "Path でファイルパスを扱う",
    heading: "OS 依存の区切り文字を直書きする",
    detail: "`\"C:\\\\Users\\\\taro\"` のような書き方は `Linux` で動きません。マルチプラットフォームで動かしたいコードでは `Paths.get(\"C:\", \"Users\", \"taro\")` のように分割して渡すか、設定ファイルから読み込みます",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-path",
    lessonTitle: "Path でファイルパスを扱う",
    heading: "`resolve` と `+` を同じ感覚で使う",
    detail: "`resolve` に絶対パスを渡すと「左辺を捨てて絶対パスを返す」という挙動になります。これは仕様であり、`+` の単純連結とは違うので注意してください。期待しない上書きを避けたい場合は、事前に `child.isAbsolute()` を確認します",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-buffered",
    lessonTitle: "BufferedReader と Stream API",
    heading: "`Stream` を閉じ忘れる",
    detail: "`Files.lines(p).mapToInt(...).sum()` のようにワンライナーで書いてしまうと、`Stream` が `close` されずファイルディスクリプタがリークします。少量なら気づきにくいですが、ループの中で何回も呼ぶと「`Too many open files`」エラーで突然落ちます。必ず `try (Stream<String> lines = Files.lines(p)) { ... }` の形にしましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-buffered",
    lessonTitle: "BufferedReader と Stream API",
    heading: "空行のパースで `NumberFormatException`",
    detail: "`Integer.parseInt(\"\")` は実行時例外を投げます。改行コードの違いや末尾改行で空文字列が混ざることがあるので、`filter(s -> !s.isEmpty())` を挟むか、`s -> s.trim().isEmpty()` のように空白だけの行も弾く処理を入れると安心です",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-buffered",
    lessonTitle: "BufferedReader と Stream API",
    heading: "エンコーディングの指定を忘れる",
    detail: "`Files.lines(p)` は引数 1 つだと `UTF-8` を仮定します。日本語を含む `Shift_JIS` のファイルなどを読もうとすると `MalformedInputException` が出ます。明示的に指定したいときは `Files.lines(p, StandardCharsets.UTF_8)` のように 2 引数版を使い、入力が `Shift_JIS` なら `Charset.forName(\"MS932\")` を渡します",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-exists",
    lessonTitle: "ファイルの存在確認と削除",
    heading: "`exists` の戻り値を盲信する (`TOCTOU`)",
    detail: "`Files.exists(p)` で `true` を取った直後に別のプロセスがファイルを消すと、続く `Files.delete(p)` が `NoSuchFileException` を投げます。これは Time Of Check / Time Of Use の頭文字を取って `TOCTOU` 問題と呼ばれ、確実に避けたいケースでは `Files.deleteIfExists(p)` を使うか、例外を `try` で受け止めるのが定石です",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-exists",
    lessonTitle: "ファイルの存在確認と削除",
    heading: "`Files.delete` と `Files.deleteIfExists` を混同する",
    detail: "前者はファイルが無いと `NoSuchFileException`、後者は無くても静かに `false` を返します。「無いなら無いで OK」のときは後者、「絶対あるはず」のときは前者、と用途で使い分けましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-io-exists",
    lessonTitle: "ファイルの存在確認と削除",
    heading: "`Files.delete` でディレクトリを消そうとして失敗する",
    detail: "中身が残っているディレクトリは `DirectoryNotEmptyException` で消せません。再帰的に消したいときは `Files.walk(p)` で全ファイルを取り出してから逆順に `Files.delete` する、もしくは `Files.walkFileTree` を使うのが定石です",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-extends",
    lessonTitle: "extends で継承する",
    heading: "`extends` と `implements` の混同",
    detail: "クラスを継承するときは `extends`、`インタフェース` を実装するときは `implements` を使います。`class Dog implements Animal` のように書いてしまうと、`Animal` がクラスの場合はコンパイルエラーになります。逆に `interface Walker` を `extends` しようとしてもエラーです。「クラスから機能を引き継ぐなら `extends`、契約だけ約束するなら `implements`」と覚えましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-extends",
    lessonTitle: "extends で継承する",
    heading: "すべてのクラスが暗黙で `Object` を継承していることを忘れる",
    detail: "Java では `extends` を書かないクラスでも、自動的に `java.lang.Object` を継承しています。だから `toString()` や `equals()` や `hashCode()` がどんなクラスでも呼べるわけです。`Animal extends Object` と明示しても同じ意味ですが、わざわざ書かないのが普通です",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-extends",
    lessonTitle: "extends で継承する",
    heading: "`final` クラスを継承しようとする",
    detail: "`String` `Integer` `LocalDate` などの標準クラスや、自分で `final class` と宣言したクラスは継承できません。`class MyString extends String` と書くと `cannot inherit from final class String` というコンパイルエラーになります。継承させたくないクラスにはあえて `final` を付ける、というのも設計上のテクニックです",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-super",
    lessonTitle: "super で親を呼ぶ",
    heading: "`super(...)` を 2 行目以降に書いてしまう",
    detail: "`super(...)` と `this(...)` は コンストラクタの 1 行目 に限ります。前にログ出力やバリデーションを入れたくなりますが、それをするとコンパイルエラーです。どうしても前処理したいときは `static` メソッドに切り出して、`super(prepare(arg))` のように引数の中で呼びましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-super",
    lessonTitle: "super で親を呼ぶ",
    heading: "オーバーライドした子で `super` なしの自己呼び出しをしてしまう",
    detail: "`Child.greet()` の中で `greet()` と書くと、`this.greet()` 扱いで自分自身が呼ばれ、無限再帰で `StackOverflowError` になります。親を呼びたいなら必ず `super.greet()` と書くこと",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-super",
    lessonTitle: "super で親を呼ぶ",
    heading: "`final` クラス / メソッドを継承しようとする",
    detail: "`String` `Math` などの `final` クラスは `extends` できません。エラーメッセージ `cannot inherit from final` が出たら、設計を見直して「継承」ではなく「コンポジション (フィールドとして持つ)」に切り替える方が筋がよいケースが多いです",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-override",
    lessonTitle: "メソッドのオーバーライド",
    heading: "`@Override` を忘れてオーバーロードになる",
    detail: "─ 親が `area()` なのに子で `area(int x)` と書いてしまうと、別メソッドが追加されただけで親の `area()` はそのまま生きてしまいます。`Shape s = new Circle(10); s.area();` を呼んでも親の `0` が返ってきて「なんで動かないんだ?」と悩むパターンです。 `@Override` を付けておけばコンパイラが「親に一致するメソッドがない」と弾いてくれます",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-override",
    lessonTitle: "メソッドのオーバーライド",
    heading: "アクセス修飾子を絞ってしまう",
    detail: "─ 親の `public int area()` を子で `protected int area()` と書くと、コンパイルエラーになります。これは前述のルールどおりで、「子は親より広く、または同じ」可視性しか許されません。逆向き (`protected` → `public`) はオッケーです",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-override",
    lessonTitle: "メソッドのオーバーライド",
    heading: "`static` メソッドを「オーバーライド」しているつもり",
    detail: "─ 親と子で同名の `static` メソッドを書くと、文法上は通りますがこれは `オーバーライド` ではなく `隠蔽` (hiding) と呼ばれる別の現象です。`動的ディスパッチ` の対象外で、呼び先はコンパイル時の型で決まります。 `Shape.foo()` と `Circle.foo()` がそれぞれ独立に存在し、`Shape s = new Circle(); s.foo();` は親の `foo` を呼んでしまいます。 `static` メソッドは `オーバーライド` できないと覚えておきましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-abstract",
    lessonTitle: "abstract で抽象クラス",
    heading: "`abstract` メソッドに本体を書いてしまう",
    detail: "`public abstract double area() { return 0; }` のように `{ }` を付けるとコンパイルエラーです。エラーメッセージは `abstract methods cannot have a body` などになります。本体を書きたいなら `abstract` を外し、サブクラスで `@Override` するスタイルにします",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-abstract",
    lessonTitle: "abstract で抽象クラス",
    heading: "抽象クラスを `new` しようとする",
    detail: "`new Shape(\"hoge\")` はコンパイルエラーです。設計図を直接実体化しようとしているサインなので、`Triangle` や `Circle` のような具象サブクラスを `new` し、変数の型だけを `Shape` にする (`Shape s = new Triangle(4, 5);`) のが正解です",
  },
  {
    course: "java-intermediate",
    lesson: "java-inheritance-abstract",
    lessonTitle: "abstract で抽象クラス",
    heading: "`final` と `abstract` を同時に書く",
    detail: "`final` は「これ以上継承させない」、`abstract` は「サブクラスで上書きさせる」という真逆の意味を持つので、同じクラス/メソッドに付けるとコンパイルエラーになります。どちらか一方だけにします",
  },
  {
    course: "java-intermediate",
    lesson: "java-interface-basics",
    lessonTitle: "interface で契約を定義",
    heading: "`extends` と `implements` の取り違え",
    detail: "クラスがクラスを継承するときは `extends`、クラスが `interface` を実装するときは `implements` です。`class MyRunner extends Runnable2` と書くと「`Runnable2` クラスを継承しようとしている」と解釈され、`Runnable2` が `interface` の場合は `no interface expected here` 系のエラーになります",
  },
  {
    course: "java-intermediate",
    lesson: "java-interface-basics",
    lessonTitle: "interface で契約を定義",
    heading: "`interface` のフィールドを変数のつもりで書く",
    detail: "`interface` の中に `int count = 0;` と書くと、`Java` は黙ってそれを `public static final int count = 0;` と解釈します。インスタンスごとの状態は持てず、後から代入もできません。状態を持たせたいなら `interface` ではなく `abstract class` を選びましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-interface-basics",
    lessonTitle: "interface で契約を定義",
    heading: "実装メソッドを `public` 以外で書く",
    detail: "契約上のメソッドは暗黙的に `public abstract` 扱いなので、実装側を `private` や `package-private` (修飾子なし) にすると「アクセス修飾子を弱められない」エラーになります。`@Override public String name()` の形を体に染み込ませてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-interface-default",
    lessonTitle: "default メソッド",
    heading: "`default` だらけで `interface` が肥大化する",
    detail: "便利だからといって、振る舞いの 90% を `default` で書いてしまうと、もはや `interface` ではなく `abstract class` のような重い存在になります。`interface` は「実装すべきメソッドの契約」を表すのが基本なので、`default` は「後から追加する後方互換ヘルパー」「複数の実装が共通して使う簡単な定型処理」に絞るのがコツです",
  },
  {
    course: "java-intermediate",
    lesson: "java-interface-default",
    lessonTitle: "default メソッド",
    heading: "多重継承の衝突を放置する",
    detail: "2 つの `interface` が同じ `default` を持つと、コンパイルエラーが必ず出ます。`Greeter.super.greet(name)` 構文を覚えて、衝突したときは実装クラス側で明示的に `override` する習慣を付けましょう。エラーメッセージは `class X inherits unrelated defaults` のように出ます",
  },
  {
    course: "java-intermediate",
    lesson: "java-interface-default",
    lessonTitle: "default メソッド",
    heading: "`abstract class` と混同して `フィールド` を持たせようとする",
    detail: "`interface` には `public static final` の定数しか書けません。`private int count;` のようにフィールドを書こうとするとコンパイルエラーになります。状態を持ちたいなら `abstract class` を選ぶか、実装クラス側にフィールドを置いてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-instanceof-pattern",
    lessonTitle: "instanceof と型パターン",
    heading: "`instanceof` と `equals` を混同する",
    detail: "`instanceof` は「型が同じ (またはサブクラス) か」を見る演算子で、`equals` は「値が等しいか」を見るメソッドです。`\"hello\" instanceof String` は `true` ですが、`\"hello\".equals(\"hello\")` も `true`、意味はまったく違います。型を見たいときは `instanceof`、値を見たいときは `equals`、と覚えてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-instanceof-pattern",
    lessonTitle: "instanceof と型パターン",
    heading: "`null` に対する `instanceof` は常に `false`",
    detail: "`null instanceof String` も `(Object) null instanceof Object` も、結果はすべて `false` です。これは Java の仕様で、`null` には実型が存在しないためです。`null` を扱いたい場合は `if (o == null) { ... }` のように別途分岐するか、Java 21 なら `switch` の `case null` を使います",
  },
  {
    course: "java-intermediate",
    lesson: "java-instanceof-pattern",
    lessonTitle: "instanceof と型パターン",
    heading: "ダウンキャストの `ClassCastException`",
    detail: "`instanceof` で確認せずにいきなり `(String) o` のようにキャストすると、実型が違っていれば実行時に `ClassCastException` が飛びます。パターンマッチングを使えばこのミスは構造的に防げるので、Java 16 以降のコードベースでは積極的に切り替えましょう",
  },
  {
    course: "java-intermediate",
    lesson: "java-polymorphism",
    lessonTitle: "ポリモーフィズムを活かす",
    heading: "キャストが必要だと思い込む",
    detail: "`Shape s = new Circle(2);` のあと `s.area();` を呼ぶのに、`((Circle) s).area();` のように書く必要はありません。子型に固有のメソッドを呼びたいときだけ `ダウンキャスト` が必要です。共通メソッドを呼ぶだけなら親型のままで十分です",
  },
  {
    course: "java-intermediate",
    lesson: "java-polymorphism",
    lessonTitle: "ポリモーフィズムを活かす",
    heading: "`static` メソッドはポリモーフィズムの対象外",
    detail: "`static` メソッドはクラスに紐づくため、`@Override` ではなく `隠蔽` (hiding) されるだけです。`Shape s = new Circle(2); s.staticMethod();` と書いても、呼ばれるのは変数型 `Shape` の `static` メソッドです。インスタンスメソッドだけが動的ディスパッチの対象だと覚えてください",
  },
  {
    course: "java-intermediate",
    lesson: "java-polymorphism",
    lessonTitle: "ポリモーフィズムを活かす",
    heading: "`final` メソッドはオーバーライド禁止",
    detail: "親クラスのメソッドに `final` が付いていると、子クラスで `@Override` しようとした瞬間にコンパイルエラーになります。同様に `final class` 自体は継承できません。「これ以上拡張させたくない」という意思表示の機能なので、ライブラリ作者が意図的に付けることが多いです",
  },
  {
    course: "javascript-basics",
    lesson: "js-in-html",
    lessonTitle: "HTML に JavaScript を読み込む",
    heading: "パスを間違えている",
    detail: "`script.js` を `js` フォルダに入れたのに `src=\"script.js\"` のままだと読み込めません。開発者ツールの Network タブに 404 と出ます。`src=\"js/script.js\"` のようにフォルダ名から書きます",
  },
  {
    course: "javascript-basics",
    lesson: "js-in-html",
    lessonTitle: "HTML に JavaScript を読み込む",
    heading: "閉じタグを省略する",
    detail: "`<script src=\"script.js\" defer />` のような自己閉じは効きません。`script` タグは必ず `</script>` で閉じます",
  },
  {
    course: "javascript-basics",
    lesson: "js-in-html",
    lessonTitle: "HTML に JavaScript を読み込む",
    heading: "defer を付けずに head に置く",
    detail: "HTML がまだ組み上がっていない状態で走るため、第 8 章以降で `null` が返る原因になります。`head` に置くときは `defer` とセットにします",
  },
  {
    course: "javascript-basics",
    lesson: "js-hello-console",
    lessonTitle: "console.log で表示する",
    heading: "`console.log` で満足してしまう",
    detail: "表示は出るのにテストが赤いままになります。値は必ず `return` で返します",
  },
  {
    course: "javascript-basics",
    lesson: "js-hello-console",
    lessonTitle: "console.log で表示する",
    heading: "クォートの種類が揃っていない",
    detail: "開始と終了は同じ記号にします。バラバラだと文字列が閉じられず構文エラーになります",
  },
  {
    course: "javascript-basics",
    lesson: "js-hello-console",
    lessonTitle: "console.log で表示する",
    heading: "関数を呼ばずに名前だけ書く",
    detail: "`helloConsole` は関数そのもの、`helloConsole(\"山田 太郎\")` が呼び出しです。カッコを忘れると関数の中身が実行されません",
  },
  {
    course: "javascript-basics",
    lesson: "js-string-concat",
    lessonTitle: "文字列を連結する",
    heading: "空白や句読点を入れ忘れる",
    detail: "見た目では気づきにくく、テストだけが赤くなります。区切り文字は自分で足します",
  },
  {
    course: "javascript-basics",
    lesson: "js-string-concat",
    lessonTitle: "文字列を連結する",
    heading: "変数名をクォートで囲んでしまう",
    detail: "`\"name\"` と書くと、引数の中身ではなく `name` という 4 文字がそのまま出ます",
  },
  {
    course: "javascript-basics",
    lesson: "js-string-concat",
    lessonTitle: "文字列を連結する",
    heading: "数値の足し算のつもりが連結になる",
    detail: "文字列が 1 つでも混ざれば連結です。足したい部分はカッコでくくります",
  },
  {
    course: "javascript-basics",
    lesson: "js-arithmetic",
    lessonTitle: "四則演算",
    heading: "文字列のまま計算しようとする",
    detail: "入力欄から取った値は文字列です。`+` は連結になってしまいます",
  },
  {
    course: "javascript-basics",
    lesson: "js-arithmetic",
    lessonTitle: "四則演算",
    heading: "優先順位を勘違いする",
    detail: "「18 ヶ月を 10 倍したい」つもりで次のように書くと、`* 10` だけが先に計算されて 72 になります",
  },
  {
    course: "javascript-basics",
    lesson: "js-arithmetic",
    lessonTitle: "四則演算",
    heading: "カッコの閉じ忘れ",
    detail: "開いた数と閉じた数が合わないと構文エラーになります。エディタの色分けで対応を確かめる癖を付けます",
  },
  {
    course: "javascript-basics",
    lesson: "js-comments",
    lessonTitle: "コメントで意図を残す",
    heading: "ブロックコメントを閉じ忘れる",
    detail: "`*/` を書き忘れると、そこから先のコードが全部コメント扱いになります。関数まるごと消えるので、動かないのに理由が分からず悩みます",
  },
  {
    course: "javascript-basics",
    lesson: "js-comments",
    lessonTitle: "コメントで意図を残す",
    heading: "ブロックコメントを入れ子にする",
    detail: "`/* */` は入れ子にできません。内側の `*/` で終わってしまい、残りが構文エラーになります",
  },
  {
    course: "javascript-basics",
    lesson: "js-comments",
    lessonTitle: "コメントで意図を残す",
    heading: "文字列の中の `//` をコメントだと思う",
    detail: "文字列の内側はコメントになりません。URL を書いても切れずに残ります",
  },
  {
    course: "javascript-basics",
    lesson: "js-read-errors",
    lessonTitle: "エラーを読んでみる",
    heading: "エラーを読まずに書き直す",
    detail: "赤い文字を見た瞬間に閉じてしまい、勘で直そうとするパターンです。まず種類と行番号を声に出して読みます",
  },
  {
    course: "javascript-basics",
    lesson: "js-read-errors",
    lessonTitle: "エラーを読んでみる",
    heading: "行番号だけを見て前後を見ない",
    detail: "とくに `SyntaxError` は、原因が示された行より前にあります。カッコとクォートの対応を上へたどります",
  },
  {
    course: "javascript-basics",
    lesson: "js-read-errors",
    lessonTitle: "エラーを読んでみる",
    heading: "大文字と小文字を同じだと思う",
    detail: "`userName` と `username` は別の名前です。`ReferenceError` の半分はこれが原因です",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch1-build",
    lessonTitle: "つくる 起動メッセージ",
    heading: "計算のカッコを忘れる",
    detail: "学習期間が `122` のように数字の並びになります。計算部分はカッコでくくります",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch1-build",
    lessonTitle: "つくる 起動メッセージ",
    heading: "句読点や単位の前後の空白がずれる",
    detail: "テストは 1 文字も違わないことを求めます。`\" 件、学習は \"` のように、読点の前後まで期待値と見比べます",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch1-build",
    lessonTitle: "つくる 起動メッセージ",
    heading: "`return` を分けて 2 回書く",
    detail: "最初の `return` で関数が終わるため、2 行目は実行されません。1 本の式につないでから返します",
  },
  {
    course: "javascript-basics",
    lesson: "js-comparison",
    lessonTitle: "比較演算子と ===",
    heading: "`=` と `==` と `===` を取り違える",
    detail: "`=` は代入です。`if (published = true)` と書くと、比較のつもりが代入になり、いつでも成立する条件ができあがります。等しいかを調べたいときは記号を 3 つ書く、と体に覚えさせてください",
  },
  {
    course: "javascript-basics",
    lesson: "js-comparison",
    lessonTitle: "比較演算子と ===",
    heading: "入力値を変換せずに比べる",
    detail: "入力欄の値は文字列です。`year === 2026` は `false` になります。`Number(year) === 2026` のように、比べる前に型をそろえます",
  },
  {
    course: "javascript-basics",
    lesson: "js-comparison",
    lessonTitle: "比較演算子と ===",
    heading: "`NaN` を `===` で調べる",
    detail: "`x === NaN` は必ず `false` です。`Number.isNaN(x)` に置き換えてください",
  },
  {
    course: "javascript-basics",
    lesson: "js-logical",
    lessonTitle: "論理演算子と短絡評価",
    heading: "`&&` の戻り値を `boolean` だと思い込む",
    detail: "`views && views >= 100` は、`views` が `0` のとき `0` を返します。`false` ではありません。真偽値を返したいなら `Boolean()` で包みます",
  },
  {
    course: "javascript-basics",
    lesson: "js-logical",
    lessonTitle: "論理演算子と短絡評価",
    heading: "`a === 1 || 2` と書く",
    detail: "「a が 1 または 2」を表したつもりでも、`2` は truthy なので式全体が常に truthy になります。`a === 1 || a === 2` のように、比較を 2 回書いてください",
  },
  {
    course: "javascript-basics",
    lesson: "js-logical",
    lessonTitle: "論理演算子と短絡評価",
    heading: "短絡評価を忘れて右辺に処理を置く",
    detail: "`isReady && init()` は `isReady` が偽なら `init()` が呼ばれません。呼ばれない可能性を意識せずに書くと、初期化されないまま進むバグになります",
  },
  {
    course: "javascript-basics",
    lesson: "js-ternary",
    lessonTitle: "三項演算子で簡潔に",
    heading: "`:` を書き忘れる",
    detail: "三項演算子は `?` と `:` がセットです。偽のときの値を省略することはできません。「条件が真のときだけ何かしたい」なら三項演算子ではなく `if` 文の出番です",
  },
  {
    course: "javascript-basics",
    lesson: "js-ternary",
    lessonTitle: "三項演算子で簡潔に",
    heading: "戻り値ではなく処理を書いてしまう",
    detail: "三項演算子は値を選ぶための道具です。`c ? doA() : doB()` のように副作用のある関数を並べると、戻り値が捨てられて読みにくくなります。処理を分けたいときは `if` 文にします",
  },
  {
    course: "javascript-basics",
    lesson: "js-ternary",
    lessonTitle: "三項演算子で簡潔に",
    heading: "入れ子を深くしすぎる",
    detail: "3 段以上の入れ子はレビューでほぼ必ず指摘されます。書けることと読めることは別なので、段数が増えたら素直に `if / else if` に書き換えてください",
  },
  {
    course: "javascript-basics",
    lesson: "js-nullish-coalescing",
    lessonTitle: "?? で既定値を用意する",
    heading: "`0` を消してしまう",
    detail: "`count || 10` は、`count` が `0` のとき `10` になります。件数や金額のように `0` に意味がある値には `??` を使ってください",
  },
  {
    course: "javascript-basics",
    lesson: "js-nullish-coalescing",
    lessonTitle: "?? で既定値を用意する",
    heading: "`??` を `||` や `&&` と括弧なしで混ぜる",
    detail: "`a ?? b || c` は SyntaxError です。優先順位が紛らわしいため、言語仕様として括弧を必須にしています。`(a ?? b) || c` のように、どちらを先に評価するかを自分で明示してください",
  },
  {
    course: "javascript-basics",
    lesson: "js-nullish-coalescing",
    lessonTitle: "?? で既定値を用意する",
    heading: "既定値を毎回書き散らす",
    detail: "同じ既定値を関数のあちこちに `??` で書くと、あとで変えるときに漏れます。既定値は関数の入り口で一度だけ決めて、以降はその変数を使い回すのが読みやすい形です",
  },
  {
    course: "javascript-basics",
    lesson: "js-precedence",
    lessonTitle: "演算子の優先順位",
    heading: "`a === 1 || a === 2` を `a === 1 || 2` と書く",
    detail: "後者は `(a === 1) || 2` と解釈され、`2` は truthy なので式全体が常に truthy になります。比較は毎回きちんと書きます",
  },
  {
    course: "javascript-basics",
    lesson: "js-precedence",
    lessonTitle: "演算子の優先順位",
    heading: "`!` の範囲を勘違いする",
    detail: "`!published && views > 0` は `(!published) && (views > 0)` です。「公開中かつ閲覧数ありではない」と書きたいなら `!(published && views > 0)` と括弧が必要です",
  },
  {
    course: "javascript-basics",
    lesson: "js-precedence",
    lessonTitle: "演算子の優先順位",
    heading: "`??` を括弧なしで混ぜる",
    detail: "動かない構文エラーなので、実行前に必ず気づけます。落ち着いて括弧を足してください",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch3-build",
    lessonTitle: "つくる 表示名を決める",
    heading: "数値の既定値に `||` を使う",
    detail: "今回は既定値が `0` なので `worksCount || 0` でもたまたま同じ結果になります。しかし既定値が `0` 以外のとき、たとえば `views || 10` と書くと、実際に `0` 回だった閲覧数が `10` にすり替わります。数値には `??`、と決めておくと事故りません",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch3-build",
    lessonTitle: "つくる 表示名を決める",
    heading: "`??` と `||` を括弧なしで混ぜる",
    detail: "`nickname || fullName ?? \"名無し\"` は SyntaxError です。別々の `const` に分けるか、括弧を書いてください",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch3-build",
    lessonTitle: "つくる 表示名を決める",
    heading: "数値と文字列の連結を忘れる",
    detail: "`\"作品 \" + count + \" 件\"` の `+` は、左が文字列なので連結として働きます。順番を変えて `count + \" 件\"` と書くと数値が文字列に変換されて連結されますが、`count + 1 + \" 件\"` のように算術を混ぜるときは、意図した順に計算されているか確かめてください",
  },
  {
    course: "javascript-basics",
    lesson: "js-if-basic",
    lessonTitle: "if 文の基本",
    heading: "`=` を 1 つしか書かない",
    detail: "比較のつもりで代入してしまう典型例です。",
  },
  {
    course: "javascript-basics",
    lesson: "js-if-basic",
    lessonTitle: "if 文の基本",
    heading: "`else` のブロックで `return` を書き忘れる",
    detail: "どちらの道でも値を返すつもりが、片方だけ `undefined` になります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-if-basic",
    lessonTitle: "if 文の基本",
    heading: "条件の向きを逆に書く",
    detail: "「3 つ以上なら称号」を `count <= 3` と書いてしまうパターンです。書き終えたら、境目の値を 1 つ選んで頭の中で流してみましょう。`count` が 3 のとき、期待どおりの文字列が返るかを確かめれば、向きの間違いはその場で見つかります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-if-elseif",
    lessonTitle: "if / else if / else で段階判定",
    heading: "上限側の条件を二重に書く",
    detail: "`else if` は前の条件が `false` だったときにしか来ないので、上限を書き足す必要はありません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-if-elseif",
    lessonTitle: "if / else if / else で段階判定",
    heading: "`else if` を独立した `if` にしてしまう",
    detail: "`else` を付け忘れると、条件が別々に評価されて複数のブロックが動きます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-if-elseif",
    lessonTitle: "if / else if / else で段階判定",
    heading: "最後の `else` を書き忘れる",
    detail: "どの範囲にも当てはまらない値が来たときに `undefined` が返ります。経験年数が `0` の場合を想定していないコードは、実際にその値が来た瞬間に壊れます。最後は必ず `else` で受け止めてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-switch",
    lessonTitle: "switch 文で値を一覧から選ぶ",
    heading: "`break` の書き忘れ",
    detail: "前述のとおり下へ流れ落ちます。代入で組み立てる `switch` を書いたら、`case` の数と `break` の数が合っているか必ず数えてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-switch",
    lessonTitle: "switch 文で値を一覧から選ぶ",
    heading: "`case` に範囲を書こうとする",
    detail: "`case n >= 5:` のような書き方は期待どおりに動きません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-switch",
    lessonTitle: "switch 文で値を一覧から選ぶ",
    heading: "`default` を書かない",
    detail: "想定外の値が来たときに何も返らず `undefined` になります。`default` は最後に置くのが慣習ですが、書く位置は自由です。どこに置いても「どの `case` にも一致しなかったとき」に実行されます。とはいえ途中に書くと読み手が混乱するので、素直に最後へ置いてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-nested-conditions",
    lessonTitle: "条件を組み合わせる",
    heading: "`&&` と `||` を取り違える",
    detail: "「公開されていて、かつ web」は `&&` です。`||` にすると、非公開の web 作品まで注目作品になってしまいます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-nested-conditions",
    lessonTitle: "条件を組み合わせる",
    heading: "比較を省略して書く",
    detail: "数学の書き方をそのまま持ち込むと、意図しない結果になります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-nested-conditions",
    lessonTitle: "条件を組み合わせる",
    heading: "フラットにしたのに順番を直さない",
    detail: "入れ子を機械的に開いただけで、広い条件を先に書いてしまうパターンです。書き換えたら、必ず代表的な入力を 3 つほど手で流して、元のコードと同じ答えになるか確かめてください。今回なら公開かつ web、公開かつ design、非公開の 3 つで足ります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-fizzbuzz",
    lessonTitle: "FizzBuzz で複数条件を組み立てる",
    heading: "15 の判定を最後に書く",
    detail: "本文で見たとおり、`\"FizzBuzz\"` が永遠に出なくなります。書き終えたら必ず `15` を手で流して確かめてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-fizzbuzz",
    lessonTitle: "FizzBuzz で複数条件を組み立てる",
    heading: "`else` を付けずに `if` を並べる",
    detail: "`return` で返している場合は結果が変わりませんが、変数に代入して組み立てる書き方だと後ろの代入で上書きされます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-fizzbuzz",
    lessonTitle: "FizzBuzz で複数条件を組み立てる",
    heading: "数値をそのまま返す",
    detail: "倍数でないときに `return n;` と書くと、数値が返ります。今回は文字列を返す仕様なので `String(n)` に通してください。戻り値の型がそろっていないと、呼び出す側が毎回型を気にすることになります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch4-build",
    lessonTitle: "つくる 時間帯で挨拶を変える",
    heading: "範囲を数学の形で書く",
    detail: "前述のとおり、意図しない結果になります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch4-build",
    lessonTitle: "つくる 時間帯で挨拶を変える",
    heading: "境界値を重複させる",
    detail: "`hour <= 12` と `hour >= 12` を両方書くと、12 時がどちらの枝にも当てはまります。`else if` なら先に書いたほうが勝つので結果は出ますが、表と食い違ったまま気付けません。上限は `< 12`、下限は `>= 12` のように、片方だけが含む形にそろえてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch4-build",
    lessonTitle: "つくる 時間帯で挨拶を変える",
    heading: "`else` を書かずに終える",
    detail: "3 つ目を `else if (hour >= 18)` と書いてしまうと、0 時から 4 時がどの枝にも入らず `undefined` が返ります。すべての値がどれか 1 つの枝に必ず入ることを、`0` と `23` を手で流して確かめてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-array-basics",
    lessonTitle: "配列のきほん",
    heading: "インデックスを 1 から数えてしまう",
    detail: "1 番目の要素は `works[0]` です。`works[1]` は 2 番目です。ひとつずつずれるこの間違いは off-by-one エラーと呼ばれ、経験を積んだ人でもやります",
  },
  {
    course: "javascript-basics",
    lesson: "js-array-basics",
    lessonTitle: "配列のきほん",
    heading: "末尾を取るときに `length` をそのまま使う",
    detail: "`works[works.length]` は必ず存在しないインデックスになるので `undefined` です。`- 1` を忘れないでください",
  },
  {
    course: "javascript-basics",
    lesson: "js-array-basics",
    lessonTitle: "配列のきほん",
    heading: "`length` にカッコを付ける",
    detail: "`works.length()` は `TypeError` になります。`length` は関数ではなく、配列が持っている値そのものです",
  },
  {
    course: "javascript-basics",
    lesson: "js-push-pop",
    lessonTitle: "要素を足す・取り除く",
    heading: "`push` の戻り値を配列だと思って返す",
    detail: "戻り値は数値です。配列を返したいなら `push` したあとに配列そのものを返します",
  },
  {
    course: "javascript-basics",
    lesson: "js-push-pop",
    lessonTitle: "要素を足す・取り除く",
    heading: "`pop` と `shift` を取り違える",
    detail: "`pop` は末尾、`shift` は先頭です。名前から向きが読み取りにくいので、表を見返して確認する癖を付けてください",
  },
  {
    course: "javascript-basics",
    lesson: "js-push-pop",
    lessonTitle: "要素を足す・取り除く",
    heading: "カッコを忘れる",
    detail: "`works.pop` と書くと関数そのものが返るだけで、何も取り除かれません。メソッドは必ず `()` を付けて呼びます",
  },
  {
    course: "javascript-basics",
    lesson: "js-for-sum",
    lessonTitle: "for ループで合計を求める",
    heading: "区切りをカンマにする",
    detail: "3 つの式はセミコロン `;` で区切ります。カンマで書くと `SyntaxError` になります",
  },
  {
    course: "javascript-basics",
    lesson: "js-for-sum",
    lessonTitle: "for ループで合計を求める",
    heading: "条件で `<=` を使う",
    detail: "`i <= views.length` にすると最後に存在しないインデックスを触り、`total` が `NaN` になります。数値と `undefined` を足すと `NaN` です",
  },
  {
    course: "javascript-basics",
    lesson: "js-for-sum",
    lessonTitle: "for ループで合計を求める",
    heading: "更新を書き忘れて無限ループ",
    detail: "`i++` を忘れると `i` がずっと `0` のままで条件が真であり続け、ループが終わりません。ブラウザのタブが固まるので、書いたらまず更新式を確認してください",
  },
  {
    course: "javascript-basics",
    lesson: "js-for-of",
    lessonTitle: "for-of で配列を走査する",
    heading: "`of` を `in` と書いてしまう",
    detail: "`for-in` は要素ではなくキー、つまり `\"0\"` `\"1\"` という文字列を返します。数値ではなく文字列である点も含めて挙動が変わるので、配列には `of` を使います",
  },
  {
    course: "javascript-basics",
    lesson: "js-for-of",
    lessonTitle: "for-of で配列を走査する",
    heading: "ためる配列をループの中で宣言する",
    detail: "毎周作り直されるので、最後の 1 件しか残りません",
  },
  {
    course: "javascript-basics",
    lesson: "js-for-of",
    lessonTitle: "for-of で配列を走査する",
    heading: "インデックスが必要なのに `for-of` を選ぶ",
    detail: "何周目かを知る手段が無いので、自分でカウンタを足すことになります。そこまでするなら最初から通常の `for` のほうが素直です",
  },
  {
    course: "javascript-basics",
    lesson: "js-while-countdown",
    lessonTitle: "while で条件を満たす間くり返す",
    heading: "更新を書き忘れる",
    detail: "条件に使う変数がループの中で変わらないと止まりません",
  },
  {
    course: "javascript-basics",
    lesson: "js-while-countdown",
    lessonTitle: "while で条件を満たす間くり返す",
    heading: "条件の向きを逆にする",
    detail: "`while (total > 100)` と書くと、`total` が `0` から始まる場合に 1 度も実行されません。「くり返したい条件」を書くのであって「終わりたい条件」ではありません",
  },
  {
    course: "javascript-basics",
    lesson: "js-while-countdown",
    lessonTitle: "while で条件を満たす間くり返す",
    heading: "初期化をループの中に置く",
    detail: "`while` の前に置くべき初期化を本体に書くと、毎周リセットされて条件がいつまでも偽になりません",
  },
  {
    course: "javascript-basics",
    lesson: "js-break-continue",
    lessonTitle: "break と continue でループを制御する",
    heading: "`continue` の前に更新を書き忘れる",
    detail: "通常の `for` なら更新式は自動で実行されるので問題ありませんが、`while` の中で `continue` を使うと、更新行を飛ばしてしまい無限ループになります",
  },
  {
    course: "javascript-basics",
    lesson: "js-break-continue",
    lessonTitle: "break と continue でループを制御する",
    heading: "`break` のつもりで `continue` を書く",
    detail: "探しものが見つかったあとも最後までまわってしまいます。結果は合っていても無駄が残るので、意図に合うほうを選びます",
  },
  {
    course: "javascript-basics",
    lesson: "js-break-continue",
    lessonTitle: "break と continue でループを制御する",
    heading: "`if` を付けずに書く",
    detail: "条件なしで `break` を置くと必ず 1 周で終わり、`continue` を置くと本体が一度も実行されません。どちらも `if` とセットで使うものです",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch5-build",
    lessonTitle: "つくる 作品リストを一覧にする",
    heading: "末尾に余分な改行が残る",
    detail: "各行のうしろに `\\n` を足すと最後にも付きます。あいだに入れる書きかたにしましょう",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch5-build",
    lessonTitle: "つくる 作品リストを一覧にする",
    heading: "番号がずれる",
    detail: "`i` をそのまま使うと `0` から始まる一覧になります。表示用は `i + 1` です",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch5-build",
    lessonTitle: "つくる 作品リストを一覧にする",
    heading: "ためる変数をループの中で宣言する",
    detail: "`let result = \"\"` をループの本体に書くと毎周からっぽに戻り、最後の 1 行しか残りません。宣言はループの前です",
  },
  {
    course: "javascript-basics",
    lesson: "js-function-declaration",
    lessonTitle: "関数宣言",
    heading: "1. return を書き忘れる",
    detail: "表示されているので動いているように見えますが、値は返っていません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-function-declaration",
    lessonTitle: "関数宣言",
    heading: "2. 呼び出すときに丸括弧を忘れる",
    detail: "`greet` は関数そのもの、`greet(\"山田 太郎\")` は呼び出した結果です。この違いは後のコールバックのレッスンで重要になるので、今のうちに区別しておいてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-function-declaration",
    lessonTitle: "関数宣言",
    heading: "3. return の直後で改行してしまう",
    detail: "`JavaScript` は行末に自動でセミコロンを補うことがあります。`return` の直後で改行すると、そこで文が終わったと解釈されます。返したい値は `return` と同じ行に書き始めてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-arrow-function",
    lessonTitle: "アロー関数",
    heading: "1. 式本体なのに return を書く",
    detail: "`{ }` を書いたら `return` が要る、書かなければ `return` は要らない。この対応関係で覚えると混乱しません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-arrow-function",
    lessonTitle: "アロー関数",
    heading: "2. ブロック本体で return を忘れる",
    detail: "`{ }` を付けた瞬間、`return` は自分で書く責任が発生します。省略記法から書き直したときに起こりやすいミスです。",
  },
  {
    course: "javascript-basics",
    lesson: "js-arrow-function",
    lessonTitle: "アロー関数",
    heading: "3. セミコロンの位置を間違える",
    detail: "アロー関数は代入文の一部です。関数宣言の `}` の後ろにセミコロンは不要ですが、アロー関数では必要になります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-default-params",
    lessonTitle: "デフォルト引数",
    heading: "1. 既定値に頼れば null 対策も済むと思い込む",
    detail: "`API` から返ってきた値が `null` だったという場面は頻繁にあります。`null` も既定値に寄せたいなら、関数の中で `role ?? \"準備中\"` と書く必要があります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-default-params",
    lessonTitle: "デフォルト引数",
    heading: "2. 既定値の中で後ろの引数を参照する",
    detail: "既定値は左から順に決まります。`a` を決める時点で `b` はまだ存在しません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-default-params",
    lessonTitle: "デフォルト引数",
    heading: "3. 既定値を書いたのに呼び出し側で空文字列を渡している",
    detail: "入力欄が空のまま送信されると、`undefined` ではなく空文字列が渡ります。フォームの値を扱うときは、既定値だけに任せず、空文字列のチェックも自分で書きましょう。",
  },
  {
    course: "javascript-basics",
    lesson: "js-guard-clause",
    lessonTitle: "早期 return でネストを浅くする",
    heading: "1. return を忘れて条件だけ書く",
    detail: "文字列を書いただけでは何も起きません。`return` を付けて初めて関数が終わります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-guard-clause",
    lessonTitle: "早期 return でネストを浅くする",
    heading: "2. ガード節に本体の判定まで混ぜる",
    detail: "これは動きますが、どれが異常系でどれが正常系なのか読み手には分かりません。異常系のガードを冒頭に固め、正常系の分岐は本体側で `if / else if` として書き分けたほうが意図が伝わります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-guard-clause",
    lessonTitle: "早期 return でネストを浅くする",
    heading: "3. 条件の否定を書き間違える",
    detail: "ガード節では条件を否定形で書く場面が多く、`!` の付け忘れがそのまま逆の動作になります。書いたあとに 1 度、「この条件が `true` のとき何が起きるか」を声に出して確かめる癖を付けると防げます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-scope",
    lessonTitle: "変数のスコープ",
    heading: "1. 関数の中で宣言を忘れて外の変数を使ってしまう",
    detail: "`let` や `const` を書き忘れただけで、ローカル変数のつもりが外側の変数への代入になります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-scope",
    lessonTitle: "変数のスコープ",
    heading: "3. ループ変数を外から参照しようとする",
    detail: "ループが終わったあとに回数を使いたいなら、`for` の外で `let` を宣言してから使います。",
  },
  {
    course: "javascript-basics",
    lesson: "js-callback",
    lessonTitle: "コールバック関数",
    heading: "1. 渡すときに丸括弧を付ける",
    detail: "渡すのは関数、呼ぶのは受け取った側の仕事です。",
  },
  {
    course: "javascript-basics",
    lesson: "js-callback",
    lessonTitle: "コールバック関数",
    heading: "2. コールバックの中で return を書き忘れる",
    detail: "アロー関数で `{ }` を書いたら `return` が必要でした。コールバックでも同じルールです。",
  },
  {
    course: "javascript-basics",
    lesson: "js-callback",
    lessonTitle: "コールバック関数",
    heading: "3. 受け取った引数の名前と実際に渡ってくる値がずれている",
    detail: "エラーにはなりませんが、読む人が確実に混乱します。渡ってくる値の意味に合った名前を付けてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-foreach",
    lessonTitle: "forEach で配列をまわす",
    heading: "2. break で抜けようとする",
    detail: "中断が必要になった時点で `for-of` に書き換えます。無理に `forEach` のまま解決しようとしないでください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-foreach",
    lessonTitle: "forEach で配列をまわす",
    heading: "3. コールバックに丸括弧を付けて渡す",
    detail: "前のレッスンの `fn` と `fn()` の区別が、そのままここでも効いてきます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch6-build",
    lessonTitle: "つくる 処理を関数に整理する",
    heading: "1. 部品の中でも入力検査をしてしまう",
    detail: "検査は入口の 1 か所にまとめます。部品は正常な値が来る前提で書いてよい、という約束にしておくと全体が読みやすくなります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch6-build",
    lessonTitle: "つくる 処理を関数に整理する",
    heading: "2. 作業用の配列を関数の外に置く",
    detail: "スコープのレッスンで見たグローバル汚染そのものです。`lines` は `listWorks` の中で宣言します。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch6-build",
    lessonTitle: "つくる 処理を関数に整理する",
    heading: "3. 空配列のときの分岐を忘れる",
    detail: "`[].join(\"\\n\")` は空文字列です。エラーにはならないので気付きにくく、画面には不自然な余白だけが残ります。空のときに何を返すかは、仕様として先に決めておきましょう。",
  },
  {
    course: "javascript-basics",
    lesson: "js-object-basics",
    lessonTitle: "オブジェクトのきほん",
    heading: "キー名のタイポに気付かない",
    detail: "`undefined` が返ってきたら、まずキー名のつづりを疑ってください。エディタの補完を使うのが最大の予防策です。",
  },
  {
    course: "javascript-basics",
    lesson: "js-object-basics",
    lessonTitle: "オブジェクトのきほん",
    heading: "入れ子の途中が無いのに深く掘ろうとする",
    detail: "`work.links` が `undefined` なので、そこからさらに `.url` を読もうとした瞬間にエラーになります。この問題への対処法はこの章の後半で扱います。",
  },
  {
    course: "javascript-basics",
    lesson: "js-multi-return",
    lessonTitle: "オブジェクトで複数の値を返す",
    heading: "`return` の直後で改行してしまう",
    detail: "JavaScript は行末に自動でセミコロンを補うことがあり、`return` だけで文が終わったと解釈されます。`return {` は必ず同じ行に書いてください。",
  },
  {
    course: "javascript-basics",
    lesson: "js-multi-return",
    lessonTitle: "オブジェクトで複数の値を返す",
    heading: "キー名を呼び出し側と食い違わせる",
    detail: "キー名は関数の外向きの約束です。タイポしても `undefined` が返るだけでエラーにならないので、気付くのが遅れます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-multi-return",
    lessonTitle: "オブジェクトで複数の値を返す",
    heading: "0 件のときを考えずに割り算する",
    detail: "`NaN` はどんな比較でも `false` を返すので、表示される直前まで誰も気付きません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-immutable-const",
    lessonTitle: "const と参照",
    heading: "`const` なら安全だと思い込む",
    detail: "オブジェクトを関数に渡すことは、書き換えの権利を渡すことでもあります。渡した先で何をされるか分からない場合は、コピーを渡すのが安全です。",
  },
  {
    course: "javascript-basics",
    lesson: "js-immutable-const",
    lessonTitle: "const と参照",
    heading: "コピーしたつもりで同じ実体を触る",
    detail: "`=` はコピーではありません。本当にコピーしたいときの書き方は次のレッスンで扱います。",
  },
  {
    course: "javascript-basics",
    lesson: "js-optional-chaining",
    lessonTitle: "?. で安全に取り出す",
    heading: "`?.` を書く位置が 1 つ後ろ",
    detail: "守りたいのは「読む対象」ではなく「その手前」です。危ないところの直後に `?.` を置きます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-optional-chaining",
    lessonTitle: "?. で安全に取り出す",
    heading: "代入の左側で使おうとする",
    detail: "`?.` は読み取り専用の記号です。書き込みには使えません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-destructuring",
    lessonTitle: "分割代入",
    heading: "付け替えの向きを逆にする",
    detail: "左がキー、右が新しい変数名です。「元の名前 から 新しい名前」の順と覚えます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-destructuring",
    lessonTitle: "分割代入",
    heading: "オブジェクトなのに `[]` を使う",
    detail: "配列は `[]`、オブジェクトは `{}` です。オブジェクトは位置を持たないので `[]` では取り出せません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-destructuring",
    lessonTitle: "分割代入",
    heading: "文の先頭に `{` を置いて代入する",
    detail: "宣言と同時に書く `const { title } = work;` なら問題は起きません。後から代入したいときだけ、この落とし穴があります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-spread",
    lessonTitle: "スプレッド構文",
    heading: "浅いコピーで安心する",
    detail: "`copy.tags` は元と同じ配列です。中身を触るなら `{ ...work, tags: [...work.tags] }` のように階層ごとにコピーします。",
  },
  {
    course: "javascript-basics",
    lesson: "js-spread",
    lessonTitle: "スプレッド構文",
    heading: "オブジェクトを `[]` で展開しようとする",
    detail: "`[]` の中で展開できるのは配列や文字列などの反復可能なものだけです。オブジェクトを展開したいなら `{}` の中で行います。",
  },
  {
    course: "javascript-basics",
    lesson: "js-find-includes",
    lessonTitle: "find と includes で探す",
    heading: "`find` の結果をそのまま使う",
    detail: "見つからないと `undefined` なので、そこからプロパティを読もうとすると止まります。前のレッスンの `?.` が効きます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-find-includes",
    lessonTitle: "find と includes で探す",
    heading: "`findIndex` の 0 を falsy と混同する",
    detail: "先頭で見つかった `0` と、見つからなかった `-1` を `if` だけで区別することはできません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch7-build",
    lessonTitle: "つくる 作品データを構造化する",
    heading: "`length` を数えるのに自分でカウンタを増やす",
    detail: "間違いではありませんが、`works.length` で 1 行です。用意されているものは使いましょう。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch7-build",
    lessonTitle: "つくる 作品データを構造化する",
    heading: "最大値の初期値を 1 件目のデータから取らずに `0` 以外の中途半端な値にする",
    detail: "初期値は「必ず負ける値」にします。年を扱うなら `-Infinity` か `0` です。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch7-build",
    lessonTitle: "つくる 作品データを構造化する",
    heading: "欠けているプロパティを守らずに足す",
    detail: "`NaN` はどんな比較でも `false` を返すため、`if (totalViews > 0)` のような判定もすり抜けます。入り口で止めるのがいちばん確実です。",
  },
  {
    course: "javascript-basics",
    lesson: "js-dom-intro",
    lessonTitle: "DOM とは何か",
    heading: "HTML ファイルが書きかわると思ってしまう",
    detail: "JavaScript が変えるのは、いまブラウザの中にある DOM だけです。次のコードで肩書きを変えても、`index.html` の中身は 1 文字も変わりません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-dom-intro",
    lessonTitle: "DOM とは何か",
    heading: "`document` を自分でつくろうとする",
    detail: "`document` はブラウザが用意している変数です。次のように書くと、もともとあるものを上書きしてしまい、以降の DOM 操作が全部おかしくなります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-dom-intro",
    lessonTitle: "DOM とは何か",
    heading: "ソース表示と Elements タブを同じものだと思う",
    detail: "「ソースの表示」は、サーバーから届いた元の HTML です。「Elements」は、いまの DOM です。JavaScript で足したカードはソース表示には出てきません。動いているかどうかを確かめるときは、必ず Elements タブのほうを見ます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-queryselector",
    lessonTitle: "要素を取得する",
    heading: "セレクタの記号を忘れる",
    detail: "クラスなら `.`、id なら `#` が要ります。付け忘れるとタグ名として探しにいくので、当然見つかりません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-queryselector",
    lessonTitle: "要素を取得する",
    heading: "querySelectorAll の結果に直接プロパティを書く",
    detail: "NodeList は要素の入れ物であって、要素そのものではありません。まとめて設定はできません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-queryselector",
    lessonTitle: "要素を取得する",
    heading: "1 つしか無いのに querySelectorAll を使う",
    detail: "間違いではありませんが、`cards[0]` と書くところが増えて読みにくくなります。1 つに決まっているなら `querySelector`、複数を相手にするなら `querySelectorAll`、と使い分けます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-textcontent",
    lessonTitle: "テキストを書き換える",
    heading: "textContent を関数として呼ぶ",
    detail: "`textContent` はプロパティです。丸かっこは付けません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-textcontent",
    lessonTitle: "テキストを書き換える",
    heading: "代入したあとに子要素を掴もうとする",
    detail: "`textContent` への代入で中身は消えます。順番を間違えると、掴めるはずのものが `null` になります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-textcontent",
    lessonTitle: "テキストを書き換える",
    heading: "数値をそのまま入れて驚く",
    detail: "`textContent` に数値を入れると、自動で文字列に変わって表示されます。エラーにはなりませんが、`0` を入れたときに空欄に見えてしまうことがあります。単位や説明を足して `\"0 回表示\"` のように渡すと、意図が伝わる表示になります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-classlist",
    lessonTitle: "class を付け外しする",
    heading: "class 名に `.` を付けてしまう",
    detail: "CSS のセレクタと混ざりやすいところです。`classList` に渡すのは class の名前だけで、`.` は要りません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-classlist",
    lessonTitle: "class を付け外しする",
    heading: "スペース区切りで 2 つ渡す",
    detail: "`add` に `\"featured new\"` のような空白入りの文字列を渡すと `InvalidCharacterError` になります。2 つ足したいときは引数を 2 つに分けます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-classlist",
    lessonTitle: "class を付け外しする",
    heading: "CSS に無い class を付けて「効かない」と悩む",
    detail: "`classList.add` は、CSS にその指定があるかどうかを気にしません。存在しない class を付けても、エラーは出ず、見た目も変わりません。効かないときは、まず検証ツールで class が付いているかを見て、付いていたら CSS のほうに指定があるかを確かめます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-create-append",
    lessonTitle: "要素を作って追加する",
    heading: "append を忘れる",
    detail: "いちばん多い間違いです。エラーは出ません。何も起きないだけです。",
  },
  {
    course: "javascript-basics",
    lesson: "js-create-append",
    lessonTitle: "要素を作って追加する",
    heading: "タグ名にかっこや記号を付ける",
    detail: "`createElement` に渡すのはタグ名だけです。",
  },
  {
    course: "javascript-basics",
    lesson: "js-create-append",
    lessonTitle: "要素を作って追加する",
    heading: "class を付ける先を間違える",
    detail: "見た目が当たらないときは、class を付けた要素と、CSS が狙っている要素がずれていることがよくあります。`.work-card` の指定は `article` に付けないと効きません。中の `h3` に付けても何も起きません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-render-list",
    lessonTitle: "配列から一覧を描画する",
    heading: "関数の中で return を忘れる",
    detail: "`createWorkCard` の最後の `return card` を書き忘れると、関数は `undefined` を返します。`append(undefined)` は文字列の `\"undefined\"` を足してしまうので、カードのかわりにその文字だけが並びます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-render-list",
    lessonTitle: "配列から一覧を描画する",
    heading: "createElement を forEach の外に置く",
    detail: "要素は 1 件ごとに新しくつくらないといけません。外で 1 つだけつくって使いまわすと、同じ要素が親から親へ移動するだけで、最後の 1 枚しか残りません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-render-list",
    lessonTitle: "配列から一覧を描画する",
    heading: "`forEach` の中で `return` すれば全部止まると思う",
    detail: "止まりません。飛ばすだけです。1 件でも条件に合うものが見つかったら終わりにしたい、という処理は `forEach` には向いていません。第7章の `find` を使うか、`for-of` に書き換えます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch8-build",
    lessonTitle: "つくる ギャラリーをデータ駆動にする",
    heading: "空にせずに描いて、カードが倍になる",
    detail: "`workList.textContent = \"\"` が無いまま `renderWorks` を 2 回呼ぶと、10 枚並びます。描き直す関数の 1 行目を空にする、と覚えます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch8-build",
    lessonTitle: "つくる ギャラリーをデータ駆動にする",
    heading: "消したはずの要素を掴んだままにする",
    detail: "HTML から消した要素を掴んでいた古いコードが残っていると、`null` や `undefined` に対する操作でエラーになります。エラーが出た行より下は動かないので、「途中までしか描かれない」という症状になります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch8-build",
    lessonTitle: "つくる ギャラリーをデータ駆動にする",
    heading: "HTML に書いた見た目と、JS でつくる見た目がずれる",
    detail: "`class` の付け忘れがいちばん多い原因です。HTML では `<article class=\"work-card\">` なのに、JavaScript で `card.className` を書き忘れると、枠も余白も付かない裸の `article` が並びます。並べて見比べる段取りは、このずれを見つけるためのものです。",
  },
  {
    course: "javascript-basics",
    lesson: "js-addeventlistener",
    lessonTitle: "クリックに反応する",
    heading: "丸かっこを付けて渡してしまう",
    detail: "丸かっこを外して `showMessage` と書きます。渡すのは関数そのものです。",
  },
  {
    course: "javascript-basics",
    lesson: "js-addeventlistener",
    lessonTitle: "クリックに反応する",
    heading: "要素を取る前で綴りを間違えている",
    detail: "id の綴りが 1 文字違うと `helloButton` は `null` になり、`Cannot read properties of null` で止まります。エラーが指す行番号は `addEventListener` の行ですが、本当の原因は 1 行上にあります。",
  },
  {
    course: "javascript-basics",
    lesson: "js-addeventlistener",
    lessonTitle: "クリックに反応する",
    heading: "イベント名に on を付ける",
    detail: "`addEventListener` に渡す名前に `on` は付けません。`\"click\"` です。エラーは出ず、ただ何も起きないので気付きにくい間違いです。",
  },
  {
    course: "javascript-basics",
    lesson: "js-event-object",
    lessonTitle: "event オブジェクト",
    heading: "引数を書き忘れる",
    detail: "引数がないので `event` は未定義です。`event is not defined` で止まります。関数の丸かっこの中に `event` と書き足します。",
  },
  {
    course: "javascript-basics",
    lesson: "js-event-object",
    lessonTitle: "event オブジェクト",
    heading: "closest を挟まずに target を使う",
    detail: "説明文を押すと `event.target` は `p` で、その中に `h3` はありません。`null` に対して `.textContent` を読もうとして止まります。まず `closest` でカードまで戻します。",
  },
  {
    course: "javascript-basics",
    lesson: "js-event-object",
    lessonTitle: "event オブジェクト",
    heading: "カードの外を押したときを考えていない",
    detail: "カードとカードのすき間を押すと `card` は `null` です。`if (!card) return;` の 1 行を入れるだけで防げます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-input-value",
    lessonTitle: "入力欄の値を取る",
    heading: "textContent で読もうとする",
    detail: "入力欄の中身は `textContent` には入りません。何を打っても空のままです。`value` を使います。",
  },
  {
    course: "javascript-basics",
    lesson: "js-input-value",
    lessonTitle: "入力欄の値を取る",
    heading: "文字列のまま計算する",
    detail: "`\"5\" + 100` は `\"5100\"` です。`Number()` を通してから足します。",
  },
  {
    course: "javascript-basics",
    lesson: "js-input-value",
    lessonTitle: "入力欄の値を取る",
    heading: "登録する相手を間違える",
    detail: "`document` に登録すると、名前欄やメール欄を打ったときにも動いてしまいます。今回は `#message` に直接登録します。",
  },
  {
    course: "javascript-basics",
    lesson: "js-submit-preventdefault",
    lessonTitle: "フォーム送信を制御する",
    heading: "ボタンに登録してしまう",
    detail: "`button` では `submit` は起きません。登録する相手は `form` です。",
  },
  {
    course: "javascript-basics",
    lesson: "js-submit-preventdefault",
    lessonTitle: "フォーム送信を制御する",
    heading: "引数を受け取っていない",
    detail: "`event is not defined` で止まり、止められないまま送信されます。関数の丸かっこに `event` と書き足します。",
  },
  {
    course: "javascript-basics",
    lesson: "js-submit-preventdefault",
    lessonTitle: "フォーム送信を制御する",
    heading: "丸かっこを付け忘れる",
    detail: "これは関数を参照しただけで、呼んでいません。エラーも出ないので気付きにくく、ページだけが再読み込みされます。`event.preventDefault()` と書きます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch9-build1",
    lessonTitle: "つくる ダークモード切替",
    heading: "add だけ書いている",
    detail: "1 回目は暗くなりますが、2 回目以降は何も起きません。`add` はすでに付いていれば黙って何もしないからです。`toggle` に置き換えます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch9-build1",
    lessonTitle: "つくる ダークモード切替",
    heading: "class 名にドットを付けてしまう",
    detail: "`classList` に渡すのは class 名そのものです。ドットが要るのは `querySelector` のセレクタのときだけです。`.dark` という名前の class が付いてしまい、CSS には当たりません。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch9-build1",
    lessonTitle: "つくる ダークモード切替",
    heading: "className を上書きしてしまう",
    detail: "これは class 全体を `dark` だけで置き換える書き方です。元から付いていた class があれば消えてしまいます。付け外しには `classList` を使います。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch9-build2",
    lessonTitle: "つくる フォームの入力チェック",
    heading: "return を書き忘れる",
    detail: "エラー文と受け付けた文が同時に出ます。`if` の中の最後に `return;` を置きます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch9-build2",
    lessonTitle: "つくる フォームの入力チェック",
    heading: "エラーを消す処理がない",
    detail: "一度出たエラー文が残ったままになります。正常だったときに `formError.textContent = \"\"` を書きます。",
  },
  {
    course: "javascript-basics",
    lesson: "js-ch9-build2",
    lessonTitle: "つくる フォームの入力チェック",
    heading: "条件を && でつないでしまう",
    detail: "これは「両方とも空のとき」です。片方だけ空のときは素通りしてしまいます。どちらか一方でも空なら止めたいので `||` を使います。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-course-intro",
    lessonTitle: "このコースで育てるもの",
    heading: "入門のファイルを直接書き換えてしまう",
    detail: "途中で分からなくなったときに戻る先が無くなります。`script.js` はそのまま残し、コピーを `app.js` として作ってください",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-course-intro",
    lessonTitle: "このコースで育てるもの",
    heading: "データと表示を同じ関数に混ぜる",
    detail: "下のように配列の宣言と描画が同じ関数に入っていると、データを外から取ってくる形に変えるときに関数ごと書き直しになります。次のレッスンからの主題がここです",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-course-intro",
    lessonTitle: "このコースで育てるもの",
    heading: "先に第8章から手を付ける",
    detail: "非同期処理はこのコースの目玉ですが、`try` と `catch` を知らないまま `await` のエラー処理に入ると、例外の話をしているのか非同期の話をしているのか切り分けられなくなります。第6章を先に置いてあるのはそのためです",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-strict-mode",
    lessonTitle: "strict mode が先に教えてくれる間違い",
    heading: "先頭以外に書いている",
    detail: "下の書きかたは、ただの文字列を評価しただけで終わります。`\"use strict\";` の前に文を置けるのはコメントだけです",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-strict-mode",
    lessonTitle: "strict mode が先に教えてくれる間違い",
    heading: "クォートを付け忘れる",
    detail: "`use strict;` と書くと、`use` という変数を参照する式になります。strict mode ならこれ自体が `ReferenceError` になりますが、まだ有効になっていないので何も起きません",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-strict-mode",
    lessonTitle: "strict mode が先に教えてくれる間違い",
    heading: "エラーが増えたので外してしまう",
    detail: "strict mode にした直後は、それまで隠れていた不具合が一気に表に出ます。増えたのはエラーであって不具合ではありません。1 つずつ潰すほうが、黙って間違った値が表示され続ける状態よりも短時間で終わります",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-data-model",
    lessonTitle: "表示とデータを切り分ける",
    heading: "変換関数の中で DOM をさわる",
    detail: "1 行でも `document` が出てきたら、それはもう変換関数ではありません。戻り値だけで確かめられなくなります",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-data-model",
    lessonTitle: "表示とデータを切り分ける",
    heading: "未設定の項目をそのまま使う",
    detail: "`tags` を持たない作品が 1 件でもあると、そこで処理全体が止まります。第9章で API から取ってきたデータには、項目が欠けている行が混ざります",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-data-model",
    lessonTitle: "表示とデータを切り分ける",
    heading: "元のデータを書き換えてしまう",
    detail: "変換のついでに `work` 自体へ項目を足すと、元の配列が汚れます。新しいオブジェクトを作って返してください",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-destructuring",
    lessonTitle: "分割代入で必要な値だけ取り出す",
    heading: "入れ子を開いた親の変数を使ってしまう",
    detail: "`stats: { views }` は「開く指示」であって変数宣言ではありません。親も使うなら `{ stats, stats: { views } }` と両方書きます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-destructuring",
    lessonTitle: "分割代入で必要な値だけ取り出す",
    heading: "付け替えと既定値の順番を逆に書く",
    detail: "キー、変数名、既定値の順です。既定値は必ずいちばん後ろに置きます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-destructuring",
    lessonTitle: "分割代入で必要な値だけ取り出す",
    heading: "受け皿を 1 か所しか置かない",
    detail: "引数全体には `= {}` があるのに、`stats` には付いていません。引数は渡されているので外側の既定値は使われず、`undefined` の `stats` を開いて落ちます。開く階層それぞれに受け皿が要ります。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-spread-rest",
    lessonTitle: "スプレッドとレストで組み替える",
    heading: "レスト引数を前に置く",
    detail: "まとめる対象が「残り」である以上、後ろに何かを置くことはできません。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-spread-rest",
    lessonTitle: "スプレッドとレストで組み替える",
    heading: "浅いコピーのまま中身を書き換える",
    detail: "`merged.tags` は `defaults.tags` と同じ配列を指しています。`tags: [...merged.tags, \"HTML\"]` のように、新しい配列を作って差し替えてください。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-object-entries",
    lessonTitle: "keys と values と entries",
    heading: "ドットで呼ぼうとする",
    detail: "`keys` はオブジェクトのメソッドではなく `Object` に生えた関数です。配列の `arr.map()` とは呼びかたが違います。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-object-entries",
    lessonTitle: "keys と values と entries",
    heading: "map の中でオブジェクトを返してしまう",
    detail: "`fromEntries` が受け取れるのは `[キー, 値]` の 2 要素配列の並びです。`[title, count]` の形で返してください。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-object-entries",
    lessonTitle: "keys と values と entries",
    heading: "値だけ取ってキーを失う",
    detail: "キーが必要になる可能性があるなら、最初から `entries` で開きます。`values` は合計や最大値のように、キーを完全に捨ててよいときだけ使います。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-bit-flags",
    lessonTitle: "ビット演算でフラグをまとめる",
    heading: "`+` で立てる",
    detail: "`3 + 2` は `5` になり、注目が降りて新着が立ったことになります。`3 | 2` なら `3` のままです。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-bit-flags",
    lessonTitle: "ビット演算でフラグをまとめる",
    heading: "降ろすつもりで `^` を使う",
    detail: "`^` は切り替えです。今の状態に関係なく降ろしたいなら `& ~` を使います。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-bit-flags",
    lessonTitle: "ビット演算でフラグをまとめる",
    heading: "`&&` と `&` を書き間違える",
    detail: "1 文字違いで、常に真になる条件ができあがります。フラグを扱っているファイルでは特に見落としやすいので、書いたあとに読み返してください。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-class-instance",
    lessonTitle: "クラスとインスタンス",
    heading: "メソッドの間にカンマを書く",
    detail: "オブジェクトリテラルの感覚でカンマを打つと構文エラーになります。クラスの本体はカンマで区切りません。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-class-instance",
    lessonTitle: "クラスとインスタンス",
    heading: "メソッドの中で `this` を書き忘れる",
    detail: "`title` とだけ書くと、インスタンスのプロパティではなく変数を探しに行きます。見つからなければ `ReferenceError` です。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-class-instance",
    lessonTitle: "クラスとインスタンス",
    heading: "`constructor` で値を返そうとする",
    detail: "`return { title }` のようにオブジェクトを返すと、`new` の結果がそのオブジェクトに差し替わり、メソッドを持たない素のオブジェクトができます。`constructor` では `this` に代入するだけにします。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-encapsulation",
    lessonTitle: "private フィールドとアクセサ",
    heading: "`#` フィールドを宣言せずに使う",
    detail: "`constructor` の中でいきなり `this.#views = 0` と書いても、クラスの直下に宣言が無ければ動きません。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-encapsulation",
    lessonTitle: "private フィールドとアクセサ",
    heading: "`set` の中で同じ名前に代入する",
    detail: "`set views(value)` の中で `this.views = value` と書くと、また `set views` が呼ばれます。それが延々と続いてスタックがあふれます。代入先は必ず `#views` のほうです。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-encapsulation",
    lessonTitle: "private フィールドとアクセサ",
    heading: "`get` にかっこを付けて呼ぶ",
    detail: "`work.views()` と書くと、返ってきた数値を関数として呼ぼうとして `TypeError` になります。アクセサはかっこなしで使います。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-inheritance",
    lessonTitle: "継承と super",
    heading: "`super()` を呼ばない",
    detail: "`extends` を書いたら、子の `constructor` では必ず `super()` を呼びます。呼ばないまま `constructor` が終わっても `ReferenceError` です。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-inheritance",
    lessonTitle: "継承と super",
    heading: "親に渡す引数を忘れる",
    detail: "`super()` を引数なしで呼ぶと、親の `constructor` の引数が全部 `undefined` になります。エラーにはならないので、`undefined (undefined)` という文字が画面に出てから気づくことになります。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-inheritance",
    lessonTitle: "継承と super",
    heading: "`super.describe()` を `this.describe()` と書く",
    detail: "上書きしたメソッドの中で `this.describe()` を呼ぶと、上書きしたほう自身がもう一度呼ばれます。止まらなくなって `RangeError` になります。親を呼ぶときは `super.` です。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-polymorphism-static",
    lessonTitle: "多態と static",
    heading: "インスタンスから `static` を呼ぶ",
    detail: "`work.from(record)` と書くと `TypeError` です。`static` はクラスに付いているので `Work.from(record)` と書きます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-polymorphism-static",
    lessonTitle: "多態と static",
    heading: "`static` のメソッドの中で `this` をインスタンスのつもりで使う",
    detail: "`static` の `this` はクラス自身です。`this.title` と書いても `undefined` にしかなりません。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-polymorphism-static",
    lessonTitle: "多態と static",
    heading: "上書きしたつもりでメソッド名を間違える",
    detail: "`format` を `fomat` と書いても、その場ではエラーになりません。子のインスタンスで `format()` を呼ぶと親の `format` が静かに動き、「人気」が付かない行が混ざります。上書きが効いていないときは、まず名前を見比べます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-ch5-build",
    lessonTitle: "つくる 作品コレクションをクラスにする",
    heading: "`#works` を返してしまう",
    detail: "`get works() { return this.#works; }` と書くと、隠した意味がありません。返した配列は同じ入れ物なので、外から `push` も `splice` も通ります。渡すなら `[...this.#works]` のようにコピーを返します。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-ch5-build",
    lessonTitle: "つくる 作品コレクションをクラスにする",
    heading: "`totalViews` を全件で計算する",
    detail: "`this.#works.reduce(...)` と書くと、非公開の作品の表示回数まで合計に入ります。合計と件数で、数える対象を揃えます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-ch5-build",
    lessonTitle: "つくる 作品コレクションをクラスにする",
    heading: "`limit` に何も渡さない",
    detail: "`topTitles()` のように引数なしで呼ぶと `slice(0, undefined)` になり、全件が返ります。0 件のつもりで呼ぶと全部出てくるので、呼ぶ側で件数を決めて必ず渡します。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-try-catch",
    lessonTitle: "try catch finally",
    heading: "失敗する行が try の外にある",
    detail: "失敗しうるのは `JSON.parse` のほうなので、囲む場所がずれている。try には失敗しうる行を入れる。ただし囲みすぎもよくない。関係のない処理まで入れると、どこで失敗したのか catch から分からなくなる。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-try-catch",
    lessonTitle: "try catch finally",
    heading: "catch を空にする",
    detail: "これで画面は止まらなくなるが、なぜ何も出ないのかが誰にも分からなくなる。この章の最後の回で、受け取った失敗を利用者に見える形で出す。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-try-catch",
    lessonTitle: "try catch finally",
    heading: "message の文字列で分岐する",
    detail: "メッセージは環境で変わるので、この if はいつか静かに通らなくなる。分けたいときは `error.name` を見るか、次のレッスンで作る自前のエラークラスを使う。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-throw-custom-error",
    lessonTitle: "自分でエラーを投げる",
    heading: "this.name を書き忘れる",
    detail: "これは \"ValidationError\" ではなく \"Error\" と出る。`name` は継承元の値がそのまま残るので、自分で入れる。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-throw-custom-error",
    lessonTitle: "自分でエラーを投げる",
    heading: "super を呼ばずにプロパティを触る",
    detail: "これは実行した時点で ReferenceError になる。継承したクラスでは `super()` を呼ぶまで `this` を触れない。`super` を必ず先頭に置く。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-throw-custom-error",
    lessonTitle: "自分でエラーを投げる",
    heading: "検証の途中で throw せず、最後まで進める",
    detail: "console.log は処理を止めないので、その下の行がそのまま動く。止めたいなら throw を書く。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-error-boundary",
    lessonTitle: "失敗を画面に出す",
    heading: "成功したときに前の表示を消していない",
    detail: "これは成功したときに `is-error` を外していない。一度失敗すると、そのあと何回成功しても赤い枠が残る。try に入る前に `clearError()` を呼ぶ。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-error-boundary",
    lessonTitle: "失敗を画面に出す",
    heading: "エラーの文を alert で出す",
    detail: "alert は押すまで消えず、押したら跡が残らない。利用者は文言を読み返せず、画面のどこが失敗したのかも分からない。失敗した場所の近くに、消えない形で出す。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-error-boundary",
    lessonTitle: "失敗を画面に出す",
    heading: "表示を隠すのに要素を消してしまう",
    detail: "これで見えなくはなるが、次に失敗したときに出す先が無くなる。隠すのと消すのは違う。付け外しするのは class だけにする。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-basics",
    lessonTitle: "正規表現のきほん",
    heading: "match の `null` を忘れる",
    detail: "当たらなかったときに `null` が返ることを忘れると、その場では動いて後で落ちます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-basics",
    lessonTitle: "正規表現のきほん",
    heading: "`.` をそのまま書く",
    detail: "正規表現の `.` は「どれか 1 文字」です。ピリオドそのものを探したいときはバックスラッシュを付けます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-basics",
    lessonTitle: "正規表現のきほん",
    heading: "`{4}` を「4 桁ちょうど」だと思い込む",
    detail: "`/\\d{4}/` は「4 個並んだ箇所がある」という意味であって、「全体が 4 桁」ではありません。`\"12345\"` にも当たります。全体を縛りたいときは、次のレッスンで扱う `^` と `$` が要ります。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-validate",
    lessonTitle: "入力を検証する",
    heading: "`g` フラグを付けた正規表現を `test` で使い回す",
    detail: "これは知らないと必ず一度は踏みます。`g` フラグ付きの正規表現オブジェクトは `lastIndex` という「次にどこから探すか」を内部に持っていて、`test` が当たるたびにそれを進めます。関数の外に置いて使い回すと、2 回目の呼び出しが前回の続きから探し始めて `false` になります。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-validate",
    lessonTitle: "入力を検証する",
    heading: "`^` と `$` を付け忘れる",
    detail: "付け忘れると、途中に混ざっていれば通る判定になります。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-validate",
    lessonTitle: "入力を検証する",
    heading: "空文字列を弾けていると思い込む",
    detail: "`+` は 1 個以上なので空文字列は弾けますが、`*` は 0 個以上なので通します。空の入力を弾くつもりで `*` を書くと素通りします。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-replace",
    lessonTitle: "置換とキャプチャ",
    heading: "戻り値を受け取らない",
    detail: "`replace` は元の文字列を書き換えません。呼びっぱなしにすると何も変わりません。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-replace",
    lessonTitle: "置換とキャプチャ",
    heading: "`g` を付け忘れて 1 件だけ置き換わる",
    detail: "テストケースが 1 件しか日付を含まないと気付けません。複数入った文字列で必ず確かめます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-regexp-replace",
    lessonTitle: "置換とキャプチャ",
    heading: "置換関数の第 1 引数をキャプチャだと思う",
    detail: "第 1 引数は当たった全体で、キャプチャは第 2 引数からです。ここを 1 つずらすと、年のつもりで `2026-04-01` 全体を受け取ってしまいます。",
  },
  {
    course: "javascript-practical",
    lesson: "jsp-http-rest",
    lessonTitle: "HTTP と REST の読みかた",
    heading: "404 が返っているのに自分のコードを疑い続ける",
    detail: "404 は「その場所には何も無い」という返事です。コードの書きかたではなく、URL の綴りか、指定した id の値を疑ってください。`works/9.json` のように存在しない id を書いていないか確認するのが先です。",
  },
  {
    course: "python-basics",
    lesson: "py-print-basics",
    lessonTitle: "printで表示",
    heading: "`return` を書き忘れる",
    detail: "`print(\"=== おこづかい帳 ===\")` とだけ書くと、画面には出ますが関数の戻り値は `None` になり、テストは失敗します。必ず `return` で返します。",
  },
  {
    course: "python-basics",
    lesson: "py-print-basics",
    lessonTitle: "printで表示",
    heading: "クオートの閉じ忘れ",
    detail: "`return \"=== おこづかい帳 ==` のように閉じるクオートがないと `SyntaxError` になります。開いたら必ず閉じます。",
  },
  {
    course: "python-basics",
    lesson: "py-print-basics",
    lessonTitle: "printで表示",
    heading: "全角の記号を使ってしまう",
    detail: "`print（）` のように全角のかっこを書くとエラーになります。記号はすべて半角で書きます。日本語を打つ設定のまま記号を入力すると起こりがちです。",
  },
  {
    course: "python-basics",
    lesson: "py-string-concat",
    lessonTitle: "文字列の連結",
    heading: "空白を入れ忘れる",
    detail: "`name + price_text` と書くと `りんご120` とくっつきます。`+ \" \" +` を挟むのを忘れないでください。半角スペースと全角スペースは別の文字なので、必ず半角にします。",
  },
  {
    course: "python-basics",
    lesson: "py-string-concat",
    lessonTitle: "文字列の連結",
    heading: "引数を文字列のクオートで囲んでしまう",
    detail: "`return \"name\" + \" \" + \"price_text\"` と書くと、引数の中身ではなく `name price_text` という文字がそのまま返ります。引数を使うときはクオートで囲みません。",
  },
  {
    course: "python-basics",
    lesson: "py-string-concat",
    lessonTitle: "文字列の連結",
    heading: "`円` を付け忘れる",
    detail: "最後の `+ \"円\"` が抜けると `りんご 120` になります。テストは 1 文字違うだけで落ちるので、末尾まで見直します。",
  },
  {
    course: "python-basics",
    lesson: "py-arithmetic",
    lessonTitle: "四則演算",
    heading: "結果をクオートで囲んでしまう",
    detail: "`return \"budget - price1 - price2\"` と書くと、計算されずにその文字がそのまま返ります。計算式はクオートで囲みません。",
  },
  {
    course: "python-basics",
    lesson: "py-arithmetic",
    lessonTitle: "四則演算",
    heading: "掛け算に `x` を使う",
    detail: "`budget x 2` は `Python` では文法エラーです。掛け算はアスタリスク `*` と決まっています。",
  },
  {
    course: "python-basics",
    lesson: "py-arithmetic",
    lessonTitle: "四則演算",
    heading: "引く順番をかっこで間違える",
    detail: "`budget - (price1 - price2)` と書くと `3000 - 40` で `2960` になってしまいます。今回はかっこは不要です。",
  },
  {
    course: "python-basics",
    lesson: "py-execution-order",
    lessonTitle: "実行順序",
    heading: "末尾に余分な `\\n` を付ける",
    detail: "`+ \"\\n\"` で締めくくると、目に見えない差でテストが失敗します。改行は行と行の 間 にだけ入れます。",
  },
  {
    course: "python-basics",
    lesson: "py-execution-order",
    lessonTitle: "実行順序",
    heading: "`\\n` を全角で書く",
    detail: "`￥n` や全角の `n` では改行になりません。半角のバックスラッシュと半角の `n` を使います。日本語キーボードでは `¥` キーで入力できます。",
  },
  {
    course: "python-basics",
    lesson: "py-execution-order",
    lessonTitle: "実行順序",
    heading: "関数を定義する前に呼ぶ",
    detail: "`def` より上で呼び出すと `NameError` になります。定義が先、呼び出しがあと、という順番を守ります。",
  },
  {
    course: "python-basics",
    lesson: "py-comments",
    lessonTitle: "コメント",
    heading: "全角の `＃` を使う",
    detail: "見た目は似ていますが、`Python` はコメントとして扱いません。`SyntaxError` になります。記号は半角で入力します。",
  },
  {
    course: "python-basics",
    lesson: "py-comments",
    lessonTitle: "コメント",
    heading: "文字列の中の `#` をコメントだと思う",
    detail: "`print(\"#1 りんご\")` の `#` はただの文字で、コメントにはなりません。クオートの中は文字列として扱われます。",
  },
  {
    course: "python-basics",
    lesson: "py-comments",
    lessonTitle: "コメント",
    heading: "コメントで処理を止めたつもりが式が壊れる",
    detail: "`return price1 + # price2` のように式の途中で `#` を書くと、右辺が消えて `SyntaxError` になります。コメントアウトは行ごとに行います。",
  },
  {
    course: "python-basics",
    lesson: "py-read-errors",
    lessonTitle: "エラーを読んでみる",
    heading: "エラーの一番上だけ読む",
    detail: "上のほうは `Python` の内部の呼び出し履歴で、原因は いちばん下 に書いてあります。下から読むのが鉄則です。",
  },
  {
    course: "python-basics",
    lesson: "py-read-errors",
    lessonTitle: "エラーを読んでみる",
    heading: "行番号を信じすぎる",
    detail: "`SyntaxError` は原因の 1 行あとを指すことがあります。指摘された行に問題が無ければ、上の行を疑います。",
  },
  {
    course: "python-basics",
    lesson: "py-read-errors",
    lessonTitle: "エラーを読んでみる",
    heading: "エラーを消すために処理を消す",
    detail: "動かない行をまるごと削ればエラーは消えますが、必要な処理まで消えます。直すのであって、消すのではありません。",
  },
  {
    course: "python-basics",
    lesson: "py-ch1-build",
    lessonTitle: "つくる:レシート表示",
    heading: "末尾に `\\n` を付けてしまう",
    detail: "`\"合計 200円\"` のうしろに改行を足すと、見た目には分からない差でテストが落ちます。改行は行と行の間だけです。",
  },
  {
    course: "python-basics",
    lesson: "py-ch1-build",
    lessonTitle: "つくる:レシート表示",
    heading: "半角と全角が混ざる",
    detail: "`120円` の `1` `2` `0` は半角、`円` は全角です。数字を全角で打つと別の文字になり、一致しません。",
  },
  {
    course: "python-basics",
    lesson: "py-ch1-build",
    lessonTitle: "つくる:レシート表示",
    heading: "見出しの記号を間違える",
    detail: "見出しはイコール `=` が半角 3 つ、内側は半角スペース 1 つずつです。`==` や `====` では通りません。",
  },
  {
    course: "python-basics",
    lesson: "py-variables",
    lessonTitle: "変数",
    heading: "左右を逆に書く",
    detail: "`\"りんご\" = name` はエラーになります。入れる先の名前は必ず左です。",
  },
  {
    course: "python-basics",
    lesson: "py-variables",
    lessonTitle: "変数",
    heading: "入れる前に使う",
    detail: "`print(label)` を `label = ...` より前に書くと `NameError` になります。まだその名前を知らないからです。エラーメッセージが名前をそのまま教えてくれます。",
  },
  {
    course: "python-basics",
    lesson: "py-variables",
    lessonTitle: "変数",
    heading: "名前を打ち間違える",
    detail: "`price_text` を `price_txt` と書くと、別の変数を新しく作ったことになります。エラーが出ずに結果だけ狂うこともあるので、名前は正確に写します。",
  },
  {
    course: "python-basics",
    lesson: "py-type-check",
    lessonTitle: "typeで型を調べる",
    heading: "`__name__` のアンダースコアの数を間違える",
    detail: "前後とも 2 本ずつで合計 4 本です。1 本だと `AttributeError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-type-check",
    lessonTitle: "typeで型を調べる",
    heading: "`type` の結果をそのまま比べようとする",
    detail: "`type(120)` は `\"int\"` という文字列ではありません。文字列がほしいときは必ず `.__name__` を付けます。",
  },
  {
    course: "python-basics",
    lesson: "py-type-check",
    lessonTitle: "typeで型を調べる",
    heading: "`type` を変数名に使ってしまう",
    detail: "`type = \"食費\"` と書くと、以降 `type` が命令として使えなくなります。組み込みの名前は変数名に使いません。",
  },
  {
    course: "python-basics",
    lesson: "py-int-cast",
    lessonTitle: "数値への変換",
    heading: "`int` の結果を受け取らない",
    detail: "`int(price_text)` と書くだけでは何も起きません。`price = int(price_text)` のように変数に入れるか、そのまま `return` する必要があります。",
  },
  {
    course: "python-basics",
    lesson: "py-int-cast",
    lessonTitle: "数値への変換",
    heading: "単位ごと渡す",
    detail: "`int(\"1200円\")` は `ValueError` です。`円` を含めずに数字だけを渡します。",
  },
  {
    course: "python-basics",
    lesson: "py-int-cast",
    lessonTitle: "数値への変換",
    heading: "変換したつもりで文字列を足す",
    detail: "`int(price_text) + \"80\"` は `TypeError` です。片方だけ変換しても、もう片方が文字列なら混ぜられません。",
  },
  {
    course: "python-basics",
    lesson: "py-str-cast",
    lessonTitle: "文字列への変換",
    heading: "`str` を付け忘れる",
    detail: "`name + \" \" + price + \"円\"` は `TypeError` です。エラーメッセージは `can only concatenate str` と出ます。「文字列にしか足せない」という意味なので、数値のほうを `str` で包みます。",
  },
  {
    course: "python-basics",
    lesson: "py-str-cast",
    lessonTitle: "文字列への変換",
    heading: "全体を `str` で包んでしまう",
    detail: "`str(name + \" \" + price + \"円\")` と書いても直りません。かっこの中で先に連結が起きて、そこで落ちるからです。包むのは `price` だけです。",
  },
  {
    course: "python-basics",
    lesson: "py-str-cast",
    lessonTitle: "文字列への変換",
    heading: "`str` と `int` を取り違える",
    detail: "名前が短いので逆に書きがちです。`str` は文字列にする、`int` は整数にする、と結果の型で覚えます。",
  },
  {
    course: "python-basics",
    lesson: "py-bool-none",
    lessonTitle: "boolとNone",
    heading: "小文字で書く",
    detail: "`true` や `none` は `NameError` です。`True` `False` `None` はすべて先頭が大文字です。",
  },
  {
    course: "python-basics",
    lesson: "py-bool-none",
    lessonTitle: "boolとNone",
    heading: "クオートで囲む",
    detail: "`\"True\"` は `str` です。`bool` がほしいときはクオートを付けません。",
  },
  {
    course: "python-basics",
    lesson: "py-bool-none",
    lessonTitle: "boolとNone",
    heading: "`None` を 0 や空文字と同じに扱う",
    detail: "型が違うので、`type` の結果も `str` にしたときの見た目も変わります。未入力を表したいときだけ `None` を使います。",
  },
  {
    course: "python-basics",
    lesson: "py-f-string",
    lessonTitle: "f-string",
    heading: "先頭の `f` を忘れる",
    detail: "`\"{name} {price}円\"` は波かっこごと文字として残ります。実行してもエラーが出ないので気づきにくく、表示を見て初めて分かります。",
  },
  {
    course: "python-basics",
    lesson: "py-f-string",
    lessonTitle: "f-string",
    heading: "波かっこの中をクオートで囲む",
    detail: "`f\"{'name'} {price}円\"` と書くと、変数ではなく `name` という文字がそのまま入ります。かっこの中の変数名にクオートは付けません。",
  },
  {
    course: "python-basics",
    lesson: "py-f-string",
    lessonTitle: "f-string",
    heading: "かっこの外に空白を入れ忘れる",
    detail: "`f\"{name}{price}円\"` は `りんご120円` になります。空白が必要なら `{}` と `{}` の間に自分で書きます。",
  },
  {
    course: "python-basics",
    lesson: "py-input-basics",
    lessonTitle: "入力を受け取る",
    heading: "`input` の結果をそのまま計算に使う",
    detail: "戻り値は必ず `str` です。`int` を通さないと `TypeError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-input-basics",
    lessonTitle: "入力を受け取る",
    heading: "`split` の結果を 1 つの変数で受ける",
    detail: "`name = text.split(\" \")` と書くと、切り分けた 2 つがまとめて `name` に入ります。左に 2 つ名前を並べます。",
  },
  {
    course: "python-basics",
    lesson: "py-input-basics",
    lessonTitle: "入力を受け取る",
    heading: "区切りが全角スペースになっている",
    detail: "`split(\" \")` は半角スペースで切ります。全角スペースで打たれた文字列は切れません。",
  },
  {
    course: "python-basics",
    lesson: "py-ch2-build",
    lessonTitle: "つくる:入力できるおこづかい帳",
    heading: "合計の行を書き忘れる",
    detail: "3 行目の `合計 ...円` が必要です。品物 1 件なので金額は同じですが、行としては別に用意します。",
  },
  {
    course: "python-basics",
    lesson: "py-ch2-build",
    lessonTitle: "つくる:入力できるおこづかい帳",
    heading: "金額を文字列のまま連結する",
    detail: "f-string を使わずに `+` で書く場合は `str(price)` が必要です。忘れると `TypeError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-ch2-build",
    lessonTitle: "つくる:入力できるおこづかい帳",
    heading: "末尾に改行を付ける",
    detail: "`合計 120円` のうしろに `\\n` を足すと、見た目では分からない差でテストが落ちます。改行は行と行の間だけです。",
  },
  {
    course: "python-basics",
    lesson: "py-comparison",
    lessonTitle: "比較演算子",
    heading: "`=` と `==` の書き間違い",
    detail: "`total = budget` は代入なので、比較のつもりで書くとエラーか、意図しない上書きになります。くらべるときは必ずイコール 2 つです。",
  },
  {
    course: "python-basics",
    lesson: "py-comparison",
    lessonTitle: "比較演算子",
    heading: "`>` と `>=` の取り違え",
    detail: "「予算を超えたら」は `>`、「予算に達したら」は `>=` です。ちょうどの値の扱いが変わります。",
  },
  {
    course: "python-basics",
    lesson: "py-comparison",
    lessonTitle: "比較演算子",
    heading: "`True` の大文字を忘れる",
    detail: "`Python` の真偽値は `True` と `False` で、先頭が大文字です。`true` と書くと `NameError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-logical",
    lessonTitle: "論理演算子",
    heading: "記号で書いてしまう",
    detail: "他の言語の `&&` や `||` は `Python` では使えません。`SyntaxError` になります。英単語の `and` `or` `not` を使います。",
  },
  {
    course: "python-basics",
    lesson: "py-logical",
    lessonTitle: "論理演算子",
    heading: "条件をつなげたつもりで比較を省く",
    detail: "`category == \"食費\" and \"日用品\"` は意図した意味になりません。`and` の右側にも `category == \"日用品\"` のように完全な条件を書きます。",
  },
  {
    course: "python-basics",
    lesson: "py-logical",
    lessonTitle: "論理演算子",
    heading: "`and` と `or` を取り違える",
    detail: "「食費かつ 1000 円以上」を `or` で書くと、100 円の食費もすべて当たってしまいます。日本語の「かつ」「または」と対応させて確認します。",
  },
  {
    course: "python-basics",
    lesson: "py-in-operator",
    lessonTitle: "in演算子",
    heading: "カテゴリ名の文字を間違える",
    detail: "`\"交通\"` や `\"交通 費\"` のように 1 文字でも違うと一致しません。全角と半角、余分な空白に注意します。",
  },
  {
    course: "python-basics",
    lesson: "py-in-operator",
    lessonTitle: "in演算子",
    heading: "文字列とリストを混同する",
    detail: "`\"食\" in \"食費\"` は `True` ですが、`\"食\" in [\"食費\"]` は `False` です。右側が何なのかで判定の意味が変わります。",
  },
  {
    course: "python-basics",
    lesson: "py-in-operator",
    lessonTitle: "in演算子",
    heading: "`in` の左右を逆にする",
    detail: "`categories in category` と書くと意図が反転します。左が探す値、右が探される場所です。",
  },
  {
    course: "python-basics",
    lesson: "py-modulo",
    lessonTitle: "剰余と切り捨て除算",
    heading: "`/` と `//` の取り違え",
    detail: "`1000 / 3` は `333.3333333333333` という小数です。`f\"{1000 / 3}円\"` と書くと、とんでもない見た目になります。整数がほしいときは `//` です。",
  },
  {
    course: "python-basics",
    lesson: "py-modulo",
    lessonTitle: "剰余と切り捨て除算",
    heading: "0 で割ってしまう",
    detail: "`10 // 0` も `10 % 0` も `ZeroDivisionError` になります。人数のように「0 になりうる値」で割るときは注意が必要です。",
  },
  {
    course: "python-basics",
    lesson: "py-modulo",
    lessonTitle: "剰余と切り捨て除算",
    heading: "`%` を百分率と思い込む",
    detail: "`50 % 100` は「50 パーセント」ではなく「50 を 100 で割った余りの 50」です。",
  },
  {
    course: "python-basics",
    lesson: "py-chained-priority",
    lessonTitle: "連鎖比較と優先順位",
    heading: "不等号の向きをそろえない",
    detail: "`0 <= price >= budget` と書くと、意味の分からない条件になります。範囲を表すときは向きをそろえます。",
  },
  {
    course: "python-basics",
    lesson: "py-chained-priority",
    lessonTitle: "連鎖比較と優先順位",
    heading: "`and` と `or` の強さを逆に覚える",
    detail: "`and` のほうが強く、先にまとまります。混ぜるときはかっこを付けます。",
  },
  {
    course: "python-basics",
    lesson: "py-chained-priority",
    lessonTitle: "連鎖比較と優先順位",
    heading: "`0 <= price <= budget` を `0 <= price and <= budget` と書く",
    detail: "`and` の右側にも完全な条件が必要です。この形は `SyntaxError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-ternary",
    lessonTitle: "三項演算子",
    heading: "語順を間違える",
    detail: "`if 条件 then A else B` のような書き方は `Python` にはありません。値が先、`if`、条件、`else`、値の順です。",
  },
  {
    course: "python-basics",
    lesson: "py-ternary",
    lessonTitle: "三項演算子",
    heading: "`else` を省略する",
    detail: "三項演算子の `else` は必須です。省くと `SyntaxError` になります。式は必ず何らかの値にならなければいけないからです。",
  },
  {
    course: "python-basics",
    lesson: "py-ternary",
    lessonTitle: "三項演算子",
    heading: "クオートを重ねる",
    detail: "f-string の中で外と同じクオートを使うと文字列が途中で切れます。外が `\"` なら内は `'` です。",
  },
  {
    course: "python-basics",
    lesson: "py-ch3-build",
    lessonTitle: "つくる:予算チェック",
    heading: "末尾に `\\n` を付けてしまう",
    detail: "3 行目のうしろに改行を足すと、見た目には分からない差でテストが落ちます。改行は行と行の間の 2 か所だけです。",
  },
  {
    course: "python-basics",
    lesson: "py-ch3-build",
    lessonTitle: "つくる:予算チェック",
    heading: "引き算の向きを間違える",
    detail: "マイナス付きの数字が出たら、この間違いです。どちらの枝にいるかを確認してください。",
  },
  {
    course: "python-basics",
    lesson: "py-ch3-build",
    lessonTitle: "つくる:予算チェック",
    heading: "f-string の `f` を書き忘れる",
    detail: "`\"合計 {total}円\"` と書くと、`{total}` が中身に置き換わらず、そのまま文字として出ます。",
  },
  {
    course: "python-basics",
    lesson: "py-if-basics",
    lessonTitle: "if文の基本",
    heading: "コロンを忘れる",
    detail: "`if price >= 1000` と書いて末尾の `:` を落とすと `SyntaxError` になります。`if` の行は必ずコロンで終わります。",
  },
  {
    course: "python-basics",
    lesson: "py-if-basics",
    lessonTitle: "if文の基本",
    heading: "インデントがずれる",
    detail: "半角スペース 4 つが基本です。タブと混ぜたり、2 つと 4 つが混在したりすると `IndentationError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-if-basics",
    lessonTitle: "if文の基本",
    heading: "`=` と `==` を取り違える",
    detail: "条件で使うのは比較の `==` です。代入の `=` を書くと `SyntaxError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-if-elif-else",
    lessonTitle: "if / elif / else",
    heading: "`elif` の順番が逆",
    detail: "`if price < 2000` を先に書くと、`300` も `2000` 未満なので「普通」で確定してしまい、「節約」に永遠に届きません。狭い条件から先に 書きます。",
  },
  {
    course: "python-basics",
    lesson: "py-if-elif-else",
    lessonTitle: "if / elif / else",
    heading: "`else` に条件を書く",
    detail: "`else price >= 2000:` は `SyntaxError` です。条件を付けたいなら `elif` を使います。",
  },
  {
    course: "python-basics",
    lesson: "py-if-elif-else",
    lessonTitle: "if / elif / else",
    heading: "`elseif` や `else if` と書く",
    detail: "`Python` の綴りは `elif` です。他の言語の書き方をそのまま持ち込むと `SyntaxError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-nested-if",
    lessonTitle: "条件の組み合わせ",
    heading: "`day == \"土\" or \"日\"` と書く",
    detail: "`Python` はこれを `(day == \"土\") or (\"日\")` と読みます。`\"日\"` は空でない文字列なので常に `True` 扱いになり、どの曜日でも週末になってしまいます。`==` は省略できません。",
  },
  {
    course: "python-basics",
    lesson: "py-nested-if",
    lessonTitle: "条件の組み合わせ",
    heading: "インデントを 1 段しか下げない",
    detail: "内側の `if` の中身は 12 個分下げます。8 個のままだと外側の `if` 直下の処理になり、意味が変わります。",
  },
  {
    course: "python-basics",
    lesson: "py-nested-if",
    lessonTitle: "条件の組み合わせ",
    heading: "`and` と `or` を取り違える",
    detail: "`and` は両方、`or` はどちらか一方です。「週末かつ高額」は `and` です。",
  },
  {
    course: "python-basics",
    lesson: "py-match",
    lessonTitle: "match文",
    heading: "`case _` を書き忘れる",
    detail: "どの `case` にも一致しないと `match` 全体が素通りされます。関数なら `return` に届かず `None` が返り、テストに落ちます。",
  },
  {
    course: "python-basics",
    lesson: "py-match",
    lessonTitle: "match文",
    heading: "`case` の値をクオートで囲まない",
    detail: "`case 食費:` と書くと、`Python` は `食費` という変数名だと解釈します。文字列と比べたいなら `case \"食費\":` です。",
  },
  {
    course: "python-basics",
    lesson: "py-match",
    lessonTitle: "match文",
    heading: "インデントが 1 段足りない",
    detail: "`match` の中の `case` で 1 段、`case` の中身でさらに 1 段下げます。関数の中なら `case` の中身は 12 個分になります。",
  },
  {
    course: "python-basics",
    lesson: "py-fizzbuzz",
    lessonTitle: "定番:FizzBuzz判定",
    heading: "順番を逆にする",
    detail: "上で見たとおりです。`\"FizzBuzz\"` が一度も出ない、という症状が出たらまず順番を疑います。",
  },
  {
    course: "python-basics",
    lesson: "py-fizzbuzz",
    lessonTitle: "定番:FizzBuzz判定",
    heading: "数値のまま返す",
    detail: "最後の枝で `return n` と書くと、`7` という数値が返ります。期待されているのは文字列の `\"7\"` なので `str(n)` が必要です。",
  },
  {
    course: "python-basics",
    lesson: "py-fizzbuzz",
    lessonTitle: "定番:FizzBuzz判定",
    heading: "`==` を書き忘れる",
    detail: "`if n % 3:` と書くと「余りが `0` でない」という逆の意味になります。余りが `0` かどうかを見るので `n % 3 == 0` です。",
  },
  {
    course: "python-basics",
    lesson: "py-default-or",
    lessonTitle: "デフォルト値とor",
    heading: "`or` が `True` を返すと思い込む",
    detail: "返るのは元の値です。`\"食費\" or \"その他\"` は `True` ではなく `\"食費\"` です。",
  },
  {
    course: "python-basics",
    lesson: "py-default-or",
    lessonTitle: "デフォルト値とor",
    heading: "数値に `or` を使う",
    detail: "`price or 100` と書くと `0` 円が `100` になります。数値では `if price is None:` と明示的に書きます。",
  },
  {
    course: "python-basics",
    lesson: "py-default-or",
    lessonTitle: "デフォルト値とor",
    heading: "`and` と取り違える",
    detail: "既定値の穴埋めは `or` です。`and` を使うと逆に、値があるときだけ右側が返ります。",
  },
  {
    course: "python-basics",
    lesson: "py-ch4-build",
    lessonTitle: "つくる:カテゴリ自動判定",
    heading: "`name in \"りんご\"` と左右を逆に書く",
    detail: "`in` は左が探すもの、右が探される場所です。逆にすると意味が反転します。",
  },
  {
    course: "python-basics",
    lesson: "py-ch4-build",
    lessonTitle: "つくる:カテゴリ自動判定",
    heading: "`or` のたびに `in` を書き忘れる",
    detail: "`if \"りんご\" or \"パン\" in name:` は `\"りんご\"` が常に `True` 扱いになるため、すべて食費になります。`or` でつなぐのは条件そのものです。",
  },
  {
    course: "python-basics",
    lesson: "py-ch4-build",
    lessonTitle: "つくる:カテゴリ自動判定",
    heading: "最後の受け皿を忘れる",
    detail: "`elif` だけで終えると、どれにも当てはまらない品名で `None` が返ります。",
  },
  {
    course: "python-basics",
    lesson: "py-for-sum",
    lessonTitle: "forで合計",
    heading: "`total = 0` の書き忘れ",
    detail: "用意していない名前にいきなり足そうとすると `NameError` になります。ループの前に必ず初期値を置きます。",
  },
  {
    course: "python-basics",
    lesson: "py-for-sum",
    lessonTitle: "forで合計",
    heading: "`return` をループの中に書いてしまう",
    detail: "`return total` がインデントの内側にあると、1 周目で関数が終わってしまい、最初の 1 件だけが返ります。`return` はループの外、`for` と同じ深さより手前に置きます。",
  },
  {
    course: "python-basics",
    lesson: "py-for-sum",
    lessonTitle: "forで合計",
    heading: "リストそのものを足してしまう",
    detail: "`total = total + prices` と書くと、数値とリストを足すことになり `TypeError` です。足すのは取り出した 1 件の `price` です。",
  },
  {
    course: "python-basics",
    lesson: "py-while-loop",
    lessonTitle: "whileで繰り返す",
    heading: "カウンタを進め忘れる",
    detail: "`i = i + 1` を書き忘れると、同じ 1 件を永久に読み続けます。手元で試すときは、止まらなくなったら Ctrl + C で強制終了できます。",
  },
  {
    course: "python-basics",
    lesson: "py-while-loop",
    lessonTitle: "whileで繰り返す",
    heading: "`i = i + 1` を `break` の前に置いてしまう",
    detail: "位置そのものは動きますが、`終了` を `items` に入れてしまう順番になっていないか、必ず読み返します。取り込むのは `終了` ではない行だけです。",
  },
  {
    course: "python-basics",
    lesson: "py-while-loop",
    lessonTitle: "whileで繰り返す",
    heading: "`while` の条件を `i <= len(inputs)` にしてしまう",
    detail: "最後の 1 件を読んだあと、存在しない位置を読もうとして `IndexError` になります。件数が 3 なら読める位置は 0 と 1 と 2 までです。",
  },
  {
    course: "python-basics",
    lesson: "py-range-step",
    lessonTitle: "rangeとstep",
    heading: "終わりの値を 1 つ足し忘れる",
    detail: "`range(1, last_day, step)` と書くと最終日が出ません。`last_day` まで含めたいなら `last_day + 1` です。",
  },
  {
    course: "python-basics",
    lesson: "py-range-step",
    lessonTitle: "rangeとstep",
    heading: "数値と文字列をそのまま足す",
    detail: "`day + \"日目\"` は `TypeError` になります。`str(day)` で文字列に変えてからつなぎます。",
  },
  {
    course: "python-basics",
    lesson: "py-range-step",
    lessonTitle: "rangeとstep",
    heading: "`step` に `0` を渡してしまう",
    detail: "`range` の刻み幅に `0` は使えず、`ValueError` になります。刻み幅は必ず `0` 以外です。",
  },
  {
    course: "python-basics",
    lesson: "py-break-continue",
    lessonTitle: "breakとcontinue",
    heading: "`break` と `continue` を取り違える",
    detail: "0 円を飛ばすつもりで `break` を書くと、最初の 0 円で集計が止まってしまいます。飛ばしたいのか終わりたいのか、書く前に決めます。",
  },
  {
    course: "python-basics",
    lesson: "py-break-continue",
    lessonTitle: "breakとcontinue",
    heading: "`continue` の下に処理を書いて実行されると思い込む",
    detail: "`continue` より下の行は、その周では絶対に動きません。「書いたのに動かない」の原因になりがちです。",
  },
  {
    course: "python-basics",
    lesson: "py-break-continue",
    lessonTitle: "breakとcontinue",
    heading: "`while` で `continue` を使ってカウンタを飛ばす",
    detail: "`while` の中で `continue` すると、カウンタを進める行を飛ばしてしまい無限ループになります。`while` と `continue` の組み合わせは特に慎重に扱います。",
  },
  {
    course: "python-basics",
    lesson: "py-enumerate",
    lessonTitle: "enumerateで番号付け",
    heading: "開始番号を忘れる",
    detail: "`enumerate(names)` のままだと `0. りんご` から始まります。人に見せる一覧なら `enumerate(names, 1)` です。",
  },
  {
    course: "python-basics",
    lesson: "py-enumerate",
    lessonTitle: "enumerateで番号付け",
    heading: "番号を文字列に変え忘れる",
    detail: "`i + \". \"` は数値と文字列の足し算になり `TypeError` です。`str(i)` を挟みます。",
  },
  {
    course: "python-basics",
    lesson: "py-enumerate",
    lessonTitle: "enumerateで番号付け",
    heading: "毎周のうしろに改行を足す",
    detail: "`result = result + str(i) + \". \" + name + \"\\n\"` と書くと、最後の行のうしろにも改行が残ります。改行は行の 手前 に足すと、この問題が起きません。",
  },
  {
    course: "python-basics",
    lesson: "py-zip",
    lessonTitle: "zipで組み合わせ",
    heading: "`zip` に渡し忘れる",
    detail: "`for name, price in names:` と書くと、品名 1 文字ずつを 2 つに分けようとして `ValueError` になります。組にするには `zip` が要ります。",
  },
  {
    course: "python-basics",
    lesson: "py-zip",
    lessonTitle: "zipで組み合わせ",
    heading: "金額を文字列に変え忘れる",
    detail: "`name + \" \" + price` は `TypeError` です。`str(price)` を挟みます。",
  },
  {
    course: "python-basics",
    lesson: "py-zip",
    lessonTitle: "zipで組み合わせ",
    heading: "長さのずれに気づかない",
    detail: "`zip` は短いほうで止まり、余った分を黙って捨てます。件数が合うことを前提にするなら、`len` を比べて確かめる処理を別途書きます。",
  },
  {
    course: "python-basics",
    lesson: "py-nested-loop",
    lessonTitle: "入れ子ループ",
    heading: "行の入れ物を初期化する位置を間違える",
    detail: "`line = \"\"` を外側のループより前に書くと、すべての行がつながってしまいます。行ごとにリセットする必要があります。",
  },
  {
    course: "python-basics",
    lesson: "py-nested-loop",
    lessonTitle: "入れ子ループ",
    heading: "内側と外側で同じ変数名を使う",
    detail: "内側でも `i` を使うと、外側の `i` が上書きされて表が崩れます。外側は `i`、内側は `j` のように分けます。",
  },
  {
    course: "python-basics",
    lesson: "py-nested-loop",
    lessonTitle: "入れ子ループ",
    heading: "`break` が外側まで効くと思い込む",
    detail: "`break` と `continue` は、いちばん内側のループ 1 つにしか効きません。内側で `break` しても、外側は次の周に進みます。",
  },
  {
    course: "python-basics",
    lesson: "py-ch5-build",
    lessonTitle: "つくる:何件でも記録",
    heading: "合計行の手前に改行を入れ忘れる",
    detail: "一覧の最後と `合計` がつながって 1 行になります。行を足すときは必ず `\"\\n\"` を先に足します。",
  },
  {
    course: "python-basics",
    lesson: "py-ch5-build",
    lessonTitle: "つくる:何件でも記録",
    heading: "金額とカテゴリを文字列に変え忘れる",
    detail: "`record[\"price\"]` は数値なので、`str` を挟まないと `TypeError` です。`record[\"category\"]` は最初から文字列なのでそのままつなげます。",
  },
  {
    course: "python-basics",
    lesson: "py-ch5-build",
    lessonTitle: "つくる:何件でも記録",
    heading: "空のときの分岐を書き忘れる",
    detail: "記録が 0 件でもループは静かに 0 周で終わるため、エラーは出ません。`記録がありません` の行が抜けたまま気づかない、というのがありがちです。",
  },
  {
    course: "python-basics",
    lesson: "py-def-basics",
    lessonTitle: "関数を定義する",
    heading: "コロンと字下げを忘れる",
    detail: "`def greet_user(name)` とコロンを書かないと `SyntaxError` です。字下げがないと `IndentationError` になります。字下げは半角スペース 4 つに揃えます。",
  },
  {
    course: "python-basics",
    lesson: "py-def-basics",
    lessonTitle: "関数を定義する",
    heading: "呼び出しでかっこを付け忘れる",
    detail: "`greet_user` とだけ書くと、関数そのものを指すだけで実行されません。`greet_user(\"たろう\")` とかっこを付けて初めて動きます。",
  },
  {
    course: "python-basics",
    lesson: "py-def-basics",
    lessonTitle: "関数を定義する",
    heading: "`return` を書かずに `print` で済ませる",
    detail: "画面には出ますが、戻り値は `None` になります。採点は戻り値を見るので、必ず `return` で返します。",
  },
  {
    course: "python-basics",
    lesson: "py-args-return",
    lessonTitle: "引数と戻り値",
    heading: "引数の順番を間違える",
    detail: "位置引数は前から順に入ります。`split(people, total)` のように順を取り違えると、エラーは出ないのに答えだけが狂います。いちばん見つけにくい種類のバグです。",
  },
  {
    course: "python-basics",
    lesson: "py-args-return",
    lessonTitle: "引数と戻り値",
    heading: "引数の数が合わない",
    detail: "`add_tax()` のように渡し忘れると `TypeError` になり、`missing 1 required positional argument` と教えてくれます。多すぎても同じく `TypeError` です。",
  },
  {
    course: "python-basics",
    lesson: "py-args-return",
    lessonTitle: "引数と戻り値",
    heading: "`/` と `//` を取り違える",
    detail: "`price * 110 / 100` は小数の `132.0` になります。`132` とは別の値なので、テストは落ちます。整数が欲しいときは `//` です。",
  },
  {
    course: "python-basics",
    lesson: "py-default-args",
    lessonTitle: "デフォルト引数",
    heading: "既定値の引数を前に置く",
    detail: "`def f(budget=3000, total)` は `SyntaxError` です。既定値のある引数は必ず後ろにまとめます。",
  },
  {
    course: "python-basics",
    lesson: "py-default-args",
    lessonTitle: "デフォルト引数",
    heading: "リストや辞書を既定値にする",
    detail: "呼び出しをまたいで同じものが使い回されます。`None` を既定値にして関数の中で作り直します。",
  },
  {
    course: "python-basics",
    lesson: "py-default-args",
    lessonTitle: "デフォルト引数",
    heading: "境界を取り違える",
    detail: "`>=` にすると `3000` 円ちょうどでもオーバー扱いになります。`>` と `>=` のどちらが仕様かを確かめてから書きます。",
  },
  {
    course: "python-basics",
    lesson: "py-keyword-args",
    lessonTitle: "キーワード引数",
    heading: "引数名を間違える",
    detail: "`format_record(\"りんご\", 120, categoly=\"食費\")` のように綴りを間違えると `TypeError` になり、`unexpected keyword argument` と出ます。定義側の綴りを確認します。",
  },
  {
    course: "python-basics",
    lesson: "py-keyword-args",
    lessonTitle: "キーワード引数",
    heading: "同じ引数を二重に渡す",
    detail: "`format_record(\"りんご\", 120, \"食費\", category=\"日用品\")` は、`category` に位置とキーワードの両方から値が来るので `TypeError` です。どちらか片方にします。",
  },
  {
    course: "python-basics",
    lesson: "py-keyword-args",
    lessonTitle: "キーワード引数",
    heading: "`str` を忘れて数値を連結する",
    detail: "`name + \" \" + price` は `TypeError` です。金額は数値なので、`str(price)` で文字列にしてからつなぎます。第 2 章でやった変換がここでも要ります。",
  },
  {
    course: "python-basics",
    lesson: "py-multiple-return",
    lessonTitle: "複数の戻り値",
    heading: "`return` を 2 行書く",
    detail: "`return total` の次の行に `return average` と書いても、2 行目には決して届きません。`return` は関数を終わらせるので、1 つの `return` にカンマで並べます。",
  },
  {
    course: "python-basics",
    lesson: "py-multiple-return",
    lessonTitle: "複数の戻り値",
    heading: "受け取る変数の数が合わない",
    detail: "`total, average, count = total_and_average(prices)` は `ValueError` です。`not enough values to unpack` と出ます。",
  },
  {
    course: "python-basics",
    lesson: "py-multiple-return",
    lessonTitle: "複数の戻り値",
    heading: "空のリストを忘れる",
    detail: "0 件で `ZeroDivisionError` になります。テストには必ず空の場合を入れて確かめます。",
  },
  {
    course: "python-basics",
    lesson: "py-divmod",
    lessonTitle: "divmod",
    heading: "引数の順番を逆にする",
    detail: "`divmod(people, total)` と書くと `divmod(3, 1000)` になり、商が `0` で余りが `3` という意味の分からない答えが返ります。エラーが出ないぶん厄介です。",
  },
  {
    course: "python-basics",
    lesson: "py-divmod",
    lessonTitle: "divmod",
    heading: "タプルのまま返してしまう",
    detail: "この環境の判定はリスト形式なので、`return divmod(total, people)` では通りません。`q, r = divmod(...)` で受けてから `[q, r]` にします。",
  },
  {
    course: "python-basics",
    lesson: "py-divmod",
    lessonTitle: "divmod",
    heading: "`0` 除算を考えていない",
    detail: "人数 `0` で `ZeroDivisionError` です。入口で弾きます。",
  },
  {
    course: "python-basics",
    lesson: "py-scope",
    lessonTitle: "変数のスコープ",
    heading: "関数の外の変数を直接書き換えようとする",
    detail: "`UnboundLocalError` になります。`global` で押し切らず、引数と戻り値の形に直します。",
  },
  {
    course: "python-basics",
    lesson: "py-scope",
    lessonTitle: "変数のスコープ",
    heading: "戻り値を受け取り忘れる",
    detail: "`add_record(records, \"りんご\", 120, \"食費\")` とだけ書いて `records = ` を付けないと、新しいリストは捨てられ、何も増えません。エラーが出ないぶん気づきにくい間違いです。",
  },
  {
    course: "python-basics",
    lesson: "py-scope",
    lessonTitle: "変数のスコープ",
    heading: "キーの名前を間違える",
    detail: "キーは `name` と `price` と `category` の 3 つです。`namae` などと書くと、あとで取り出すときに落ちます。",
  },
  {
    course: "python-basics",
    lesson: "py-lambda",
    lessonTitle: "lambda関数",
    heading: "`lambda` に `return` を書く",
    detail: "`lambda r: return r[\"price\"]` は `SyntaxError` です。`lambda` は式なので、`return` は書きません。",
  },
  {
    course: "python-basics",
    lesson: "py-lambda",
    lessonTitle: "lambda関数",
    heading: "`key` に呼び出した結果を渡す",
    detail: "`key=lambda r: r[\"price\"]()` のようにかっこを付けると、金額を関数として呼ぼうとして `TypeError` になります。渡すのは関数そのものです。",
  },
  {
    course: "python-basics",
    lesson: "py-lambda",
    lessonTitle: "lambda関数",
    heading: "`sort` と `sorted` を取り違える",
    detail: "`records.sort()` は元のリストを並べ替えて `None` を返します。新しいリストが欲しいときは `sorted` です。戻り値が `None` になっていたら、まずここを疑います。",
  },
  {
    course: "python-basics",
    lesson: "py-ch6-build",
    lessonTitle: "つくる:関数に分割",
    heading: "キーの名前や数を変える",
    detail: "キーは `count` `total` `remaining` `over` の 4 つです。1 つでも綴りが違うと、受け取る側が取り出せません。",
  },
  {
    course: "python-basics",
    lesson: "py-ch6-build",
    lessonTitle: "つくる:関数に分割",
    heading: "`total` の初期化を忘れる",
    detail: "`for` の前に `total = 0` を書かないと `NameError` です。合計を作るときの定番の手順です。",
  },
  {
    course: "python-basics",
    lesson: "py-ch6-build",
    lessonTitle: "つくる:関数に分割",
    heading: "`over` を文字列にする",
    detail: "`\"オーバー\"` のような文字列ではなく、`True` か `False` の真偽値を入れます。判定結果は真偽値で返し、言葉にするのは表示側の仕事です。",
  },
  {
    course: "python-basics",
    lesson: "py-list-methods",
    lessonTitle: "リストの追加と削除",
    heading: "無い値を `remove` する",
    detail: "`[\"りんご\"].remove(\"みかん\")` は `ValueError` になり、プログラムがそこで止まります。メッセージは `list.remove(x): x not in list` です。消す前に `if \"みかん\" in items` で存在を確かめるのが基本の対策です。",
  },
  {
    course: "python-basics",
    lesson: "py-list-methods",
    lessonTitle: "リストの追加と削除",
    heading: "`append` の戻り値を受け取る",
    detail: "`items = items.append(\"ノート\")` と書くと `items` が `None` になり、次の行で `TypeError` が出ます。`append` は呼ぶだけにします。",
  },
  {
    course: "python-basics",
    lesson: "py-list-methods",
    lessonTitle: "リストの追加と削除",
    heading: "`append` に複数渡す",
    detail: "`items.append(\"りんご\", \"みかん\")` はエラーです。`append` が受け取れるのは 1 つだけで、2 件足したいなら 2 回呼びます。",
  },
  {
    course: "python-basics",
    lesson: "py-slicing",
    lessonTitle: "スライス",
    heading: "終了位置を含むと思い込む",
    detail: "`items[0:3]` は 4 件ではなく 3 件です。「終了の手前まで」と覚えます。取れた件数が 1 つ足りない、多いと感じたらここを疑います。",
  },
  {
    course: "python-basics",
    lesson: "py-slicing",
    lessonTitle: "スライス",
    heading: "`items[-3]` と `items[-3:]` を混同する",
    detail: "コロンが無いと 1 件だけ、あるとリストが返ります。前者の戻り値は辞書や文字列、後者はリストです。",
  },
  {
    course: "python-basics",
    lesson: "py-slicing",
    lessonTitle: "スライス",
    heading: "空リストで `items[0]` を読む",
    detail: "`[]` に対する `items[0]` は `IndexError` です。`len(items)` で件数を確かめるか、スライスを使えば避けられます。",
  },
  {
    course: "python-basics",
    lesson: "py-dict-basics",
    lessonTitle: "辞書の基本",
    heading: "キーのクオートを忘れる",
    detail: "`record[name]` と書くと、`name` という変数を探しに行ってしまいます。変数が無ければ `NameError` です。キーを直接書くときは `record[\"name\"]` とクオートで囲みます。",
  },
  {
    course: "python-basics",
    lesson: "py-dict-basics",
    lessonTitle: "辞書の基本",
    heading: "無いキーを読む",
    detail: "`record[\"memo\"]` は `KeyError` になります。`in` で確認するか、キーが無くても落ちない `record.get(\"memo\")` を使います。`get` は無いとき `None` を返します。",
  },
  {
    course: "python-basics",
    lesson: "py-dict-basics",
    lessonTitle: "辞書の基本",
    heading: "リストと辞書のかっこを取り違える",
    detail: "`[]` はリスト、`{}` は辞書です。`{\"name\", \"price\"}` のようにコロンを書き忘れると、辞書ではなく別のものになります。",
  },
  {
    course: "python-basics",
    lesson: "py-dict-loop",
    lessonTitle: "辞書を走査",
    heading: "`for key, value in record` と書く",
    detail: "`items()` を付けずに 2 つ受け取ろうとすると `ValueError` になります。辞書をそのまま回すと 1 周ごとにキーが 1 つ返るだけなので、2 つに分けられません。",
  },
  {
    course: "python-basics",
    lesson: "py-dict-loop",
    lessonTitle: "辞書を走査",
    heading: "`+` でつなぐときに `int` を混ぜる",
    detail: "`key + \" = \" + value` は `value` が `120` だと `TypeError` です。f-string を使うか `str(value)` で変換します。",
  },
  {
    course: "python-basics",
    lesson: "py-dict-loop",
    lessonTitle: "辞書を走査",
    heading: "末尾に改行を足してしまう",
    detail: "ループの中で `+ \"\\n\"` を付けると最後の行にも改行が残ります。`join` を使えばこの問題は起きません。",
  },
  {
    course: "python-basics",
    lesson: "py-sort-key",
    lessonTitle: "keyでソート",
    heading: "`key` に呼び出し結果を渡す",
    detail: "`key=lambda r: r[\"price\"]()` のようにかっこを足すと関数ではなくなります。渡すのは関数そのもので、呼ぶのは `sorted` の仕事です。",
  },
  {
    course: "python-basics",
    lesson: "py-sort-key",
    lessonTitle: "keyでソート",
    heading: "キーの名前を間違える",
    detail: "`r[\"prise\"]` のようなつづり違いは `KeyError` になります。エラーメッセージに無いキー名がそのまま出るので、見比べれば分かります。",
  },
  {
    course: "python-basics",
    lesson: "py-sort-key",
    lessonTitle: "keyでソート",
    heading: "降順を `-1` などで表そうとする",
    detail: "降順は `reverse=True` です。`sorted(...)[::-1]` でも見た目は逆順になりますが、同じ値どうしの並びが変わってしまうことがあるため `reverse` を使います。",
  },
  {
    course: "python-basics",
    lesson: "py-set-dedup",
    lessonTitle: "setで重複除去",
    heading: "集合をそのまま返す",
    detail: "`return set(...)` と書くと、順番が定まらないうえに型もリストではありません。テストの比較も安定しません。必ず `sorted` でリストにしてから返します。",
  },
  {
    course: "python-basics",
    lesson: "py-set-dedup",
    lessonTitle: "setで重複除去",
    heading: "空集合を `{}` と書く",
    detail: "`{}` は空の 辞書 です。空の集合は `set()` です。`type({})` を試すと `dict` と出ます。",
  },
  {
    course: "python-basics",
    lesson: "py-set-dedup",
    lessonTitle: "setで重複除去",
    heading: "番号で取り出そうとする",
    detail: "集合には順番が無いので `categories[0]` は `TypeError` になります。取り出したいなら先にリストへ変換します。",
  },
  {
    course: "python-basics",
    lesson: "py-list-comprehension",
    lessonTitle: "リスト内包表記",
    heading: "`append` を書いてしまう",
    detail: "`[result.append(r) for r in records]` と書くと、`append` の戻り値である `None` が並んだリストができます。内包表記の中では `append` は要りません。",
  },
  {
    course: "python-basics",
    lesson: "py-list-comprehension",
    lessonTitle: "リスト内包表記",
    heading: "`if` の位置を間違える",
    detail: "絞り込みの `if` は `for` の うしろ です。前に書く `[a if 条件 else b for ...]` は第 3 章の三項演算子で、絞り込みではなく値の切り替えになります。",
  },
  {
    course: "python-basics",
    lesson: "py-list-comprehension",
    lessonTitle: "リスト内包表記",
    heading: "条件に `=` を 1 つしか書かない",
    detail: "`if record[\"category\"] = \"食費\"` は `SyntaxError` です。比較は `==` で、代入の `=` とは別ものです。",
  },
  {
    course: "python-basics",
    lesson: "py-tuple-unpack",
    lessonTitle: "タプルアンパック",
    heading: "要素の数が合わない",
    detail: "3 つ入った組を `name, price = pair` で受けると `ValueError` になります。メッセージは `too many values to unpack` です。数を合わせるか、`pair[0]` のように番号で取ります。",
  },
  {
    course: "python-basics",
    lesson: "py-tuple-unpack",
    lessonTitle: "タプルアンパック",
    heading: "要素 1 つのタプルを `(\"りんご\")` と書く",
    detail: "これはただの文字列です。要素が 1 つのタプルには `(\"りんご\",)` とカンマが必要です。",
  },
  {
    course: "python-basics",
    lesson: "py-tuple-unpack",
    lessonTitle: "タプルアンパック",
    heading: "タプルを書き換えようとする",
    detail: "`pair[1] = 150` は `TypeError` です。変えたいなら新しいタプルを作り直すか、最初からリストを使います。",
  },
  {
    course: "python-basics",
    lesson: "py-ch7-build",
    lessonTitle: "つくる:カテゴリ別集計",
    heading: "初回の `KeyError`",
    detail: "存在確認をせずに足そうとすると、そのカテゴリが初めて出てきた時点で止まります。`in` で確認するか、無いとき `0` を返す `totals.get(category, 0)` を使います。",
  },
  {
    course: "python-basics",
    lesson: "py-ch7-build",
    lessonTitle: "つくる:カテゴリ別集計",
    heading: "`0` のキーを入れてしまう",
    detail: "5 カテゴリ分を先に `0` で埋めると、使っていないカテゴリまで結果に残ります。この仕様では入れません。",
  },
  {
    course: "python-basics",
    lesson: "py-ch7-build",
    lessonTitle: "つくる:カテゴリ別集計",
    heading: "合計を文字列で作る",
    detail: "値は `int` です。`str` にすると `\"120180\"` のような連結になり、金額として使えなくなります。",
  },
  {
    course: "python-basics",
    lesson: "py-file-read",
    lessonTitle: "ファイルを読み込む",
    heading: "`close` を書き忘れる",
    detail: "開いたまま放置するとファイルが解放されず、書き込んだ内容が反映されないことがあります。次のレッスンで学ぶ `with` を使えばこの問題は消えます。",
  },
  {
    course: "python-basics",
    lesson: "py-file-read",
    lessonTitle: "ファイルを読み込む",
    heading: "モードを間違える",
    detail: "読むつもりで `\"w\"` を渡すと、ファイルの中身が空になってしまいます。`\"r\"` が読み、`\"w\"` が書きです。",
  },
  {
    course: "python-basics",
    lesson: "py-file-read",
    lessonTitle: "ファイルを読み込む",
    heading: "戻り値が文字列のまま",
    detail: "`read` の結果をそのまま `return` すると、行のリストではなく 1 本の文字列が返ります。`splitlines` を通してからにします。",
  },
  {
    course: "python-basics",
    lesson: "py-file-write",
    lessonTitle: "ファイルに書き込む",
    heading: "`write` に改行を期待する",
    detail: "`write(\"りんご\")` を 2 回呼んでも 2 行にはならず、`りんごりんご` と横につながります。改行は自分で書きます。",
  },
  {
    course: "python-basics",
    lesson: "py-file-write",
    lessonTitle: "ファイルに書き込む",
    heading: "`write` にリストを渡す",
    detail: "`f.write([\"a\", \"b\"])` は `TypeError` になります。`write` が受け取れるのは文字列だけなので、先に `join` で 1 本にします。",
  },
  {
    course: "python-basics",
    lesson: "py-file-write",
    lessonTitle: "ファイルに書き込む",
    heading: "数値をそのまま渡す",
    detail: "`f.write(120)` も `TypeError` です。金額を書くときは `str(120)` で文字列にしてからにします。",
  },
  {
    course: "python-basics",
    lesson: "py-with-statement",
    lessonTitle: "withで安全に扱う",
    heading: "コロンとインデントを忘れる",
    detail: "`with open(path, \"w\") as f` の行末にはコロンが必要で、次の行はインデントします。忘れると `SyntaxError` になります。",
  },
  {
    course: "python-basics",
    lesson: "py-with-statement",
    lessonTitle: "withで安全に扱う",
    heading: "ブロックの外で `f` を使う",
    detail: "ブロックを抜けたあとに `f.read()` を呼ぶと `ValueError` になります。閉じたファイルは読めません。",
  },
  {
    course: "python-basics",
    lesson: "py-with-statement",
    lessonTitle: "withで安全に扱う",
    heading: "`with` を使ったのに `close` も書く",
    detail: "動きはしますが二重に閉じる意味のない行です。`with` を使ったら `close` は書きません。",
  },
  {
    course: "python-basics",
    lesson: "py-csv-like",
    lessonTitle: "CSV形式で保存",
    heading: "`str` を付け忘れる",
    detail: "`\",\".join([name, price, category])` のように `int` を混ぜると `TypeError` になります。エラーメッセージは sequence item 1 と、何番目が悪いかを教えてくれます。",
  },
  {
    course: "python-basics",
    lesson: "py-csv-like",
    lessonTitle: "CSV形式で保存",
    heading: "区切り文字を混同する",
    detail: "項目の区切りは `\",\"`、行の区切りは `\"\\n\"` です。両方 `join` で書くので取り違えやすい部分です。",
  },
  {
    course: "python-basics",
    lesson: "py-csv-like",
    lessonTitle: "CSV形式で保存",
    heading: "品名にカンマが入る",
    detail: "`\"りんご, みかんセット\"` のようにカンマを含む品名を保存すると、読み戻したときに項目数が合わなくなります。このコースでは品名にカンマを使わない前提にしますが、実務では標準ライブラリの `csv` を使ってこの問題を避けます。",
  },
  {
    course: "python-basics",
    lesson: "py-try-except",
    lessonTitle: "try-exceptで備える",
    heading: "`except` の範囲を広げすぎる",
    detail: "`except:` とだけ書くと、あらゆる例外を飲み込んでしまいます。本当のバグまで隠れて原因が追えなくなるので、受け止める種類は必ず書きます。",
  },
  {
    course: "python-basics",
    lesson: "py-try-except",
    lessonTitle: "try-exceptで備える",
    heading: "`try` の範囲を広げすぎる",
    detail: "`try` のブロックに関係ない処理まで入れると、どこで失敗したのか分からなくなります。危ない 1 行だけを囲むのが基本です。",
  },
  {
    course: "python-basics",
    lesson: "py-try-except",
    lessonTitle: "try-exceptで備える",
    heading: "`except` で何もしない",
    detail: "`except ValueError: pass` と書くと、失敗を無視して先に進みます。今回は `0` を返すという後始末があるので、`pass` は使いません。",
  },
  {
    course: "python-basics",
    lesson: "py-exception-types",
    lessonTitle: "例外の種類",
    heading: "`except` の種類を間違える",
    detail: "`except ValueError` では `FileNotFoundError` を受け止められません。関係のない種類を書くと、例外はそのまま素通りして止まります。",
  },
  {
    course: "python-basics",
    lesson: "py-exception-types",
    lessonTitle: "例外の種類",
    heading: "`try` に `with` を入れ忘れる",
    detail: "`with` の外側だけ `try` で囲んでも意味がありません。`open` が呼ばれる場所が `try` の中に入っている必要があります。",
  },
  {
    course: "python-basics",
    lesson: "py-exception-types",
    lessonTitle: "例外の種類",
    heading: "`None` の判定に `==` を使う",
    detail: "`initial_text is not None` と書きます。空文字も「渡された」として扱いたいので、`if initial_text:` では空文字が省略扱いになってしまいます。",
  },
  {
    course: "python-basics",
    lesson: "py-ch8-build",
    lessonTitle: "つくる:保存機能",
    heading: "`int` に戻し忘れる",
    detail: "金額が `\"120\"` のまま返ると、見た目は似ていてもテストは落ちます。辞書に入れる前に `int(price)` を通します。",
  },
  {
    course: "python-basics",
    lesson: "py-ch8-build",
    lessonTitle: "つくる:保存機能",
    heading: "キーの順番や名前を変える",
    detail: "保存の形式は `name`、`price`、`category` の順です。読み戻すときも同じ順で受け取ります。キー名は第 7 章から一貫して英語です。",
  },
  {
    course: "python-basics",
    lesson: "py-ch8-build",
    lessonTitle: "つくる:保存機能",
    heading: "書き込みのあと読まずに `records` を返す",
    detail: "テストは通ってしまうかもしれませんが、保存できたことの確認になりません。必ずファイル経由で戻します。",
  },
  {
    course: "python-basics",
    lesson: "py-final-integrate",
    lessonTitle: "全機能を統合しよう",
    heading: "`command[0]` 以外を先に取り出してしまう",
    detail: "`[\"summary\"]` は要素が 1 つしかないので、いきなり `command[1]` を読むと `IndexError` になります。操作名で分岐したあと、その枝の中で取り出します。",
  },
  {
    course: "python-basics",
    lesson: "py-final-integrate",
    lessonTitle: "全機能を統合しよう",
    heading: "削除で全部消してしまう",
    detail: "同じ品名が 2 件あるとき、`remove` を繰り返すと両方消えます。1 件だけ消すなら `break` でループを抜けます。",
  },
  {
    course: "python-basics",
    lesson: "py-final-integrate",
    lessonTitle: "全機能を統合しよう",
    heading: "合計をループの中で数え直す",
    detail: "`add` のたびに合計を作り直すと、`delete` のあとにずれます。最後にまとめて足すほうが確実です。",
  },
  {
    course: "python-basics",
    lesson: "py-final-report",
    lessonTitle: "月間レポート機能",
    heading: "`remaining` を合計の途中で計算する",
    detail: "ループの中で引くと、最後の記録を足す前の値が残ります。引き算はループを抜けたあとです。",
  },
  {
    course: "python-basics",
    lesson: "py-final-report",
    lessonTitle: "月間レポート機能",
    heading: "`top` に金額を入れてしまう",
    detail: "`top` は品名の文字列です。`top_price` は探すための作業用の変数で、戻り値には入れません。",
  },
  {
    course: "python-basics",
    lesson: "py-final-report",
    lessonTitle: "月間レポート機能",
    heading: "予算オーバーを 0 で止める",
    detail: "使いすぎたときの `remaining` は負の数のままにします。`-500` と出るからこそ、いくら超えたのかが分かります。",
  },
  {
    course: "python-basics",
    lesson: "py-final-extend",
    lessonTitle: "自由拡張",
    heading: "`in` の向きを逆にする",
    detail: "`record[\"name\"] in keyword` と書くと、品名がキーワードに含まれるかという逆の判定になります。ほとんどの場合ずっと偽になります。",
  },
  {
    course: "python-basics",
    lesson: "py-final-extend",
    lessonTitle: "自由拡張",
    heading: "見つからないときに `None` を返す",
    detail: "呼び出し側が `for` で回した瞬間に `TypeError` になります。空のリストを返します。",
  },
  {
    course: "python-basics",
    lesson: "py-final-extend",
    lessonTitle: "自由拡張",
    heading: "元のリストから消しながら探す",
    detail: "`remove` で絞り込もうとすると、元の記録が壊れます。新しいリストに集めるのが安全です。",
  },
];

assertUniquePublicCourseMistakes(COURSE_MISTAKES);

const COURSE_MISTAKES_BY_STATIC_LESSON_KEY = new Map<string, CourseMistake[]>();
for (const mistake of COURSE_MISTAKES) {
  const key = `${mistake.course}/${mistake.lesson}`;
  const rows = COURSE_MISTAKES_BY_STATIC_LESSON_KEY.get(key) ?? [];
  rows.push(mistake);
  COURSE_MISTAKES_BY_STATIC_LESSON_KEY.set(key, rows);
}
const NO_COURSE_MISTAKES: readonly CourseMistake[] = Object.freeze([]);

/** The old and new lesson key resolve to the same canonical row array. */
export function mistakesForLessonKey(
  lessonKey: string,
): readonly CourseMistake[] {
  return (
    COURSE_MISTAKES_BY_STATIC_LESSON_KEY.get(
      staticCourseMistakeKey(lessonKey),
    ) ?? NO_COURSE_MISTAKES
  );
}

/** 辞典を持っているコースの slug と題名。ページの有無はこれで決める */
export const MISTAKE_COURSE_TITLES = new Map<string, string>([
  ["java-basics", "Java入門：基礎文法編"],
  ["java-intermediate", "Java 中級：コレクション・Stream・例外処理"],
  ["javascript-basics", "JavaScript入門：プロフィールサイトを動かす"],
  ["javascript-practical", "実践JavaScript：外のデータでアプリを動かす"],
  ["python-basics", "Python入門：基礎文法編"],
]);

/** 辞典を持っているコースの slug */
export const MISTAKE_COURSE_SLUGS = [...MISTAKE_COURSE_TITLES.keys()];

/** コースごとのつまずきの件数。索引カードの見出しに出す */
export const MISTAKE_COURSE_COUNTS = new Map<string, number>([
  ["java-basics", 148],
  ["java-intermediate", 168],
  ["javascript-basics", 158],
  ["javascript-practical", 54],
  ["python-basics", 195],
]);

/** つまずきの総数 */
export { MISTAKE_TOTAL };

/** 編の詳細ページに出す抜粋。見出しだけを 3 件 */
export const MISTAKE_PREVIEWS = new Map<string, string[]>([
  ["java-basics", ["初期化を忘れて `NullPointerException`","空配列で `IndexOutOfBoundsException`","文末の `;` を忘れる"]],
  ["java-intermediate", ["`IOException` を握りつぶす","ダウンキャストの `ClassCastException`","空行のパースで `NumberFormatException`"]],
  ["javascript-basics", ["`:` を書き忘れる","`0` を消してしまう","条件で `<=` を使う"]],
  ["javascript-practical", ["`+` で立てる","`.` をそのまま書く","降ろすつもりで `^` を使う"]],
  ["python-basics", ["初回の `KeyError`","`円` を付け忘れる","`\\n` を全角で書く"]],
]);

/** Project the one-copy static rows onto the active pre/post-DB catalog. */
export function materializeCourseMistakes(
  catalog: readonly CourseMistakeCatalogCourse[],
) {
  return materializeCourseMistakeRows(
    COURSE_MISTAKES,
    catalog,
    MISTAKE_COURSE_TITLES,
  );
}

export interface MistakeErrorCourse {
  /** コースの slug */
  course: string;
  /** コースの題名 */
  courseTitle: string;
  /** そのコースの辞典に何件あるか */
  count: number;
  /** 最初の 1 件へのアンカー */
  anchor: string;
}

export interface MistakeErrorEntry {
  /** エラー名 */
  name: string;
  /** 全コース合計の件数 */
  total: number;
  /** どのコースに何件あるか。多い順 */
  courses: MistakeErrorCourse[];
}

/**
 * エラー名からの逆引き索引。多い順。
 *
 * 名前が付くつまずきは全体の 2 割弱しかないので、これは索引の一部に
 * すぎない。名前で引ける人だけが使う近道として置いている
 */
export const MISTAKE_ERROR_INDEX: MistakeErrorEntry[] = [
  {
    name: "TypeError",
    total: 25,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 22, anchor: "mistake-py-int-cast-2" },
      { course: "javascript-practical", courseTitle: "実践JavaScript：外のデータでアプリを動かす", count: 2, anchor: "mistake-jsp-encapsulation-2" },
      { course: "javascript-basics", courseTitle: "JavaScript入門：プロフィールサイトを動かす", count: 1, anchor: "mistake-js-array-basics-2" },
    ],
  },
  {
    name: "SyntaxError",
    total: 20,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 16, anchor: "mistake-py-print-basics-1" },
      { course: "javascript-basics", courseTitle: "JavaScript入門：プロフィールサイトを動かす", count: 4, anchor: "mistake-js-read-errors-1" },
    ],
  },
  {
    name: "NullPointerException",
    total: 12,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 7, anchor: "mistake-java-set-basics-2" },
      { course: "java-basics", courseTitle: "Java入門：基礎文法編", count: 5, anchor: "mistake-java-operator-priority-2" },
    ],
  },
  {
    name: "ValueError",
    total: 10,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 10, anchor: "mistake-py-int-cast-1" },
    ],
  },
  {
    name: "NameError",
    total: 7,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 7, anchor: "mistake-py-execution-order-2" },
    ],
  },
  {
    name: "ReferenceError",
    total: 5,
    courses: [
      { course: "javascript-practical", courseTitle: "実践JavaScript：外のデータでアプリを動かす", count: 4, anchor: "mistake-jsp-strict-mode-1" },
      { course: "javascript-basics", courseTitle: "JavaScript入門：プロフィールサイトを動かす", count: 1, anchor: "mistake-js-read-errors-2" },
    ],
  },
  {
    name: "RuntimeException",
    total: 4,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 4, anchor: "mistake-java-try-catch-1" },
    ],
  },
  {
    name: "ArrayIndexOutOfBoundsException",
    total: 3,
    courses: [
      { course: "java-basics", courseTitle: "Java入門：基礎文法編", count: 3, anchor: "mistake-java-array-basic-0" },
    ],
  },
  {
    name: "ConcurrentModificationException",
    total: 3,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 3, anchor: "mistake-java-list-iterate-2" },
    ],
  },
  {
    name: "IndexError",
    total: 3,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 3, anchor: "mistake-py-while-loop-2" },
    ],
  },
  {
    name: "KeyError",
    total: 3,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 3, anchor: "mistake-py-dict-basics-1" },
    ],
  },
  {
    name: "NumberFormatException",
    total: 3,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 3, anchor: "mistake-java-try-catch-2" },
    ],
  },
  {
    name: "ZeroDivisionError",
    total: 3,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 3, anchor: "mistake-py-modulo-1" },
    ],
  },
  {
    name: "ArithmeticException",
    total: 2,
    courses: [
      { course: "java-basics", courseTitle: "Java入門：基礎文法編", count: 2, anchor: "mistake-java-arithmetic-1" },
    ],
  },
  {
    name: "ClassCastException",
    total: 2,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 2, anchor: "mistake-java-stream-sorted-1" },
    ],
  },
  {
    name: "IllegalStateException",
    total: 2,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 2, anchor: "mistake-java-stream-collect-0" },
    ],
  },
  {
    name: "IndentationError",
    total: 2,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 2, anchor: "mistake-py-if-basics-1" },
    ],
  },
  {
    name: "IOException",
    total: 2,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 2, anchor: "mistake-java-throws-0" },
    ],
  },
  {
    name: "NoSuchFileException",
    total: 2,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 2, anchor: "mistake-java-io-exists-0" },
    ],
  },
  {
    name: "AttributeError",
    total: 1,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 1, anchor: "mistake-py-type-check-0" },
    ],
  },
  {
    name: "DateTimeException",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-localdate-1" },
    ],
  },
  {
    name: "DirectoryNotEmptyException",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-io-exists-2" },
    ],
  },
  {
    name: "FileNotFoundError",
    total: 1,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 1, anchor: "mistake-py-exception-types-0" },
    ],
  },
  {
    name: "IllegalArgumentException",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-stream-limit-skip-2" },
    ],
  },
  {
    name: "IllegalFormatConversionException",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-string-format-0" },
    ],
  },
  {
    name: "IndexOutOfBoundsException",
    total: 1,
    courses: [
      { course: "java-basics", courseTitle: "Java入門：基礎文法編", count: 1, anchor: "mistake-java-array-max-1" },
    ],
  },
  {
    name: "InsufficientFundsException",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-custom-exception-0" },
    ],
  },
  {
    name: "InvalidCharacterError",
    total: 1,
    courses: [
      { course: "javascript-basics", courseTitle: "JavaScript入門：プロフィールサイトを動かす", count: 1, anchor: "mistake-js-classlist-1" },
    ],
  },
  {
    name: "MalformedInputException",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-io-buffered-2" },
    ],
  },
  {
    name: "OutOfMemoryError",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-io-read-lines-0" },
    ],
  },
  {
    name: "RangeError",
    total: 1,
    courses: [
      { course: "javascript-practical", courseTitle: "実践JavaScript：外のデータでアプリを動かす", count: 1, anchor: "mistake-jsp-inheritance-2" },
    ],
  },
  {
    name: "StackOverflowError",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-inheritance-super-1" },
    ],
  },
  {
    name: "UnboundLocalError",
    total: 1,
    courses: [
      { course: "python-basics", courseTitle: "Python入門：基礎文法編", count: 1, anchor: "mistake-py-scope-0" },
    ],
  },
  {
    name: "UnsupportedOperationException",
    total: 1,
    courses: [
      { course: "java-intermediate", courseTitle: "Java 中級：コレクション・Stream・例外処理", count: 1, anchor: "mistake-java-collections-utility-0" },
    ],
  },
  {
    name: "ValidationError",
    total: 1,
    courses: [
      { course: "javascript-practical", courseTitle: "実践JavaScript：外のデータでアプリを動かす", count: 1, anchor: "mistake-jsp-throw-custom-error-0" },
    ],
  },
];

/**
 * 辞典ページの項目 1 件ぶんのアンカー。
 *
 * 逆引きから項目そのものへ飛ばすために、索引側と辞典ページ側で
 * 同じ規則を使う必要がある。片方だけ直すと黙って外れるのでここに置く
 */
export function mistakeAnchor(lessonSlug: string, indexInLesson: number): string {
  return `mistake-${lessonSlug}-${indexInLesson}`;
}

/** 1 コースぶんのつまずきをレッスン単位にまとめて返す */
export function mistakesByLesson(courseSlug: string): Array<{
  lesson: string;
  lessonTitle: string;
  items: CourseMistake[];
}> {
  const groups: Array<{
    lesson: string;
    lessonTitle: string;
    items: CourseMistake[];
  }> = [];
  for (const m of COURSE_MISTAKES) {
    if (m.course !== courseSlug) continue;
    const last = groups[groups.length - 1];
    if (last && last.lesson === m.lesson) {
      last.items.push(m);
      continue;
    }
    groups.push({ lesson: m.lesson, lessonTitle: m.lessonTitle, items: [m] });
  }
  return groups;
}
